// Muscle-inspired balance controller — pure TypeScript, no React / Three.js /
// box3d-web imports. Owns activation, fatigue, and weakness state and converts
// balance observations into joint effort / torque-capacity commands.
//
// This is intentionally NOT a physiological muscle-tendon model. It is a
// game-readable joint-level approximation: delayed activation, antagonistic
// effort, strength/weakness/fatigue that reduce available torque, and a
// deterministic reset for evaluation seeds.
//
// Integration boundary (later checkpoints):
//   observation  ->  stepMuscleController  ->  joint efforts + activations
//                                           ->  humanoidMotor joint motors
// Forbidden assists (pelvis hoist, COM translation force, teleports) are never
// emitted by this module; forbiddenAssistCount is always 0 here.

import {
  type HumanoidJointName,
} from './humanoidBones';
import {
  CAPTURE_FOOT_DROP_M,
  CAPTURE_GRAVITY_MPS2,
  CAPTURE_MIN_COM_HEIGHT_M,
  CAPTURE_SHANK_LENGTH_M,
  CAPTURE_THIGH_LENGTH_M,
  captureLandingErrorM,
  isFootNearGround,
  planExplicitForwardStep,
  planGeometryCaptureStep,
  solveTwoLinkLegIk,
  supportSpanM,
  type CaptureVec3,
} from './humanoidMuscleCapture';
import {
  FORCE_DIST_DEFAULT_MASS_KG,
  forceDistDualSupportActive,
  forceDistNormalForceN,
  solveDualSupportForceDistribution,
  type LegJointConfig,
} from './humanoidForceDistribution';
import {
  emptyLateralDofCommand,
  publishLateralCommandSnapshot,
  type LocomotionLateralDofCommand,
  type LocomotionP1ALoadLateralPolicyOverlay,
} from './humanoidLocomotionTrace';
import {
  blendScalarTowardReference,
  blendWeightForPhaseGroup,
  isZeroReferenceBlend,
  mirrorReferenceLaterals,
  REFERENCE_STEP_LENGTH_M,
  referencePhaseFromControllerState,
  referencePhaseGroupFromStepPhase,
  resolveReferenceStepBlendWeights,
  resolveReferenceStepShape,
  sampleReferenceStep,
  type ReferenceStepBlendWeights,
  type ReferenceStepShapeScalars,
} from './humanoidReferenceStep';

/** Gate A default commanded forward step length (m). */
export const GATE_A_FORWARD_STEP_M = 0.22 as const;

/**
 * Diagnostic-only: last P1A load-acceptance lateral policy overlays for the
 * current control step. Cleared at the start of each stepMuscleController call;
 * set only inside applyP1AInPlaceLoadAcceptanceCommands. Never read by control.
 */
let diagP1aLoadPolicyLateral: LocomotionP1ALoadLateralPolicyOverlay | null = null;

function lateralDofFromJointCommand(
  cmd: MuscleJointCommand | undefined,
): LocomotionLateralDofCommand {
  if (!cmd) return emptyLateralDofCommand();
  const lat = cmd.targetRotationVectorRad[2];
  return {
    targetLatRad: Number.isFinite(lat) ? lat : null,
    commandedTargetSpeedRadS:
      cmd.maxSpeedRadS !== undefined && Number.isFinite(cmd.maxSpeedRadS)
        ? cmd.maxSpeedRadS
        : null,
    maxTorqueNm: Number.isFinite(cmd.maxTorqueNm) ? cmd.maxTorqueNm : null,
  };
}

/** Publish issued lateral joint commands for locomotion-trace attribution. */
function publishIssuedLateralCommands(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
): void {
  publishLateralCommandSnapshot({
    leftHip: lateralDofFromJointCommand(commands.leftHip),
    rightHip: lateralDofFromJointCommand(commands.rightHip),
    leftAnkleRoll: lateralDofFromJointCommand(commands.leftAnkle),
    rightAnkleRoll: lateralDofFromJointCommand(commands.rightAnkle),
    spine: lateralDofFromJointCommand(commands.spine),
    p1aLoadPolicyLateral: diagP1aLoadPolicyLateral,
  });
}

// ---------------------------------------------------------------------------
// Muscle groups (lower body + trunk)
// ---------------------------------------------------------------------------

/** Major lower-body and trunk groups exposed for activation visualization. */
export const MUSCLE_GROUP_NAMES = [
  'leftAnkle',
  'rightAnkle',
  'leftKnee',
  'rightKnee',
  'leftHip',
  'rightHip',
  'trunk',
] as const;

export type MuscleGroupName = (typeof MUSCLE_GROUP_NAMES)[number];

/** Antagonistic direction at each group (flexor / extensor). */
export type MuscleDirection = 'flexor' | 'extensor';

/**
 * Joints this controller may drive.
 * Balance groups own lower body + trunk. Upper-body joints are held at the
 * neutral STAND_POSE offset (zero) so arms don't free-fall and yank the COM
 * (T-pose spawn with undriven shoulders was a measured idle destabilizer).
 */
export const MUSCLE_DRIVEN_JOINTS: readonly HumanoidJointName[] = [
  'spine',
  'neck',
  'leftShoulder',
  'rightShoulder',
  'leftElbow',
  'rightElbow',
  'leftHip',
  'rightHip',
  'leftKnee',
  'rightKnee',
  'leftAnkle',
  'rightAnkle',
] as const;

/** Joints that receive balance offsets / activation (not pure neutral holds). */
export const MUSCLE_BALANCE_JOINTS: readonly HumanoidJointName[] = [
  'spine',
  'leftHip',
  'rightHip',
  'leftKnee',
  'rightKnee',
  'leftAnkle',
  'rightAnkle',
] as const;

export type MusclePhase = 'idle' | 'bracing' | 'recovering' | 'fatigued' | 'fallen' | 'stepping';

/**
 * Capture-step sub-phase (joint-motor foot placement only).
 * Checkpoint 15: `prepare` = stance load transfer with both feet planted
 * before any swing lift.
 * P1A diagnostic adds `hold` (airborne single-support dwell before replace).
 */
export type MuscleStepPhase = 'none' | 'prepare' | 'lift' | 'hold' | 'place' | 'load';

export type MusclePushDirection = 'left' | 'right' | 'forward' | 'backward' | null;

export type MuscleSwingSide = 'left' | 'right';

/**
 * Single-use explicit one-step command (Gate A).
 * Independent of the push-recovery capture heuristic and of `stepEnabled`.
 */
export interface ExplicitOneStepRequest {
  /** Forward swing-foot displacement (m). Gate A uses 0.22. */
  forwardDisplacementM: number;
  /** Lateral swing-foot displacement (m, +right). Default 0. */
  lateralDisplacementM: number;
  /**
   * Preferred swing side. `null` selects the deterministic default (`right`).
   */
  swingSide: MuscleSwingSide | null;
}

/**
 * P1A / unified single-support pipeline request.
 * Transfer → lift 2–4 cm → hold airborne 0.25 s → place (lateral capture
 * replant; sagittal = origin + commanded forward) → load.
 * `forwardDisplacementM: 0` is the certified P1A in-place replace.
 * Nonzero forward is the Gate A / P1B step on the same mechanisms.
 * Independent of `stepEnabled`.
 */
export interface ExplicitSingleSupportHoldRequest {
  /**
   * Preferred swing side. `null` selects the deterministic default (`right`).
   */
  swingSide: MuscleSwingSide | null;
  /**
   * Commanded forward displacement of the swing foot (m). Default 0 (P1A).
   * Clamped to `stepMaxLengthM` at plan time.
   */
  forwardDisplacementM?: number;
  /**
   * Commanded lateral displacement (m, +right). Default 0.
   * Lateral place still retargets via capture-point; this seeds the origin only
   * when nonzero is requested (rare; Gate A uses 0).
   */
  lateralDisplacementM?: number;
}

/**
 * P1A sole clearance target (top of the frozen 0.02–0.04 m band). The relative
 * swing controller measured 0.02 m at a 0.03 m command while a sole edge still
 * touched; 0.04 m preserves the acceptance ceiling while creating contact end.
 */
export const P1A_LIFT_CLEARANCE_M = 0.03697936536207092; // search v6 idx183 (seeds 0-9, 8/8)

/**
 * Forward single-support lift apex floor (m). Historical 0.065 was never tracked
 * under the −0.55 knee clamp (geometric max center ≈0.036 m). Kept as the
 * command floor so place/hold still ask for a raised sole; actual peak is set
 * by {@link p1aSwingKneeSagMinRad} opening just enough for sole ≥0.032 m.
 */
export const P1A_FORWARD_LIFT_CLEARANCE_FLOOR_M = 0.065 as const;

/**
 * Offline P1A sagittal target offsets (evaluation / policy search only).
 * All fields default to zero; undefined or explicit zero must match the verified baseline.
 * Applied only while `singleSupportHoldActive` is true, after measured feedback/IK
 * and before existing anatomical clamps. Lateral targets, torque, activation, and
 * speed caps are never modified.
 */
export interface P1ASagittalPolicyOffsets {
  readonly singleSupportStanceHipRad: number;
  readonly singleSupportStanceKneeRad: number;
  readonly singleSupportStanceAnkleRad: number;
  readonly singleSupportSpineRad: number;
  readonly loadNewHipRad: number;
  readonly loadNewKneeRad: number;
  readonly loadNewAnkleRad: number;
  readonly loadStanceHipRad: number;
  readonly loadStanceKneeRad: number;
  readonly loadStanceAnkleRad: number;
  readonly loadSpineRad: number;
}

/** Frozen all-zero policy — behaviorally identical to omitting the field. */
export const ZERO_P1A_SAGITTAL_POLICY_OFFSETS: Readonly<P1ASagittalPolicyOffsets> =
  Object.freeze({
    singleSupportStanceHipRad: 0,
    singleSupportStanceKneeRad: 0,
    singleSupportStanceAnkleRad: 0,
    singleSupportSpineRad: 0,
    loadNewHipRad: 0,
    loadNewKneeRad: 0,
    loadNewAnkleRad: 0,
    loadStanceHipRad: 0,
    loadStanceKneeRad: 0,
    loadStanceAnkleRad: 0,
    loadSpineRad: 0,
  });

/**
 * Measured production default (2026-07-28): `bestPhysical` from the offline
 * policy search (`artifacts/humanoid-p1-policy-search-v2.json`), promoted
 * after the step-3 capture-replant evaluation measured it upright through
 * P1A load on fixed V2 (min height 1.001 m, tilt 7.2 deg, entry margin
 * +0.0047 m — `artifacts/humanoid-p1-step3-stage2-trace.json`) versus the
 * zero-offset default collapsing to 0.643 m / 24.7 deg. Explicit callers
 * (search/eval) always pass their own offsets; zeros stay literal zeros.
 */
export const P1A_MEASURED_SAGITTAL_POLICY_OFFSETS: Readonly<P1ASagittalPolicyOffsets> =
  Object.freeze({
    // Search v6 idx183 (2026-07-28): first candidate to pass ALL 8 P1A hard
    // gates on seeds 0-9 (slip 0.0167, replace 0.0237, contSat 0.217, height
    // 1.008, tilt 7.95 deg) — artifacts/humanoid-p1-policy-search-v6.json.
    singleSupportStanceHipRad: 0.0004055294594619047,
    singleSupportStanceKneeRad: -0.12,
    singleSupportStanceAnkleRad: -0.0010495965975954522,
    singleSupportSpineRad: 0.053080930698781364,
    loadNewHipRad: 0.2,
    loadNewKneeRad: 0.06721414161293505,
    loadNewAnkleRad: 0.028688727532989552,
    loadStanceHipRad: 0.004564104719873743,
    loadStanceKneeRad: 0.09315602775244461,
    loadStanceAnkleRad: -0.022497903142828138,
    loadSpineRad: -0.18,
  });

/**
 * Resolve optional policy. Absence now resolves to the measured production
 * default above (promoted from evaluation-only on 2026-07-28); pass explicit
 * zeros (`ZERO_P1A_SAGITTAL_POLICY_OFFSETS`) for the historical baseline.
 */
export function resolveP1ASagittalPolicyOffsets(
  policy?: Readonly<P1ASagittalPolicyOffsets> | null,
): Readonly<P1ASagittalPolicyOffsets> {
  return policy ?? P1A_MEASURED_SAGITTAL_POLICY_OFFSETS;
}

/**
 * Optional P1A trial style / place-geometry knobs (evaluation / offline search).
 * Every field is additive: absence or undefined leaves production defaults.
 * Applied only while `singleSupportHoldActive` is true (arms) or on the P1A
 * lift/place paths (clearance / capture). Never touches muscleBalance push
 * recovery outside the hold window.
 */
export interface P1ATrialStyleKnobs {
  /**
   * Lift clearance target (m). Absent → {@link P1A_LIFT_CLEARANCE_M} (0.04).
   * Search range 0.03–0.10.
   */
  readonly liftClearanceM?: number;
  /**
   * P1A place capture reach bound (m). Absent → {@link P1A_PLACE_CAPTURE_REACH_M} (0.12).
   * Search range 0.06–0.16. Min separation stays fixed at 0.10 m.
   */
  readonly placeCaptureReachM?: number;
  /**
   * Freeze band above plant Y (m). Absent → {@link P1A_PLACE_CAPTURE_FREEZE_ABOVE_PLANT_M} (0.015).
   * Search range 0.010–0.030.
   */
  readonly placeCaptureFreezeAbovePlantM?: number;
  /**
   * Shoulder sagittal target offset (rad), both arms, STAND-relative.
   * Absent / 0 → current neutral hold. Search range [-1.3, 0].
   */
  readonly armShoulderSagRad?: number;
  /**
   * Elbow flex target offset (rad), both elbows. Absent / 0 → current neutral.
   * Search range [0, 0.6]. Forward hang production default is 0.25.
   */
  readonly armElbowFlexRad?: number;
  /**
   * Through-step hang shoulder maxTorque (N·m). Absent →
   * {@link P1A_FORWARD_ARM_SHOULDER_MAX_TORQUE_NM} (120). Search ~60–200.
   */
  readonly armShoulderHangTorqueNm?: number;
  /**
   * Through-step hang elbow maxTorque (N·m). Absent →
   * {@link P1A_FORWARD_ARM_ELBOW_MAX_TORQUE_NM} (50). Search ~30–100.
   */
  readonly armElbowHangTorqueNm?: number;
  /**
   * Prepare hip lateral rebalance |max| (rad). Absent →
   * {@link P1A_PREPARE_HIP_LAT_MAX_RAD} (0.08). Arms-down COM may need more/less.
   */
  readonly prepareHipLatMaxRad?: number;
  /**
   * Prepare hip lateral rebalance gain (rad/m). Absent →
   * {@link P1A_PREPARE_HIP_LAT_GAIN_PER_M} (0.92).
   */
  readonly prepareHipLatGainPerM?: number;
  /**
   * Prepare→lift min stance load fraction. Absent →
   * {@link P1A_PREPARE_STANCE_LOAD_FRACTION_MIN} (0.85). Lower for hang COM shift.
   */
  readonly prepareStanceLoadFractionMin?: number;
  /**
   * Prepare→lift max swing load fraction. Absent →
   * {@link P1A_PREPARE_SWING_LOAD_FRACTION_MAX} (0.15).
   */
  readonly prepareSwingLoadFractionMax?: number;
  /**
   * Fixed-foot hip shift opens above this stance load fraction. Absent →
   * {@link P1A_PREPARE_SHIFT_LOAD_FRACTION_START} (0.7).
   */
  readonly prepareShiftLoadFractionStart?: number;
  /**
   * P1A prepare phase timeout (s). Absent → {@link P1A_PREPARE_TIMEOUT_S} (3.0).
   * Forward hang still adds the +1.5 s latch bonus on top of this base.
   */
  readonly prepareTimeoutS?: number;
  /**
   * Continuous ready-dwell before lift (s). Absent →
   * {@link P1A_PREPARE_READY_DWELL_S} (0.1).
   */
  readonly prepareReadyDwellS?: number;
  /**
   * Single-support stance column lateral gain (rad/m of COM−plant X).
   * Absent → {@link P1A_STANCE_COLUMN_LAT_GAIN_PER_M} (2.2). Search ~1.2–3.2.
   */
  readonly stanceColumnLatGainPerM?: number;
  /**
   * Single-support stance column lateral |max| (rad).
   * Absent → {@link P1A_STANCE_COLUMN_LAT_MAX_RAD} (0.35). Search ~0.2–0.5.
   */
  readonly stanceColumnLatMaxRad?: number;
  /**
   * Scale on lift/hold stance sagittal column gains (hip/ankle capture×errZ).
   * Absent → 1. Search ±40% → [0.6, 1.4].
   */
  readonly ssStanceGainScale?: number;
  /**
   * Scale on lift/hold swing foot-error feedback gains (kZ / kX).
   * Absent → 1. Search ±40% → [0.6, 1.4].
   */
  readonly ssSwingGainScale?: number;
  /**
   * Scale on SS spine lat/sag feedback (column spine counter + capture spine).
   * Absent → 1. Search ±40% → [0.6, 1.4].
   */
  readonly ssSpineGainScale?: number;
  /**
   * Per-phase-group reference-step feedforward blend weights in [0, 1].
   * Absent / all-zero → current certified feedback only (identity).
   * Applied after capture/load/column/residual laws on the unified one-step path.
   */
  readonly referenceBlend?: Readonly<Partial<ReferenceStepBlendWeights>>;
  /**
   * Optional reference shape scalars (swing flex / stance brace / spine / arm).
   * Absent → authored defaults (1). Only meaningful when some blend weight > 0.
   */
  readonly referenceShape?: Readonly<ReferenceStepShapeScalars>;
  /**
   * Gate A push 12b: enable load-fraction-indexed new-knee momentum-absorb flex
   * on forward plant load. Evaluation-only. Absent / false → push-7 load law
   * (no absorb flex). See {@link p1aMomentumAbsorbKneeFlexRad}.
   * Full-strength when true; prefer {@link momentumAbsorbKneeScale} for partial
   * dose (imitation style search uses 0–0.4).
   */
  readonly momentumAbsorbKnee?: boolean;
  /**
   * Scale on the push-12 absorb-knee flex profile in [0, 1]. Evaluation-only.
   * Absent / 0 → off (unless {@link momentumAbsorbKnee} is true → full 1.0).
   * Imitation round 2 searches [0, 0.4] for a harmless landing-spike dose.
   */
  readonly momentumAbsorbKneeScale?: number;
  /**
   * Gate A push 12b: settle COM capture toward the NEW support plant (not
   * geometric mid) during forward compliant load. Evaluation-only. Absent /
   * false → push-7 mid-plant capture. Separable from {@link momentumAbsorbKnee}.
   */
  readonly comSettleToNewSupport?: boolean;
  /**
   * Gate A push 13: enable early-load new-leg hip-sag soft-track (push 9 Stage 2)
   * while newFootLoadFraction < {@link P1A_FORWARD_EARLY_LOAD_FRACTION}.
   * Evaluation-only. Absent / false → push-7/8 full plant hip-sag composition
   * (upright through load on arms-up path). Separable from absorb/settle knobs.
   */
  readonly earlyLoadHipSagSoftTrack?: boolean;
  /**
   * Gate A push 15/18: scale on place/hold swing-foot closed-loop
   * touchdown-velocity nulling (world-frame horizontal velocity → 0).
   * Evaluation-only. Absent → {@link P1A_PLACE_SWING_RETRACT_SCALE} (0 = off).
   * Explicit >0 enables. Zero-forward path never applies regardless.
   */
  readonly placeSwingRetractScale?: number;
  /**
   * Gate A push 15/18: velocity-null gain (s) for damp = foot − gain×V
   * (signed measured world velocity; push 18 closed the loop — no open-loop
   * max(0,Vz) rearward bias). Absent → {@link P1A_PLACE_SWING_RETRACT_GAIN_S}.
   * Search ~0.04–0.25.
   */
  readonly placeSwingRetractGainS?: number;
  /**
   * Gate A push 19: scale on place/hold swing-foot sole-pitch flatten
   * (servo ankle sagittal so sole pitch → 0 at contact). Evaluation-only.
   * Absent → {@link P1A_PLACE_SOLE_FLATTEN_SCALE} (0 = off). Explicit >0 enables.
   * Zero-forward path never applies regardless.
   */
  readonly placeSwingSoleFlattenScale?: number;
  /**
   * Gate A push 19: sole-pitch → ankle gain (rad ankle / rad pitch).
   * Sign: +pitch (toe up) → +ankle (plantarflex) to lower toes.
   * Absent → {@link P1A_PLACE_SOLE_FLATTEN_GAIN}. Search ~0.5–2.0.
   */
  readonly placeSwingSoleFlattenGain?: number;
  /**
   * Gate A push 15: forward SS swing knee sagittal floor (rad). Absent →
   * {@link P1A_FORWARD_SWING_KNEE_SAG_MIN_RAD} (−0.55 = in-place). Search may
   * open slightly (e.g. −0.56…−0.60) for sole clearance; deeper collapses
   * upright on this mass model.
   */
  readonly forwardSwingKneeSagMinRad?: number;
  /**
   * Gate A push 20: commanded plant target sagittal shift (m, world +Z forward).
   * Evaluation-only. Absent / 0 → no shift (P1A identity). Negative = plant
   * farther rear so quiet touchdown lands where the post-TD skid used to end.
   * Forward-only; zero-forward path never applies.
   */
  readonly placePlantShiftSagittalM?: number;
  /**
   * Gate A push 20: commanded plant target lateral shift (m, world +X).
   * Evaluation-only. Absent / 0 → no shift. Forward-only.
   */
  readonly placePlantShiftLateralM?: number;
}

/** Frozen empty knobs — behaviorally identical to omitting the field. */
export const ZERO_P1A_TRIAL_STYLE_KNOBS: Readonly<P1ATrialStyleKnobs> = Object.freeze({});

/**
 * Attach evaluation-only style knobs to controller state.
 * Pass `null` to clear (production defaults). Not hashed into state identity.
 */
export function setP1ATrialStyleKnobs(
  state: MuscleControllerState,
  knobs: Readonly<P1ATrialStyleKnobs> | null,
): void {
  state.p1aTrialStyleKnobs = knobs;
}

/** Resolve lift clearance (m); absence → {@link P1A_LIFT_CLEARANCE_M}. */
export function resolveP1ALiftClearanceM(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.liftClearanceM;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return P1A_LIFT_CLEARANCE_M;
}

/** Resolve place capture reach (m); absence → production constant. */
export function resolveP1APlaceCaptureReachM(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.placeCaptureReachM;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return P1A_PLACE_CAPTURE_REACH_M;
}

/** Resolve place freeze band (m); absence → production constant. */
export function resolveP1APlaceCaptureFreezeM(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.placeCaptureFreezeAbovePlantM;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return P1A_PLACE_CAPTURE_FREEZE_ABOVE_PLANT_M;
}

/**
 * Canonical arm hang depth (STAND-relative) when hang is **active** via knobs.
 * Soft-slew pre-settle in quiet DS, then hold through the step. Residual≈0
 * at −1.1 kills shoulder contSat.
 *
 * Gate A push 12: production **arms-up** suppresses hang (effective goals 0) so
 * the forward path matches push-7 (direct prepare, 480/220 arm boost, full
 * cycle). Opt-in hang: set knobs to these constants (or any non-zero pair) to
 * run the push-8 pre-settle sequence. Zero-forward P1A stays exact zero.
 *
 * Plant note (push 8–11): hang-through-SS can still collapse this mass model
 * and aborts prepare at −1.1; pre-settle removes mid-step drop yank but not
 * hang COM shift.
 */
export const P1A_FORWARD_ARM_SHOULDER_SAG_RAD = -1.1 as const;
export const P1A_FORWARD_ARM_ELBOW_FLEX_RAD = 0.25 as const;

/** Knobs that enable production-depth hang + pre-settle (search / hang-active path). */
export const P1A_FORWARD_ARM_HANG_KNOBS: Readonly<P1ATrialStyleKnobs> = Object.freeze({
  armShoulderSagRad: P1A_FORWARD_ARM_SHOULDER_SAG_RAD,
  armElbowFlexRad: P1A_FORWARD_ARM_ELBOW_FLEX_RAD,
});
/** Through-step hang hold torque (residual≈0 → low torque, no sat). */
export const P1A_FORWARD_ARM_SHOULDER_MAX_TORQUE_NM = 120 as const;
export const P1A_FORWARD_ARM_ELBOW_MAX_TORQUE_NM = 50 as const;
/** Pre-settle approach torque — enough to track a slow slew without long sat. */
export const P1A_FORWARD_ARM_PRESETTLE_SHOULDER_TORQUE_NM = 200 as const;
export const P1A_FORWARD_ARM_PRESETTLE_ELBOW_TORQUE_NM = 80 as const;
/** Pre-settle target slew speed (rad/s) — slow hang drop. */
export const P1A_FORWARD_ARM_PREPARE_MAX_SPEED_RAD_S = 0.55 as const;
/** Through-step freeze speed (rad/s). */
export const P1A_FORWARD_ARM_MAX_SPEED_RAD_S = 0.08 as const;
/**
 * Minimum pre-settle window (s). Hang slew |sag|/speed ≈ 1.1/0.55 ≈ 2.0 s.
 */
export const P1A_FORWARD_ARM_TARGET_RAMP_S = 1.0 as const;
/** Spine counter (rad) scaled by |sag|/1.1 during pre-settle only. */
export const P1A_FORWARD_ARM_SPINE_COUNTER_SAG_RAD = 0.04 as const;

/** Pre-settle balance gate: max COM horizontal speed (m/s). */
export const P1A_ARM_PRESETTLE_COM_SPEED_MPS = 0.1 as const;
/** Pre-settle balance gate: max pelvis tilt (deg). */
export const P1A_ARM_PRESETTLE_TILT_DEG = 10 as const;
/** Quiet dwell after hang+balance before arming prepare (s). */
export const P1A_ARM_PRESETTLE_READY_DWELL_S = 0.25 as const;
/** Force-begin prepare if quiet gate never latches (s). */
export const P1A_ARM_PRESETTLE_TIMEOUT_S = 3.0 as const;
/** Hang target reached tolerance (rad) for pre-settle ready. */
export const P1A_ARM_PRESETTLE_TARGET_TOL_RAD = 0.12 as const;

/** Resolve arm shoulder sagittal offset (rad); absence → 0. */
export function resolveP1AArmShoulderSagRad(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.armShoulderSagRad;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return 0;
}

/** Resolve arm elbow flex offset (rad); absence → 0. */
export function resolveP1AArmElbowFlexRad(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.armElbowFlexRad;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return 0;
}

/** Through-step hang shoulder torque (N·m); absence → production hang budget. */
export function resolveP1AArmShoulderHangTorqueNm(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.armShoulderHangTorqueNm;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  return P1A_FORWARD_ARM_SHOULDER_MAX_TORQUE_NM;
}

/** Through-step hang elbow torque (N·m); absence → production hang budget. */
export function resolveP1AArmElbowHangTorqueNm(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.armElbowHangTorqueNm;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  return P1A_FORWARD_ARM_ELBOW_MAX_TORQUE_NM;
}

/**
 * Effective forward-step arm hang goals (push 12).
 *
 * - Knobs present (either shoulder or elbow finite) → those values (missing
 *   side defaults to 0). Explicit zeros suppress hang.
 * - No arm knobs → production arms-up: both 0 (hang suppressed, push-7 path).
 * - Canonical hang depth for opt-in: {@link P1A_FORWARD_ARM_HANG_KNOBS}.
 *
 * Pre-settle engages only when `hangActive` is true.
 */
export function resolveEffectiveForwardArmHangTargets(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): { shoulderSagRad: number; elbowFlexRad: number; hangActive: boolean } {
  const hasKnobSh =
    typeof knobs?.armShoulderSagRad === 'number' &&
    Number.isFinite(knobs.armShoulderSagRad);
  const hasKnobEl =
    typeof knobs?.armElbowFlexRad === 'number' &&
    Number.isFinite(knobs.armElbowFlexRad);
  // Production arms-up: no arm knobs → hang suppressed (push-7 identity).
  if (!hasKnobSh && !hasKnobEl) {
    return { shoulderSagRad: 0, elbowFlexRad: 0, hangActive: false };
  }
  const shoulderSagRad = hasKnobSh ? (knobs!.armShoulderSagRad as number) : 0;
  const elbowFlexRad = hasKnobEl ? (knobs!.armElbowFlexRad as number) : 0;
  return {
    shoulderSagRad,
    elbowFlexRad,
    hangActive:
      Math.abs(shoulderSagRad) > 1e-4 || Math.abs(elbowFlexRad) > 1e-4,
  };
}

/**
 * Effective arm hold targets for forward-step hang (pre-settle + through step).
 * Knobs win when set; else production hang on commanded forward when arm hold
 * is active **and** production hang is not suppressed; else exact zero
 * (P1A identity / idle / zero-forward / production arms-up).
 *
 * `ramp01` ∈ [0,1] scales targets from 0 → full (smoothstep).
 * `suppressProductionDefaults` keeps production at 0 (knobs still apply).
 * Push 12: production hang is suppressed by default — use knobs to activate.
 */
export function resolveP1AArmHoldTargets(args: {
  /** Pre-settle, latched through-step hold, or single-support with arm path. */
  armHoldActive: boolean;
  /** @deprecated Prefer armHoldActive; still accepted for call-site compat. */
  singleSupportHoldActive?: boolean;
  commandedStepForwardM: number;
  knobs?: Readonly<P1ATrialStyleKnobs> | null;
  ramp01?: number;
  suppressProductionDefaults?: boolean;
}): { shoulderSagRad: number; elbowFlexRad: number } {
  const hold =
    args.armHoldActive === true || args.singleSupportHoldActive === true;
  if (!hold) {
    return { shoulderSagRad: 0, elbowFlexRad: 0 };
  }
  // Push 12: effective goals already encode production arms-up suppress.
  // suppressProductionDefaults remains for call-site compat (forces knobs-only).
  const effective = resolveEffectiveForwardArmHangTargets(args.knobs);
  const fullShoulder = args.suppressProductionDefaults
    ? typeof args.knobs?.armShoulderSagRad === 'number' &&
      Number.isFinite(args.knobs.armShoulderSagRad)
      ? args.knobs.armShoulderSagRad
      : 0
    : effective.shoulderSagRad;
  const fullElbow = args.suppressProductionDefaults
    ? typeof args.knobs?.armElbowFlexRad === 'number' &&
      Number.isFinite(args.knobs.armElbowFlexRad)
      ? args.knobs.armElbowFlexRad
      : 0
    : effective.elbowFlexRad;
  const r =
    typeof args.ramp01 === 'number' && Number.isFinite(args.ramp01)
      ? clamp(args.ramp01, 0, 1)
      : 1;
  const s = r * r * (3 - 2 * r);
  return {
    shoulderSagRad: fullShoulder * s,
    elbowFlexRad: fullElbow * s,
  };
}

/**
 * Pure pre-settle ready gate: ramp complete + quiet COM/tilt + dwell.
 * Timeout forces ready so a stuck balance never blocks prepare forever.
 */
export function p1aArmPreSettleReady(args: {
  elapsedS: number;
  rampS: number;
  comSpeedMps: number;
  tiltDeg: number;
  readyDwellS: number;
  timeoutS: number;
  maxComSpeedMps?: number;
  maxTiltDeg?: number;
  minReadyDwellS?: number;
}): boolean {
  const rampS = Math.max(1.0, args.rampS);
  if (args.elapsedS >= args.timeoutS) return true;
  if (args.elapsedS < rampS) return false;
  const maxV = args.maxComSpeedMps ?? P1A_ARM_PRESETTLE_COM_SPEED_MPS;
  const maxTilt = args.maxTiltDeg ?? P1A_ARM_PRESETTLE_TILT_DEG;
  const minDwell = args.minReadyDwellS ?? P1A_ARM_PRESETTLE_READY_DWELL_S;
  if (args.comSpeedMps > maxV || args.tiltDeg > maxTilt) return false;
  return args.readyDwellS >= minDwell;
}

/** Resolve reference blend weights; absence → all-zero (identity). */
export function resolveP1AReferenceBlendWeights(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): ReferenceStepBlendWeights {
  return resolveReferenceStepBlendWeights(knobs?.referenceBlend ?? null);
}

/** Resolve reference shape scalars; absence → defaults (1). */
export function resolveP1AReferenceShape(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): Required<ReferenceStepShapeScalars> {
  return resolveReferenceStepShape(knobs?.referenceShape ?? null);
}

/**
 * Momentum-absorb knee flex opt-in (push 12b). Absent / false → off (push-7).
 * True when boolean flag is set OR scale > 0.
 */
export function resolveP1AMomentumAbsorbKnee(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): boolean {
  return resolveP1AMomentumAbsorbKneeScale(knobs) > 0;
}

/**
 * Absorb-knee strength scale in [0, 1]. Multiplies push-12 flex gain + max.
 * - `momentumAbsorbKneeScale` finite → clamped [0, 1]
 * - else `momentumAbsorbKnee === true` → 1 (full push-12 profile)
 * - else → 0 (off)
 */
export function resolveP1AMomentumAbsorbKneeScale(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const s = knobs?.momentumAbsorbKneeScale;
  if (typeof s === 'number' && Number.isFinite(s)) {
    return Math.max(0, Math.min(1, s));
  }
  return knobs?.momentumAbsorbKnee === true ? 1 : 0;
}

/**
 * COM settle-to-new-support opt-in (push 12b). Absent / false → mid-plant (push-7).
 */
export function resolveP1AComSettleToNewSupport(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): boolean {
  return knobs?.comSettleToNewSupport === true;
}

/**
 * Early-load hip-sag soft-track opt-in (push 13). Absent / false → off (push-7/8).
 * Push 9 shipped this always-on; bisect showed it collapses arms-up uprightness
 * (h 1.008→0.641). Gated eval-only so the intended prepare-knob / search work
 * remains without poisoning production load law.
 */
export function resolveP1AEarlyLoadHipSagSoftTrack(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): boolean {
  return knobs?.earlyLoadHipSagSoftTrack === true;
}

/**
 * Diagnostic tracking accumulator (search/eval only).
 * Mean |cmd − reference| after blend when non-zero weights are active.
 */
export interface ReferenceTrackingAccumulator {
  sumAbsRad: number;
  samples: number;
}

export function createReferenceTrackingAccumulator(): ReferenceTrackingAccumulator {
  return { sumAbsRad: 0, samples: 0 };
}

export function meanReferenceTrackingErrorRad(
  acc: Readonly<ReferenceTrackingAccumulator>,
): number | null {
  if (acc.samples <= 0) return null;
  return acc.sumAbsRad / acc.samples;
}

/**
 * Blend certified feedback joint targets toward the hand-authored reference step.
 * Identity when blend weights are all zero or the path is not a single-support
 * one-step (capture recovery / ordinary balance untouched).
 *
 * `final = (1−w)·feedback + w·reference` per phase group.
 * Capture replant, load authority, column/residual compositions remain the base.
 */
export function applyReferenceStepFeedforwardBlend(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  state: MuscleControllerState,
  config: MuscleControllerConfig,
  tracking?: ReferenceTrackingAccumulator | null,
): void {
  // Only unified one-step / P1A pipeline. Zero blend = exact early return.
  if (!state.singleSupportHoldActive || !state.swingSide) return;
  if (state.stepPhase === 'none' && !state.plantedPoseActive) return;

  const knobs = state.p1aTrialStyleKnobs;
  const blends = resolveP1AReferenceBlendWeights(knobs);
  if (isZeroReferenceBlend(blends)) return;

  const group = referencePhaseGroupFromStepPhase(state.stepPhase);
  const w = blendWeightForPhaseGroup(blends, group);
  if (w <= 0) return;

  const phaseU = referencePhaseFromControllerState({
    stepPhase: state.stepPhase,
    stepPhaseElapsedS: state.stepPhaseElapsedS,
    prepareMinS: config.stepPrepareMinS,
    liftRampS: P1A_LIFT_RAMP_S,
    holdS: P1A_HOLD_S,
    replaceS: P1A_REPLACE_S,
    loadS:
      state.commandedStepForwardM > 1e-4
        ? Math.max(P1A_RECOVER_S, 2.0)
        : P1A_RECOVER_S,
  });
  const shape = resolveP1AReferenceShape(knobs);
  let ref = sampleReferenceStep(phaseU, {
    shape,
    stepLengthM:
      state.commandedStepForwardM > 1e-4
        ? state.commandedStepForwardM
        : REFERENCE_STEP_LENGTH_M,
  });
  // Authored laterals assume swing=right / stance=left.
  if (state.swingSide === 'left') {
    ref = mirrorReferenceLaterals(ref);
  }

  const swing = state.swingSide;
  const stance: MuscleSwingSide = swing === 'left' ? 'right' : 'left';
  const hipSwing = swing === 'left' ? 'leftHip' : 'rightHip';
  const kneeSwing = swing === 'left' ? 'leftKnee' : 'rightKnee';
  const ankleSwing = swing === 'left' ? 'leftAnkle' : 'rightAnkle';
  const hipStance = stance === 'left' ? 'leftHip' : 'rightHip';
  const kneeStance = stance === 'left' ? 'leftKnee' : 'rightKnee';
  const ankleStance = stance === 'left' ? 'leftAnkle' : 'rightAnkle';
  const shoulderSwing = swing === 'left' ? 'leftShoulder' : 'rightShoulder';
  const shoulderStance = stance === 'left' ? 'leftShoulder' : 'rightShoulder';

  const blendJoint = (
    joint: HumanoidJointName,
    refSag: number,
    refLat: number,
  ): void => {
    const prev = commands[joint];
    if (!prev) return;
    const prevLat = prev.targetRotationVectorRad[2] ?? 0;
    const sag = blendScalarTowardReference(prev.targetOffsetRad, refSag, w);
    const lat = blendScalarTowardReference(prevLat, refLat, w);
    commands[joint] = {
      ...prev,
      targetOffsetRad: sag,
      targetRotationVectorRad: [sag, 0, lat],
    };
    if (tracking) {
      tracking.sumAbsRad += Math.abs(sag - refSag) + Math.abs(lat - refLat);
      tracking.samples += 2;
    }
  };

  blendJoint(hipSwing, ref.swingHipSag, ref.swingHipLat);
  blendJoint(kneeSwing, ref.swingKnee, 0);
  blendJoint(ankleSwing, ref.swingAnkleSag, ref.swingAnkleLat);
  blendJoint(hipStance, ref.stanceHipSag, ref.stanceHipLat);
  blendJoint(kneeStance, ref.stanceKnee, 0);
  blendJoint(ankleStance, ref.stanceAnkleSag, ref.stanceAnkleLat);
  blendJoint('spine', ref.spineSag, ref.spineLat);
  // Arms only when already present (upper-body hold path).
  if (commands[shoulderSwing]) {
    blendJoint(shoulderSwing, ref.swingShoulderSag, 0);
  }
  if (commands[shoulderStance]) {
    blendJoint(shoulderStance, ref.stanceShoulderSag, 0);
  }
}

/** Module-level tracking acc for the active controller step (eval/search). */
let diagReferenceTracking: ReferenceTrackingAccumulator | null = null;

/** Install/clear diagnostic tracking accumulator (null = off). */
export function setReferenceTrackingAccumulator(
  acc: ReferenceTrackingAccumulator | null,
): void {
  diagReferenceTracking = acc;
}

export function getReferenceTrackingAccumulator(): ReferenceTrackingAccumulator | null {
  return diagReferenceTracking;
}

/**
 * P1A single-support stance lateral gain (rad/m of COM−plant X).
 * Dual-support balance maps COM-toward-swing onto stance flexors; this column
 * uses the measured cp16 COM→stance shrink law instead (stanceHipLat ∝ +errX).
 */
export const P1A_STANCE_COLUMN_LAT_GAIN_PER_M = 2.2;
/** Max |stance hip lateral| during P1A single-support column (rad). */
export const P1A_STANCE_COLUMN_LAT_MAX_RAD = 0.35;
/**
 * P1A lift C2 target ramp before hold (s).
 * A 0.16 s smoothing experiment reduced slip but worsened touchdown velocity and
 * recovery height, so the evaluator-selected 0.08 s ramp is restored.
 */
export const P1A_LIFT_RAMP_S = 0.08;
/** P1A airborne hold duration (s). */
export const P1A_HOLD_S = 0.25;
/** P1A replace / lower phase budget (s). C2 place-lowering denominator only. */
export const P1A_REPLACE_S = 0.40;
/**
 * Post-solver contact-observation grace after the C2 lower finishes (fixed steps).
 * Place abort uses {@link p1aPlaceTimeoutS}; lowering blend still ends at
 * {@link P1A_REPLACE_S}. Timer alone never opens load.
 */
export const P1A_PLACE_CONTACT_GRACE_STEPS = 2;
/** P1A double-support recover after replace (s). */
export const P1A_RECOVER_S = 1.0;
/**
 * P1A place timeout only: C2 lower duration + contact-observation grace.
 * Does not change the place trajectory blend denominator ({@link P1A_REPLACE_S}).
 */
export function p1aPlaceTimeoutS(safeDt: number): number {
  const dt = Math.max(0, safeDt);
  return P1A_REPLACE_S + P1A_PLACE_CONTACT_GRACE_STEPS * dt;
}
/**
 * P1A stance-preload duration (s): counterweight + ankle CoP bias establish
 * load before either hip receives a pelvis-translation target.
 */
export const P1A_PREPARE_PRELOAD_S = 0.5;
/** P1A pelvis-shift duration (s), after the stance preload. */
export const P1A_PREPARE_SHIFT_S = 0.8;
/**
 * P1A prepare timeout (s) — phase budget only, not a gate relaxation.
 * Covers the 1.6 s reference ramp, physical response, ready dwell, and margin.
 * Gate A/capture keep their independent `stepPrepareTimeoutS`.
 */
export const P1A_PREPARE_TIMEOUT_S = 3.0;
/**
 * P1A whole-body support-reference transfer duration (s). The initial 0.8 s
 * trajectory reached 0.804 stance load but exceeded the plant gate by 1.1 mm
 * and saturated the stance ankle for 0.40 s. Doubling the duration halves the
 * ~0.10 m reference peak speed to ≈0.117 m/s without changing its endpoint.
 */
export const P1A_PREPARE_LATERAL_RAMP_S = 1.6;
/**
 * Lateral inset (m) of the desired prepare pelvis X inside the stance footprint,
 * measured from the stance foot center toward the inter-foot midpoint.
 * Left stance → +inset; right stance → −inset.
 */
export const P1A_PREPARE_PELVIS_INSET_M = 0.02;
/**
 * P1A prepare→lift |COM.x − stanceFoot.x| bound (m).
 * Anatomical / capture foot half-width is 0.05 m; 0.04 m is a 1 cm lateral
 * margin inside the stance plate. This is an axis-aligned half-extent, not a
 * radial distance — Gate A / capture still use `stepPrepareComRadiusM` (0.11).
 */
export const P1A_PREPARE_COM_LATERAL_MAX_M = 0.04;
/**
 * P1A prepare→lift |COM.z − stanceFoot.z| bound (m).
 * Anatomical / capture foot half-length is 0.13 m; 0.12 m is a 1 cm sagittal
 * margin inside the stance plate. Settled COM can be ~0.09 m behind foot center
 * and still be on-plate. This is an axis-aligned half-extent, not a radial
 * distance — Gate A / capture still use `stepPrepareComRadiusM` (0.11).
 */
export const P1A_PREPARE_COM_SAGITTAL_MAX_M = 0.12;
/**
 * P1A prepare→lift max horizontal stance plant slip (m): live stance foot center
 * vs latched `stancePlantWorldX/Z`. Matches the frozen P1A stance-slip gate
 * (0.02 m). Momentary COM-in-footprint while the stance foot has skated is not
 * weight transfer.
 */
export const P1A_PREPARE_STANCE_PLANT_ERROR_MAX_M = 0.02;
/**
 * P1A prepare→lift continuous ready dwell (s). Full ready condition
 * (contacts + rectangular COM + tilt/speed + plant error + min time) must hold
 * without interruption for this long before lift. Any frame fail resets dwell.
 */
export const P1A_PREPARE_READY_DWELL_S = 0.1;
/** Minimum measured share of combined foot load carried by the stance foot. */
export const P1A_PREPARE_STANCE_LOAD_FRACTION_MIN = 0.85;
/** Maximum measured share of combined foot load left on the swing foot. */
export const P1A_PREPARE_SWING_LOAD_FRACTION_MAX = 0.15;
/**
 * Fixed-foot hip translation remains closed below this stance-load share and
 * blends on only from the highest proven planted preload region toward the
 * prepare gate. If measured load falls, the shift closes again.
 */
export const P1A_PREPARE_SHIFT_LOAD_FRACTION_START = 0.7;
/**
 * P1A prepare-only motor target-speed cap (rad/s) for left/right hip and ankle.
 * Applied after the fixed-foot min-jerk lateral overlay. Does not change target
 * angles or torque caps. Gate A / capture leave this unset; P1A lift/hold/place
 * swing uses {@link P1A_SWING_MOTOR_SPEED_MAX_RAD_S} (2.25; prepare remains 0.75).
 */
export const P1A_PREPARE_MOTOR_SPEED_MAX_RAD_S = 0.75;
/**
 * P1A lift/hold/place swing hip/knee/ankle motor speed cap (rad/s).
 * Canonical trajectory reachability experiment: 2.25 rad/s (was 0.75, matching
 * prepare). Sized so the ~0.435 rad canonical knee change is ~0.19–0.20 s instead
 * of ~0.58 s. Prepare cap stays 0.75. Stance joints are not capped.
 */
export const P1A_SWING_MOTOR_SPEED_MAX_RAD_S = 2.25;

/**
 * P1A lift/hold/place swing ankle sagittal bound (rad).
 *
 * Relative chain: `ankleSag = plantedAnkle − hipSagDelta − kneeSagDelta`.
 * Restored legacy ±0.25 (experimental 0.35 widening rejected). Gate A / capture /
 * prepare do not use this constant.
 */
export const P1A_SWING_ANKLE_SAG_MAX_RAD = 0.25;
/**
 * Effective torso length (m) for geometry-derived spine counter-lean:
 * spineLat = −atan2(COM.x − desiredCOM.x, torsoLength).
 */
export const P1A_PREPARE_SPINE_TORSO_LENGTH_M = 0.35;
/**
 * Max |ankle roll| (rad) for geometry-derived CoP bias during P1A prepare.
 */
export const P1A_PREPARE_ANKLE_COP_ROLL_MAX_RAD = 0.12;
/**
 * P1A prepare ankle-roll target ceiling (rad). Soft CoP-ish bound below the
 * balance clamp (0.238). Leaves enough roll for transfer; cuts wasted peak.
 *
 * Stage-1 (`artifacts/humanoid-p1-prepare-ankle-sat-attribution.json`): stance
 * ankle sat 0.317 s with |target| up to 0.238 and ~56 Nm cap while CoP-useful
 * is ~37 Nm. Combined with hip rebalance + mild torque headroom → prepare
 * contSat ~0.22 s and ready still clears. Gate A / capture unchanged.
 */
export const P1A_PREPARE_STANCE_ANKLE_ROLL_MAX_RAD = 0.145;
/**
 * Prepare-only scale on balance-layer ankle-roll targets before the ceiling.
 * 1 = full balance gain (errorGain × 0.55).
 */
export const P1A_PREPARE_ANKLE_ROLL_GAIN_SCALE = 1.0;
/**
 * prepareLateralTargets max/gain for prepare hip rebalance (milder than the
 * default 0.2 / 1.85). cp16 signs route residual COM shift through hips
 * (~1000 Nm) without skating the plant past 0.02 m.
 */
export const P1A_PREPARE_HIP_LAT_MAX_RAD = 0.08;
export const P1A_PREPARE_HIP_LAT_GAIN_PER_M = 0.92;
/**
 * Prepare-only stance-ankle maxTorque multiplier. Mild headroom so CoP-useful
 * actual torque is less often ≥98% of the dual-support cap.
 */
export const P1A_PREPARE_ANKLE_TORQUE_SCALE = 1.18;

/** Resolve prepare hip-lat max (rad); absence → production. */
export function resolveP1APrepareHipLatMaxRad(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.prepareHipLatMaxRad;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  return P1A_PREPARE_HIP_LAT_MAX_RAD;
}

/** Resolve prepare hip-lat gain (rad/m); absence → production. */
export function resolveP1APrepareHipLatGainPerM(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.prepareHipLatGainPerM;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  return P1A_PREPARE_HIP_LAT_GAIN_PER_M;
}

/** Resolve prepare stance load-fraction min; absence → production. */
export function resolveP1APrepareStanceLoadFractionMin(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.prepareStanceLoadFractionMin;
  if (typeof v === 'number' && Number.isFinite(v)) return clamp(v, 0.5, 0.95);
  return P1A_PREPARE_STANCE_LOAD_FRACTION_MIN;
}

/** Resolve prepare swing load-fraction max; absence → production. */
export function resolveP1APrepareSwingLoadFractionMax(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.prepareSwingLoadFractionMax;
  if (typeof v === 'number' && Number.isFinite(v)) return clamp(v, 0.05, 0.45);
  return P1A_PREPARE_SWING_LOAD_FRACTION_MAX;
}

/** Resolve prepare shift-load open threshold; absence → production. */
export function resolveP1APrepareShiftLoadFractionStart(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.prepareShiftLoadFractionStart;
  if (typeof v === 'number' && Number.isFinite(v)) return clamp(v, 0.4, 0.85);
  return P1A_PREPARE_SHIFT_LOAD_FRACTION_START;
}

/** Resolve prepare phase timeout base (s); absence → production. */
export function resolveP1APrepareTimeoutS(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.prepareTimeoutS;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  return P1A_PREPARE_TIMEOUT_S;
}

/** Resolve prepare ready-dwell (s); absence → production. */
export function resolveP1APrepareReadyDwellS(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.prepareReadyDwellS;
  if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return v;
  return P1A_PREPARE_READY_DWELL_S;
}

/** Resolve SS stance-column lateral gain (rad/m); absence → production. */
export function resolveP1AStanceColumnLatGainPerM(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.stanceColumnLatGainPerM;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  return P1A_STANCE_COLUMN_LAT_GAIN_PER_M;
}

/** Resolve SS stance-column lateral max (rad); absence → production. */
export function resolveP1AStanceColumnLatMaxRad(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.stanceColumnLatMaxRad;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  return P1A_STANCE_COLUMN_LAT_MAX_RAD;
}

/**
 * Resolve SS stance sagittal gain scale; absence → 1.
 * Clamped to [0.4, 1.8] so search ±40% stays in a safe band.
 */
export function resolveP1ASsStanceGainScale(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.ssStanceGainScale;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
    return clamp(v, 0.4, 1.8);
  }
  return 1;
}

/** Resolve SS swing feedback gain scale; absence → 1. */
export function resolveP1ASsSwingGainScale(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.ssSwingGainScale;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
    return clamp(v, 0.4, 1.8);
  }
  return 1;
}

/** Resolve SS spine lat/sag feedback gain scale; absence → 1. */
export function resolveP1ASsSpineGainScale(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.ssSpineGainScale;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
    return clamp(v, 0.4, 1.8);
  }
  return 1;
}

/**
 * Resolve place-phase swing retract scale. Absent → production default (0).
 * Explicit 0 disables. Clamped to [0, 2].
 */
export function resolveP1APlaceSwingRetractScale(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.placeSwingRetractScale;
  if (typeof v === 'number' && Number.isFinite(v)) {
    return clamp(v, 0, 2);
  }
  return P1A_PLACE_SWING_RETRACT_SCALE;
}

/**
 * Resolve place-phase swing retract gain (s). Absent → production default.
 * Clamped to [0, 0.5].
 */
export function resolveP1APlaceSwingRetractGainS(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.placeSwingRetractGainS;
  if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
    return clamp(v, 0, 0.5);
  }
  return P1A_PLACE_SWING_RETRACT_GAIN_S;
}

/**
 * Resolve place-phase sole-flatten scale. Absent → production default (0).
 * Explicit 0 disables. Clamped to [0, 2].
 */
export function resolveP1APlaceSwingSoleFlattenScale(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.placeSwingSoleFlattenScale;
  if (typeof v === 'number' && Number.isFinite(v)) {
    return clamp(v, 0, 2);
  }
  return P1A_PLACE_SOLE_FLATTEN_SCALE;
}

/**
 * Resolve place-phase sole-flatten gain (rad ankle / rad pitch).
 * Absent → production default. Clamped to [0, 3].
 */
export function resolveP1APlaceSwingSoleFlattenGain(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.placeSwingSoleFlattenGain;
  if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
    return clamp(v, 0, 3);
  }
  return P1A_PLACE_SOLE_FLATTEN_GAIN;
}

/**
 * Resolve forward SS swing knee floor (rad). Absent → −0.55.
 * Clamped to [−0.70, −0.50].
 */
export function resolveP1AForwardSwingKneeSagMinRad(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.forwardSwingKneeSagMinRad;
  if (typeof v === 'number' && Number.isFinite(v)) {
    return clamp(v, -0.7, -0.5);
  }
  return P1A_FORWARD_SWING_KNEE_SAG_MIN_RAD;
}

/**
 * Production plant sagittal shift (m). **0 = off** (P1A identity).
 * Search opt-in; negative = rear (toward COM / skid end-point).
 */
export const P1A_PLACE_PLANT_SHIFT_SAGITTAL_M = 0 as const;
/**
 * Production plant lateral shift (m). **0 = off**.
 */
export const P1A_PLACE_PLANT_SHIFT_LATERAL_M = 0 as const;
/**
 * Max |plant shift| per axis (m) so search cannot invert the step.
 * ~half foot length; keeps advance accounting tractable.
 */
export const P1A_PLACE_PLANT_SHIFT_MAX_M = 0.08 as const;

/**
 * Resolve commanded plant sagittal shift (m, +Z forward). Absent → 0.
 * Clamped to ±{@link P1A_PLACE_PLANT_SHIFT_MAX_M}.
 */
export function resolveP1APlacePlantShiftSagittalM(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.placePlantShiftSagittalM;
  if (typeof v === 'number' && Number.isFinite(v)) {
    return clamp(v, -P1A_PLACE_PLANT_SHIFT_MAX_M, P1A_PLACE_PLANT_SHIFT_MAX_M);
  }
  return P1A_PLACE_PLANT_SHIFT_SAGITTAL_M;
}

/**
 * Resolve commanded plant lateral shift (m, +X). Absent → 0.
 * Clamped to ±{@link P1A_PLACE_PLANT_SHIFT_MAX_M}.
 */
export function resolveP1APlacePlantShiftLateralM(
  knobs?: Readonly<P1ATrialStyleKnobs> | null,
): number {
  const v = knobs?.placePlantShiftLateralM;
  if (typeof v === 'number' && Number.isFinite(v)) {
    return clamp(v, -P1A_PLACE_PLANT_SHIFT_MAX_M, P1A_PLACE_PLANT_SHIFT_MAX_M);
  }
  return P1A_PLACE_PLANT_SHIFT_LATERAL_M;
}

/**
 * Pure plant-target shift for one world axis (m).
 *
 * Push 20: upright recovery on the skidding path relocates the support via
 * post-touchdown slide; quiet landings hold the sole at raw TD = wrong support.
 * Shift the commanded plant by the measured slide vector so quiet TD places
 * the sole where the skid used to deliver it.
 *
 * ```text
 * goal = plant + shift     // only when commandedForward > 0
 * goal ∈ [plant − max, plant + max]  // already applied at resolve
 * ```
 *
 * Returns `plantWorldM` when commanded forward is 0 or shift is 0
 * (P1A identity + production default off).
 */
export function p1aPlacePlantShiftedTargetM(args: {
  commandedForwardM: number;
  plantWorldM: number;
  shiftM?: number;
}): number {
  const plant =
    typeof args.plantWorldM === 'number' && Number.isFinite(args.plantWorldM)
      ? args.plantWorldM
      : 0;
  if (
    !(
      typeof args.commandedForwardM === 'number' &&
      Number.isFinite(args.commandedForwardM) &&
      args.commandedForwardM > 1e-4
    )
  ) {
    return plant;
  }
  const shift =
    typeof args.shiftM === 'number' && Number.isFinite(args.shiftM)
      ? clamp(args.shiftM, -P1A_PLACE_PLANT_SHIFT_MAX_M, P1A_PLACE_PLANT_SHIFT_MAX_M)
      : 0;
  if (!(Math.abs(shift) > 1e-9)) return plant;
  return plant + shift;
}

// ---------------------------------------------------------------------------
// Config / observation / state / output
// ---------------------------------------------------------------------------

export interface MuscleControllerConfig {
  /** Overall strength scale in [0, 1]. Multiplies available torque. */
  strength: number;
  /** When true, left-leg groups receive reduced torque capacity. */
  weakLeftLeg: boolean;
  /**
   * Left-leg capacity multiplier when weakLeftLeg is true.
   * Default 0.60 => 40% reduction (>= 35% gate).
   */
  weakLeftLegCapacity: number;
  /** Max rate at which activation can rise (units of activation per second). */
  activationRisePerS: number;
  /** Max rate at which activation can fall (units of activation per second). */
  activationFallPerS: number;
  /** Fatigue accumulated per second at full mean activation. */
  fatigueGainPerS: number;
  /** Fatigue recovered per second at rest (near-zero activation). */
  fatigueRecoveryPerS: number;
  /**
   * How strongly fatigue reduces torque capacity.
   * capacity *= 1 - fatigue * fatigueTorqueScale.
   */
  fatigueTorqueScale: number;
  /** Support-error (m) above which the controller is considered bracing. */
  braceSupportErrorM: number;
  /** Pelvis tilt (deg) above which the controller is considered recovering. */
  recoverTiltDeg: number;
  /** Mean activation above which phase becomes fatigued (if fatigue high). */
  fatiguePhaseThreshold: number;
  /** Fatigue level that flips phase to 'fatigued'. */
  fatiguePhaseLevel: number;
  /** Pelvis height (m) below which phase is 'fallen'. */
  fallenPelvisHeightM: number;
  /** Pelvis tilt (deg) above which phase is 'fallen'. */
  fallenTiltDeg: number;
  /** Ankle strategy gain: activation per meter of support error. */
  ankleStrategyGain: number;
  /** Hip strategy gain: activation per meter of support error (large lean). */
  hipStrategyGain: number;
  /**
   * Minimum spherical hip/spine engagement (0–1) independent of support error.
   * Quiet lateral balance on narrow anatomical feet needs hips active before SE
   * grows; 0 restores the old “hips only when leaning” gate.
   */
  hipGateFloor: number;
  /** Trunk counter-lean gain from support error. */
  trunkGain: number;
  /** Tilt (deg) contribution to bracing activation. */
  tiltActivationGain: number;
  /** Linear velocity feed-forward scale on support-error demand (s). */
  velocityFeedforwardS: number;
  /** Angular velocity feed-forward scale on support-error demand (m·s/rad). */
  angularFeedforwardS: number;
  /** Quiet-stance co-contraction floor (activation) for driven groups. */
  stanceTone: number;
  /** Proportional map from support error (m) to joint offset fraction of max. */
  errorToOffsetGainPerM: number;
  /**
   * Sign of ankle error→target map (+1 default: +errorZ → +offset / plantarflex).
   * Swept in evaluation diagnostics; −1 flips feedback polarity only.
   */
  ankleErrorSign: number;
  /** Max signed joint target offset (rad) from standing for driven joints. */
  maxJointOffsetRad: number;
  /**
   * Integral gain (1/s): joint-demand correction (m) = integralGainPerS * I,
   * where I is the clamped horizontal error integral (m·s). Affects joint
   * targets / demand only — never body forces.
   */
  integralGainPerS: number;
  /** Clamp on |integral state| (m·s). Anti-windup bound. */
  integralClampMs: number;
  /**
   * Leak rate (1/s) when not accumulating (fallen / stepping / not upright).
   * Also soft anti-windup: I ← I − leak·I·dt while integrating at the clamp.
   */
  integralLeakPerS: number;
  /**
   * Standing support-error bias (m) added to geometric COM−support error before
   * integral / joint demand. Does not change telemetry supportError (still
   * geometric). For anatomical heel-biased feet, +Z ≈ heelBias cancels the
   * artificial COM-behind-midfoot reading so quiet stance regulates about the
   * ankles — joint-setpoint geometry, not a body force.
   */
  stanceErrorBiasXM: number;
  stanceErrorBiasZM: number;
  /**
   * Neutral standing joint setpoints (rad) added to commanded offsets — quiet
   * posture calibration only (e.g. mild plantarflex so COM sits slightly
   * forward of the ankle inside the foot polygon). Not a body force.
   */
  stanceAnkleOffsetRad: number;
  stanceKneeOffsetRad: number;
  stanceHipSagittalOffsetRad: number;
  /**
   * When true, large support/velocity errors can trigger a bounded capture step
   * (joint motors only — no root forces). Checkpoint 10: default false so
   * static integral balance is not masked by stepping.
   */
  stepEnabled: boolean;
  /** Support error (m) that alone can trigger a capture step. */
  stepSupportErrorM: number;
  /** Horizontal COM speed (m/s) used with a lower support-error trigger. */
  stepSpeedMps: number;
  /** Support error (m) that pairs with stepSpeedMps for an earlier trigger. */
  stepSupportErrorWithSpeedM: number;
  /** Pelvis tilt (deg) that can trigger a step when support error is elevated. */
  stepTiltDeg: number;
  /** Minimum support error (m) required with stepTiltDeg. */
  stepSupportErrorWithTiltM: number;
  /**
   * Continuous recovered dwell (s) required after a capture step before another
   * step may be armed. Prevents automatic second steps during the same push trial.
   */
  stepEpisodeResetS: number;
  /** Minimum prepare duration (s) before lift may start. */
  stepPrepareMinS: number;
  /** Max prepare duration (s); timeout aborts without lifting. */
  stepPrepareTimeoutS: number;
  /** COM-to-stance-foot horizontal radius (m) required to leave prepare. */
  stepPrepareComRadiusM: number;
  /** Max tilt (deg) allowed during prepare→lift gate. */
  stepPrepareMaxTiltDeg: number;
  /** Max horizontal COM speed (m/s) allowed during prepare→lift gate. */
  stepPrepareMaxSpeedMps: number;
  /** Lift phase duration (s). */
  stepLiftS: number;
  /** Place phase duration (s). */
  stepPlaceS: number;
  /** Load / re-stance phase duration (s). */
  stepLoadS: number;
  /** Max sagittal step length of the swing foot (m), adult-plausible. */
  stepMaxLengthM: number;
  /** Max lateral step width of the swing foot (m), adult-plausible. */
  stepMaxWidthM: number;
  /** Velocity preview horizon for foot placement (s). */
  stepVelocityPreviewS: number;
  /**
   * LIP capture blend in [0, 1] (checkpoint 17). Lower = shorter, more reachable
   * swing targets.
   */
  stepLipGain: number;
  /**
   * Outward hull expansion margin (m) beyond the current support AABB edge
   * along fall/capture direction (checkpoint 18). Pure geometry — no L/R/F/B cases.
   */
  stepHullExpandMarginM: number;
  /** Place-phase horizontal foot-error gain (1/m → rad bias). */
  stepPlaceGainXZ: number;
  /** Place-phase vertical foot-error gain. */
  stepPlaceGainY: number;
  /** Lift-phase horizontal foot-error gain. */
  stepLiftGainXZ: number;
  /** Swing hip/knee torque scale during lift/place (× base). */
  stepSwingTorqueScale: number;
  /** Base max torque (N·m) at strength=1, fatigue=0, no weakness — per joint. */
  baseMaxTorqueNm: Readonly<Partial<Record<HumanoidJointName, number>>>;
  /**
   * Optional P1A sagittal joint-target offsets (evaluation / offline search).
   * Absent or undefined resolves to all zeros without changing DEFAULT semantics.
   * Only applied while `singleSupportHoldActive` is true.
   */
  p1aSagittalPolicyOffsets?: Readonly<P1ASagittalPolicyOffsets>;
}

/**
 * Default balance law (checkpoint 4).
 *
 * Rig coordinate notes (from humanoidMotor TUCK_POSE / joint limits):
 * parent-local +X rotation is extension for hips/knees; −X is flexion.
 * Ankle hinge +X is plantarflex / "extensor" direction in this controller.
 * Lateral spherical +Z is lean/abduct toward world +X (right).
 */
export const DEFAULT_MUSCLE_CONTROLLER_CONFIG: Readonly<MuscleControllerConfig> = {
  strength: 1,
  weakLeftLeg: false,
  weakLeftLegCapacity: 0.6,
  // Fast enough to brace a moderate push, slow enough to avoid thrashing.
  activationRisePerS: 9,
  activationFallPerS: 4.5,
  fatigueGainPerS: 0.12,
  fatigueRecoveryPerS: 0.05,
  fatigueTorqueScale: 0.55,
  braceSupportErrorM: 0.03,
  recoverTiltDeg: 8,
  fatiguePhaseThreshold: 0.35,
  fatiguePhaseLevel: 0.45,
  fallenPelvisHeightM: 0.65,
  fallenTiltDeg: 55,
  // Ankle-dominant quiet stance; hips grow with larger leans only.
  ankleStrategyGain: 14,
  hipStrategyGain: 8,
  // Checkpoint 12: anatomical feet are ~10 cm wide — residual errX tipped the
  // rig at ~2.5 s while hipGate stayed ~0.1–0.2. Floor keeps hip/spine lateral
  // motors engaged in quiet stance (joint targets only).
  hipGateFloor: 0.55,
  trunkGain: 5.5,
  tiltActivationGain: 0.02,
  // Checkpoint 7 physical sweep: with muscle-spawn linearDamping=0.3 and
  // muscle motor maxSpeed=8, ankleErrorSign=+1 + errorGain=12 holds idle 10s
  // (sign=-1 / gain=4.5 collapsed ~2.7–5.7s). Ankle torque 40–120 N·m was a
  // trajectory no-op (motors track; wrong polarity/gain was binding).
  velocityFeedforwardS: 0.05,
  angularFeedforwardS: 0.012,
  stanceTone: 0.18,
  // Checkpoint 12: with anatomical feet + dorsiflex setpoint, gain 8 cancels
  // residual |err| better than 12 (final SE 0.008 vs 0.072) while holding idle.
  errorToOffsetGainPerM: 8,
  // +1: +errorZ (COM forward) → +ankle offset (plantarflex) restoring COP.
  // Checkpoint 4b preferred −1 alone; combined servo/damping re-validated +1.
  ankleErrorSign: 1,
  maxJointOffsetRad: 0.28,
  // Checkpoint 10/12: integral bias cancel. Anatomical idle still needs the
  // cancel on residual X/Z after ankle-line support calibration.
  integralGainPerS: 0.16,
  integralClampMs: 0.14,
  integralLeakPerS: 0.08,
  // Checkpoint 12: default bias 0 — anatomical support is calibrated in the
  // observation (ankle-line support target), so controller bias stays 0.
  stanceErrorBiasXM: 0,
  stanceErrorBiasZM: 0,
  // Checkpoint 12 idle winner: mild dorsiflex (−0.04 rad) places COM over the
  // ankle-line support target inside the anatomical foot polygon. Positive
  // plantarflex offsets failed idle; −0.04 is the measured pass setpoint.
  stanceAnkleOffsetRad: -0.04,
  stanceKneeOffsetRad: 0.03,
  stanceHipSagittalOffsetRad: 0,
  // Checkpoint 10: capture step disabled by default (static integral path).
  stepEnabled: false,
  stepSupportErrorM: 0.078,
  // Early speed path (SE≥0.045 + |v|≥0.14) catches post-push slip; swing
  // selection ignores residual idle errX when sagittal-dominant so a forward
  // push does not start on the left leg.
  stepSpeedMps: 0.14,
  stepSupportErrorWithSpeedM: 0.045,
  stepTiltDeg: 10,
  stepSupportErrorWithTiltM: 0.05,
  // Checkpoint 8b/13: one capture step per disturbance until sustained recovery.
  stepEpisodeResetS: 0.75,
  // Checkpoint 15/16: prepare = load transfer before lift (both feet planted).
  stepPrepareMinS: 0.08,
  stepPrepareTimeoutS: 0.45,
  /** COM must be within this radius (m) of stance foot to allow lift. */
  stepPrepareComRadiusM: 0.11,
  stepPrepareMaxTiltDeg: 20,
  stepPrepareMaxSpeedMps: 0.65,
  // Checkpoint 17/18: place timing from cp17; hull margin/LIP from cp18 partial.
  stepLiftS: 0.1,
  stepPlaceS: 0.6,
  stepLoadS: 0.4,
  stepMaxLengthM: 0.26,
  stepMaxWidthM: 0.15,
  stepVelocityPreviewS: 0.35,
  // Slightly higher LIP + smaller expand margin (combo-3): best left land ≤0.12
  // with positive spanΔ among low-impulse combos — still not four-dir certified.
  stepLipGain: 0.42,
  // Checkpoint 18: land beyond current support hull by this margin (m).
  stepHullExpandMarginM: 0.04,
  stepPlaceGainXZ: 3.2,
  stepPlaceGainY: 0.75,
  stepLiftGainXZ: 1.25,
  stepSwingTorqueScale: 2.0,
  // Torque budgets aligned with pose-hold motor table × default 75 kg body
  // (humanoidMotor MOTOR_MAX_TORQUE_NM_PER_KG × child segment mass). Earlier
  // 100–150 N·m hips were ~6× under-powered vs the certified stand motors.
  baseMaxTorqueNm: {
    spine: 700,
    leftHip: 1000,
    rightHip: 1000,
    leftKnee: 250,
    rightKnee: 250,
    leftAnkle: 70,
    rightAnkle: 70,
    neck: 115,
    leftShoulder: 70,
    rightShoulder: 70,
    leftElbow: 40,
    rightElbow: 40,
  },
};

/** Balance observation — caller fills from physics each step. */
export interface MuscleObservation {
  /** World-space center of mass (m). */
  centerOfMass: readonly [number, number, number];
  /** Foot support midpoint (m). Y is ignored for support error. */
  supportCenter: readonly [number, number, number];
  /** Pelvis / root world position (m). */
  pelvisPosition: readonly [number, number, number];
  /** Unit vector of pelvis local-up in world space. */
  pelvisUp: readonly [number, number, number];
  /** Pelvis linear velocity (m/s). */
  pelvisVelocity: readonly [number, number, number];
  /** Pelvis angular velocity (rad/s). */
  pelvisAngularVelocity: readonly [number, number, number];
  /**
   * Optional mass-weighted COM velocity (m/s). Capture-step LIP prefers this
   * when present; otherwise falls back to pelvisVelocity.
   */
  centerOfMassVelocity?: readonly [number, number, number];
  /**
   * Optional signed joint angles (rad) relative to the standing pose.
   * Positive flex convention: hip flex, knee flex, ankle dorsiflex, trunk lean.
   */
  jointAngles?: Partial<Record<HumanoidJointName, number>>;
  /** Optional signed joint angular velocities (rad/s). */
  jointVelocities?: Partial<Record<HumanoidJointName, number>>;
  /** Last user push direction (telemetry only; does not apply forces). */
  lastPushDirection?: MusclePushDirection;
  /**
   * Checkpoint 13: live foot / hip geometry for capture-step planning.
   * When omitted, the controller synthesizes feet from supportCenter (unit tests).
   */
  leftFootPosition?: readonly [number, number, number];
  rightFootPosition?: readonly [number, number, number];
  leftFootVelocity?: readonly [number, number, number];
  rightFootVelocity?: readonly [number, number, number];
  leftHipPosition?: readonly [number, number, number];
  rightHipPosition?: readonly [number, number, number];
  /** Near-ground / contact proxies (true when foot Y is low or ray hit). */
  leftFootGrounded?: boolean;
  rightFootGrounded?: boolean;
  /** Post-solver foot contact-load telemetry from the previous fixed step. */
  leftFootContactPointCount?: number;
  rightFootContactPointCount?: number;
  leftFootNormalImpulse?: number;
  rightFootNormalImpulse?: number;
  /**
   * Sole pitch about world +X (rad). Character faces +Z; local +Z is toe.
   * 0 = sole flat (toe horizontal); + = toe up; − = toe down.
   * Gate A push 19 foot-slap flatten feedback. Absent → no flatten term.
   */
  leftFootPitchRad?: number;
  rightFootPitchRad?: number;
}

export interface MuscleGroupActivation {
  flexor: number;
  extensor: number;
}

export interface MuscleControllerState {
  seed: number;
  /** Smoothed antagonistic activations per group, each in [0, 1]. */
  activations: Record<MuscleGroupName, MuscleGroupActivation>;
  /** Global fatigue in [0, 1]. */
  fatigue: number;
  phase: MusclePhase;
  lastPushDirection: MusclePushDirection;
  /** Cumulative simulated time since last reset (s). */
  elapsedS: number;
  /**
   * Always 0 for this pure controller. Integration layers that call forbidden
   * assists must increment their own counters — never this one.
   */
  forbiddenAssistCount: number;
  /** Capture-step sub-phase (`none` when not stepping). */
  stepPhase: MuscleStepPhase;
  /** Elapsed time inside the current step sub-phase (s). */
  stepPhaseElapsedS: number;
  /**
   * When false, no new capture step may start until recovered dwell re-arms.
   * Cleared on planCaptureStep; restored after stepEpisodeResetS of recovery.
   */
  stepArmed: boolean;
  /** Continuous time in the recovered envelope while stepArmed is false (s). */
  recoveredDwellS: number;
  /**
   * Horizontal COM−support error integral (m·s), lateral X.
   * Accumulates only while upright and not stepping/fallen.
   */
  integralErrorXM: number;
  /** Horizontal COM−support error integral (m·s), sagittal Z. */
  integralErrorZM: number;
  /** Swing leg for the active / last capture step. */
  swingSide: MuscleSwingSide | null;
  /** Planned swing-foot lateral placement relative to support (m, +right). */
  stepTargetXM: number;
  /** Planned swing-foot sagittal placement relative to support (m, +forward). */
  stepTargetZM: number;
  /** Absolute world swing-foot target (m) — geometry capture. */
  stepTargetWorldXM: number;
  stepTargetWorldYM: number;
  stepTargetWorldZM: number;
  /** Horizontal landing error (m) at last place→load transition. */
  stepLandingErrorM: number;
  /** Inter-foot span before step (m). */
  supportSpanBeforeM: number;
  /** Inter-foot span after plant (m) — measured when available. */
  supportSpanAfterM: number;
  /** Checkpoint 15: time spent in prepare before lift or abort (s). */
  prepareDurationS: number;
  /** Horizontal COM distance to stance foot (m); updated each prepare frame. */
  comToStanceM: number;
  /** Min COM-to-stance distance observed during prepare (m) — stability proxy. */
  minComToStanceM: number;
  /** |COM.x − stanceFoot.x| (m); updated each prepare frame. */
  comLateralToStanceM: number;
  /** |COM.z − stanceFoot.z| (m); updated each prepare frame. */
  comSagittalToStanceM: number;
  /** Min lateral COM-to-stance error during prepare (m). */
  minComLateralToStanceM: number;
  /** Min sagittal COM-to-stance error during prepare (m). */
  minComSagittalToStanceM: number;
  /** Peak swing-foot lift height above plant Y during lift/place (m). */
  swingLiftM: number;
  /** True if prepare timed out without lifting. */
  prepareAborted: boolean;
  /**
   * Horizontal distance (m) from live stance foot center to latched
   * `stancePlantWorldX/Z` (0 when not latched). Updated each prepare frame.
   */
  prepareStancePlantErrorM: number;
  /** Min prepare stance plant error observed (m); Infinity until measured. */
  minPrepareStancePlantErrorM: number;
  /** Max prepare stance plant error observed (m). */
  maxPrepareStancePlantErrorM: number;
  /**
   * Continuous seconds the full P1A prepare ready condition has held without
   * interruption. Reset to 0 whenever any ready condition fails.
   */
  prepareReadyDwellS: number;
  /** Peak consecutive ready dwell observed during prepare (s). */
  maxPrepareReadyDwellS: number;
  /** Horizontal landing error trend: previous frame err for plant gate. */
  prevLandingErrorM: number;
  /** Number of capture steps started since reset (telemetry / tests). */
  stepCount: number;
  /**
   * When true, joint motors hold the frozen planted-support pose from the
   * completed capture step (not symmetric neutral). Cleared only after
   * recovered dwell re-arms or on reset/fall.
   */
  plantedPoseActive: boolean;
  /** Frozen planted targets (rad) — valid while plantedPoseActive. */
  plantedSwingHipSag: number;
  plantedSwingHipLat: number;
  plantedSwingKnee: number;
  plantedSwingAnkle: number;
  plantedStanceHipSag: number;
  plantedStanceHipLat: number;
  plantedStanceKnee: number;
  plantedStanceAnkle: number;
  plantedSpineSag: number;
  plantedSpineLat: number;
  /** P1A-only latched swing ankle lateral target for relative trajectory control. */
  p1aSwingAnkleLat: number;
  /**
   * Gate A push 11 experiment state (slew / first-impulse latch). Cleared on
   * every load tick while the retained mild soft-track path is active; kept on
   * the state object so a future load-consistent plant experiment can re-use
   * without a schema change. P1A zero-forward never engages this path.
   */
  earlyLoadHipSagSlewRad: number;
  /** True while an early-load hip-sag experiment integrator is latched. */
  earlyLoadHipSagSlewActive: boolean;
  /**
   * Gate A push 12: COM forward speed (m/s, +Z) latched at first impulse-
   * bearing load sample. Indexes the momentum-absorbing new-knee flex profile.
   * Zero-forward never latches.
   */
  momentumAbsorbTouchdownComVzMps: number;
  /** True once touchdown COM forward speed has been latched this step. */
  momentumAbsorbTouchdownLatched: boolean;
  /**
   * P1A place: original swing plant world X latched at plan (reach-clamp origin).
   * Not used on Gate A / capture paths.
   */
  p1aPlaceOriginWorldXM: number;
  /**
   * P1A place: once true, stop updating the lateral capture-point target so the
   * final near-ground approach is not jerked. Reset on plan / clear.
   */
  p1aPlaceCaptureTargetFrozen: boolean;
  /**
   * Pending single-use explicit step request. Consumed on the next armed
   * non-stepping control tick. Does not require `stepEnabled`.
   */
  pendingOneStep: ExplicitOneStepRequest | null;
  /**
   * Pending single-use P1A single-support hold request. Mutually exclusive
   * with pendingOneStep. Does not require `stepEnabled`.
   */
  pendingSingleSupportHold: ExplicitSingleSupportHoldRequest | null;
  /**
   * Forward one-step deferred while arms pre-settle in quiet double support.
   * Consumed when the pre-settle ready gate fires → begin prepare.
   * Zero-forward P1A never uses this path.
   */
  pendingArmPreSettleStep: ExplicitOneStepRequest | null;
  /**
   * True while ramping arms to hang under ordinary balance (stepPhase stays
   * `none`). Gate A push 8: must complete before prepare on commanded forward.
   */
  armPreSettleActive: boolean;
  /** Elapsed time in the arm pre-settle window (s). */
  armPreSettleElapsedS: number;
  /** Continuous quiet dwell after ramp complete (s). */
  armPreSettleReadyDwellS: number;
  /**
   * Soft-slewed shoulder hang target (rad) during pre-settle / latched hold.
   * Slew rate-limited so residual (and contSat) stay small.
   */
  armShoulderTargetRad: number;
  /** Soft-slewed elbow flex target (rad). */
  armElbowTargetRad: number;
  /**
   * True once hang is latched (post pre-settle or mid-step). Arms keep hang
   * targets + low torque through the whole forward step.
   */
  armHoldLatched: boolean;
  /** True while the active step was started by an explicit command. */
  explicitStepActive: boolean;
  /**
   * True while the active motion uses the certified P1A single-support
   * pipeline (prepare load-share → lift → hold → capture place → load).
   * Covers in-place replace (`commandedStepForwardM === 0`) and Gate A / P1B
   * forward steps on the same mechanisms. False for capture-recovery steps.
   */
  singleSupportHoldActive: boolean;
  /** Last commanded forward displacement (m) for telemetry. */
  commandedStepForwardM: number;
  /** Last commanded lateral displacement (m) for telemetry. */
  commandedStepLateralM: number;
  /** True when the planned target was reach-clamped. */
  stepTargetClamped: boolean;
  /**
   * Original stance-foot world plant latched at explicit plan start.
   * Used only for explicit one-step plant retention (Gate A / P1A).
   */
  stancePlantWorldXM: number;
  stancePlantWorldYM: number;
  stancePlantWorldZM: number;
  /** True when stancePlantWorld* was latched for the active explicit step. */
  stancePlantLatched: boolean;
  /**
   * Evaluation/search-only P1A style + place-geometry knobs.
   * Null/absent → production defaults. Not part of state hash.
   * Survives clearStepMotion; cleared on resetMuscleController.
   */
  p1aTrialStyleKnobs: Readonly<P1ATrialStyleKnobs> | null;
}

/**
 * Driven joints that always emit spherical-style [sag,0,lat] commands.
 * Checkpoint 20: ankles also fill lateral (Z) so muscleBalance spherical
 * ankles get real roll; default revolute ankles still only actuate X via the
 * motor bridge (Z ignored).
 */
export const MUSCLE_SPHERICAL_JOINTS: readonly HumanoidJointName[] = [
  'spine',
  'leftHip',
  'rightHip',
  'leftAnkle',
  'rightAnkle',
] as const;

/** True if the controller fills a full [X,0,Z] target vector for this joint. */
export function isMuscleSphericalJoint(joint: HumanoidJointName): boolean {
  return (
    joint === 'spine' ||
    joint === 'leftHip' ||
    joint === 'rightHip' ||
    joint === 'leftAnkle' ||
    joint === 'rightAnkle'
  );
}

/**
 * Per-joint command produced by the controller for motor/torque integration.
 *
 * Coordinate convention (parent-local, matching STAND_POSE / TUCK_POSE):
 * - X = sagittal (positive = extend / plantarflex; negative = flex / dorsiflex)
 * - Y = twist (always 0 for this balance controller)
 * - Z = lateral (positive = lean/abduct toward +X / right)
 *
 * Revolute knees only use the hinge scalar (`targetOffsetRad` = X).
 * Spherical hips/spine/ankles fill `targetRotationVectorRad` with bounded [X, 0, Z].
 * Default revolute ankles still receive Z in the vector; the motor bridge
 * discards non-hinge DOF when the joint is revolute.
 */
export interface MuscleJointCommand {
  /**
   * Signed hinge / sagittal target offset from stand pose (rad).
   * Positive = extend. For spherical joints this equals the X component of
   * `targetRotationVectorRad`.
   */
  targetOffsetRad: number;
  /**
   * Parent-local rotation-vector target (rad). Revolute joints put the hinge
   * scalar on X and leave Y/Z at 0. Spherical joints set X=sagittal, Z=lateral.
   */
  targetRotationVectorRad: readonly [number, number, number];
  /** Available torque budget (N·m) after strength / weakness / fatigue. */
  maxTorqueNm: number;
  /** Capacity scale in [0, 1] (strength * weakness * fatigue factor). */
  capacity: number;
  /** Combined activation used for this joint (max of mapped group dirs). */
  activation: number;
  /**
   * Optional per-command motor target-speed cap (rad/s). When finite and
   * nonnegative, the motor bridge clamps revolute/spherical target speed to
   * min(global muscle max 8 rad/s, this value). Absent → global only.
   * Zero → target velocity zero while the motor stays enabled with its torque
   * cap. Impulse fallback ignores this field.
   */
  maxSpeedRadS?: number;
}

export interface MuscleControllerOutput {
  controlMode: 'muscle-inspired';
  phase: MusclePhase;
  /** Peak group activation (max of flexor/extensor across groups). */
  peakActivation: number;
  /** Mean of per-group max(flexor, extensor). */
  meanActivation: number;
  fatigue: number;
  weakLeftLeg: boolean;
  strength: number;
  centerOfMass: readonly [number, number, number];
  supportCenter: readonly [number, number, number];
  /** Horizontal COM-minus-support distance (m). */
  supportErrorM: number;
  pelvisTiltDeg: number;
  lastPushDirection: MusclePushDirection;
  forbiddenAssistCount: number;
  /** Per-group max(flexor, extensor) in [0, 1] for visualization. */
  groupActivation: Record<MuscleGroupName, number>;
  /** Full antagonistic state (copy-safe snapshot). */
  activations: Record<MuscleGroupName, MuscleGroupActivation>;
  /** Driven-joint commands only; undriven joints are omitted. */
  jointCommands: Partial<Record<HumanoidJointName, MuscleJointCommand>>;
  /**
   * Capability-safe flag: when joint motors are unavailable the integration
   * layer should use equal-and-opposite torque impulses, never forbidden
   * root assists. The pure controller itself never applies physics.
   */
  preferJointMotors: boolean;
  /** Capture-step telemetry (additive; always present). */
  stepPhase: MuscleStepPhase;
  swingSide: MuscleSwingSide | null;
  stepCount: number;
  stepTargetXM: number;
  stepTargetZM: number;
  /** Absolute world swing-foot target (m). */
  stepTargetWorldXM: number;
  stepTargetWorldYM: number;
  stepTargetWorldZM: number;
  /** Horizontal landing error at plant (m). */
  stepLandingErrorM: number;
  supportSpanBeforeM: number;
  supportSpanAfterM: number;
  prepareDurationS: number;
  comToStanceM: number;
  minComToStanceM: number;
  comLateralToStanceM: number;
  comSagittalToStanceM: number;
  minComLateralToStanceM: number;
  minComSagittalToStanceM: number;
  swingLiftM: number;
  prepareAborted: boolean;
  prepareStancePlantErrorM: number;
  minPrepareStancePlantErrorM: number;
  maxPrepareStancePlantErrorM: number;
  prepareReadyDwellS: number;
  maxPrepareReadyDwellS: number;
  /** True when another capture step may start. */
  stepArmed: boolean;
  /** True while holding the frozen planted-support motor pose. */
  plantedPoseActive: boolean;
  /** Continuous recovered dwell toward episode re-arm (s). */
  recoveredDwellS: number;
  /** Integral state (m·s) and applied joint-demand correction (m). */
  integralErrorXM: number;
  integralErrorZM: number;
  integralCorrectionXM: number;
  integralCorrectionZM: number;
  /** True when the active/last step was an explicit one-step command. */
  explicitStepActive: boolean;
  /** True when the active motion is the P1A hold/replace diagnostic. */
  singleSupportHoldActive: boolean;
  commandedStepForwardM: number;
  commandedStepLateralM: number;
  stepTargetClamped: boolean;
  /** True when a single-use explicit step is queued. */
  pendingOneStep: boolean;
  /** True when a single-use P1A hold/replace is queued. */
  pendingSingleSupportHold: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function clamp(value: number, lo: number, hi: number): number {
  if (!Number.isFinite(value)) return lo;
  return value < lo ? lo : value > hi ? hi : value;
}

function emptyActivations(): Record<MuscleGroupName, MuscleGroupActivation> {
  const out = {} as Record<MuscleGroupName, MuscleGroupActivation>;
  for (const name of MUSCLE_GROUP_NAMES) {
    out[name] = { flexor: 0, extensor: 0 };
  }
  return out;
}

function cloneActivations(
  source: Record<MuscleGroupName, MuscleGroupActivation>,
): Record<MuscleGroupName, MuscleGroupActivation> {
  const out = {} as Record<MuscleGroupName, MuscleGroupActivation>;
  for (const name of MUSCLE_GROUP_NAMES) {
    const entry = source[name];
    out[name] = { flexor: entry.flexor, extensor: entry.extensor };
  }
  return out;
}

function resolveConfig(partial?: Partial<MuscleControllerConfig>): MuscleControllerConfig {
  const base = DEFAULT_MUSCLE_CONTROLLER_CONFIG;
  return {
    strength: clamp01(partial?.strength ?? base.strength),
    weakLeftLeg: partial?.weakLeftLeg ?? base.weakLeftLeg,
    weakLeftLegCapacity: clamp01(partial?.weakLeftLegCapacity ?? base.weakLeftLegCapacity),
    activationRisePerS: Math.max(0, partial?.activationRisePerS ?? base.activationRisePerS),
    activationFallPerS: Math.max(0, partial?.activationFallPerS ?? base.activationFallPerS),
    fatigueGainPerS: Math.max(0, partial?.fatigueGainPerS ?? base.fatigueGainPerS),
    fatigueRecoveryPerS: Math.max(0, partial?.fatigueRecoveryPerS ?? base.fatigueRecoveryPerS),
    fatigueTorqueScale: clamp01(partial?.fatigueTorqueScale ?? base.fatigueTorqueScale),
    braceSupportErrorM: Math.max(0, partial?.braceSupportErrorM ?? base.braceSupportErrorM),
    recoverTiltDeg: Math.max(0, partial?.recoverTiltDeg ?? base.recoverTiltDeg),
    fatiguePhaseThreshold: clamp01(partial?.fatiguePhaseThreshold ?? base.fatiguePhaseThreshold),
    fatiguePhaseLevel: clamp01(partial?.fatiguePhaseLevel ?? base.fatiguePhaseLevel),
    fallenPelvisHeightM: Math.max(0, partial?.fallenPelvisHeightM ?? base.fallenPelvisHeightM),
    fallenTiltDeg: Math.max(0, partial?.fallenTiltDeg ?? base.fallenTiltDeg),
    ankleStrategyGain: Math.max(0, partial?.ankleStrategyGain ?? base.ankleStrategyGain),
    hipStrategyGain: Math.max(0, partial?.hipStrategyGain ?? base.hipStrategyGain),
    hipGateFloor: clamp01(partial?.hipGateFloor ?? base.hipGateFloor),
    trunkGain: Math.max(0, partial?.trunkGain ?? base.trunkGain),
    tiltActivationGain: Math.max(0, partial?.tiltActivationGain ?? base.tiltActivationGain),
    velocityFeedforwardS: Math.max(0, partial?.velocityFeedforwardS ?? base.velocityFeedforwardS),
    angularFeedforwardS: Math.max(0, partial?.angularFeedforwardS ?? base.angularFeedforwardS),
    stanceTone: clamp01(partial?.stanceTone ?? base.stanceTone),
    errorToOffsetGainPerM: Math.max(0, partial?.errorToOffsetGainPerM ?? base.errorToOffsetGainPerM),
    ankleErrorSign: (partial?.ankleErrorSign ?? base.ankleErrorSign) < 0 ? -1 : 1,
    maxJointOffsetRad: Math.max(0, partial?.maxJointOffsetRad ?? base.maxJointOffsetRad),
    integralGainPerS: Math.max(0, partial?.integralGainPerS ?? base.integralGainPerS),
    integralClampMs: Math.max(0, partial?.integralClampMs ?? base.integralClampMs),
    integralLeakPerS: Math.max(0, partial?.integralLeakPerS ?? base.integralLeakPerS),
    stanceErrorBiasXM: Number.isFinite(partial?.stanceErrorBiasXM ?? NaN)
      ? (partial!.stanceErrorBiasXM as number)
      : base.stanceErrorBiasXM,
    stanceErrorBiasZM: Number.isFinite(partial?.stanceErrorBiasZM ?? NaN)
      ? (partial!.stanceErrorBiasZM as number)
      : base.stanceErrorBiasZM,
    stanceAnkleOffsetRad: Number.isFinite(partial?.stanceAnkleOffsetRad ?? NaN)
      ? (partial!.stanceAnkleOffsetRad as number)
      : base.stanceAnkleOffsetRad,
    stanceKneeOffsetRad: Number.isFinite(partial?.stanceKneeOffsetRad ?? NaN)
      ? (partial!.stanceKneeOffsetRad as number)
      : base.stanceKneeOffsetRad,
    stanceHipSagittalOffsetRad: Number.isFinite(partial?.stanceHipSagittalOffsetRad ?? NaN)
      ? (partial!.stanceHipSagittalOffsetRad as number)
      : base.stanceHipSagittalOffsetRad,
    stepEnabled: partial?.stepEnabled ?? base.stepEnabled,
    stepSupportErrorM: Math.max(0, partial?.stepSupportErrorM ?? base.stepSupportErrorM),
    stepSpeedMps: Math.max(0, partial?.stepSpeedMps ?? base.stepSpeedMps),
    stepSupportErrorWithSpeedM: Math.max(
      0,
      partial?.stepSupportErrorWithSpeedM ?? base.stepSupportErrorWithSpeedM,
    ),
    stepTiltDeg: Math.max(0, partial?.stepTiltDeg ?? base.stepTiltDeg),
    stepSupportErrorWithTiltM: Math.max(
      0,
      partial?.stepSupportErrorWithTiltM ?? base.stepSupportErrorWithTiltM,
    ),
    stepEpisodeResetS: Math.max(0.1, partial?.stepEpisodeResetS ?? base.stepEpisodeResetS),
    stepPrepareMinS: Math.max(0.02, partial?.stepPrepareMinS ?? base.stepPrepareMinS),
    stepPrepareTimeoutS: Math.max(
      0.05,
      partial?.stepPrepareTimeoutS ?? base.stepPrepareTimeoutS,
    ),
    stepPrepareComRadiusM: Math.max(
      0.02,
      partial?.stepPrepareComRadiusM ?? base.stepPrepareComRadiusM,
    ),
    stepPrepareMaxTiltDeg: Math.max(1, partial?.stepPrepareMaxTiltDeg ?? base.stepPrepareMaxTiltDeg),
    stepPrepareMaxSpeedMps: Math.max(
      0.05,
      partial?.stepPrepareMaxSpeedMps ?? base.stepPrepareMaxSpeedMps,
    ),
    stepLiftS: Math.max(0.02, partial?.stepLiftS ?? base.stepLiftS),
    stepPlaceS: Math.max(0.02, partial?.stepPlaceS ?? base.stepPlaceS),
    stepLoadS: Math.max(0.02, partial?.stepLoadS ?? base.stepLoadS),
    stepMaxLengthM: Math.max(0.05, partial?.stepMaxLengthM ?? base.stepMaxLengthM),
    stepMaxWidthM: Math.max(0.04, partial?.stepMaxWidthM ?? base.stepMaxWidthM),
    stepVelocityPreviewS: Math.max(0, partial?.stepVelocityPreviewS ?? base.stepVelocityPreviewS),
    stepLipGain: clamp(partial?.stepLipGain ?? base.stepLipGain, 0, 1),
    stepHullExpandMarginM: Math.max(
      0.01,
      partial?.stepHullExpandMarginM ?? base.stepHullExpandMarginM,
    ),
    stepPlaceGainXZ: Math.max(0, partial?.stepPlaceGainXZ ?? base.stepPlaceGainXZ),
    stepPlaceGainY: Math.max(0, partial?.stepPlaceGainY ?? base.stepPlaceGainY),
    stepLiftGainXZ: Math.max(0, partial?.stepLiftGainXZ ?? base.stepLiftGainXZ),
    stepSwingTorqueScale: Math.max(0.5, partial?.stepSwingTorqueScale ?? base.stepSwingTorqueScale),
    baseMaxTorqueNm: {
      ...base.baseMaxTorqueNm,
      ...(partial?.baseMaxTorqueNm ?? {}),
    },
    // Optional evaluation-only policy: preserve absence (undefined) so DEFAULT
    // object semantics stay unchanged; application resolves undefined → zero.
    ...(partial?.p1aSagittalPolicyOffsets !== undefined
      ? { p1aSagittalPolicyOffsets: partial.p1aSagittalPolicyOffsets }
      : {}),
  };
}

/** Smoothly approach a target activation with asymmetric rise/fall rates. */
export function approachActivation(
  current: number,
  target: number,
  dt: number,
  risePerS: number,
  fallPerS: number,
): number {
  const safeDt = Math.max(0, dt);
  const cur = clamp01(current);
  const tgt = clamp01(target);
  if (safeDt === 0 || cur === tgt) return cur;
  const rate = tgt > cur ? risePerS : fallPerS;
  const maxStep = rate * safeDt;
  const delta = tgt - cur;
  if (Math.abs(delta) <= maxStep) return tgt;
  return clamp01(cur + Math.sign(delta) * maxStep);
}

/** Pelvis tilt from world-up in degrees (0 = upright). */
export function pelvisTiltDegFromUp(pelvisUp: readonly [number, number, number]): number {
  const len = Math.hypot(pelvisUp[0], pelvisUp[1], pelvisUp[2]) || 1;
  const uy = clamp(pelvisUp[1] / len, -1, 1);
  // angle between pelvis-up and world-up [0,1,0]
  return (Math.acos(uy) * 180) / Math.PI;
}

/** Horizontal support error magnitude (m). */
export function supportErrorM(
  centerOfMass: readonly [number, number, number],
  supportCenter: readonly [number, number, number],
): number {
  const dx = centerOfMass[0] - supportCenter[0];
  const dz = centerOfMass[2] - supportCenter[2];
  return Math.hypot(dx, dz);
}

function isLeftLegGroup(group: MuscleGroupName): boolean {
  return group === 'leftAnkle' || group === 'leftKnee' || group === 'leftHip';
}

function groupToJoint(group: MuscleGroupName): HumanoidJointName {
  if (group === 'trunk') return 'spine';
  return group;
}

// ---------------------------------------------------------------------------
// Desired (unsmoothed) antagonistic targets from balance error
// ---------------------------------------------------------------------------

interface DesiredActivations {
  targets: Record<MuscleGroupName, MuscleGroupActivation>;
  /**
   * Geometric horizontal COM-minus-support (m). Telemetry / supportError use
   * pure geometry. Joint *targets* use controlError* (geometry + integral).
   */
  errorX: number;
  errorZ: number;
  /** Geometry + integral correction (m) used for demand and joint offsets. */
  controlErrorX: number;
  controlErrorZ: number;
  /** Demand including velocity/angular feed-forward (activation strategy). */
  demandX: number;
  demandZ: number;
  supportError: number;
  tiltDeg: number;
  integralCorrectionXM: number;
  integralCorrectionZM: number;
}

/**
 * Update bounded horizontal error integrals. Accumulates only while upright
 * and not actively stepping / fallen. Leak when not accumulating; soft
 * anti-windup when held at clamp against same-sign error.
 */
function updateSupportErrorIntegral(
  state: MuscleControllerState,
  errorX: number,
  errorZ: number,
  tiltDeg: number,
  pelvisYM: number,
  config: MuscleControllerConfig,
  dt: number,
): void {
  const safeDt = Math.max(0, dt);
  if (safeDt === 0) return;
  const clampMs = config.integralClampMs;
  const leak = config.integralLeakPerS;
  // Leak (do not accumulate) during arm pre-settle — arm COM shift would
  // wind up the bias and poison the subsequent prepare transfer.
  const upright =
    pelvisYM >= config.fallenPelvisHeightM + 0.1 &&
    tiltDeg <= config.recoverTiltDeg + 4 &&
    state.stepPhase === 'none' &&
    !state.armPreSettleActive &&
    state.phase !== 'fallen';

  const integrateAxis = (I: number, err: number): number => {
    if (!upright) {
      // Leak toward zero when not accumulating.
      return I * Math.exp(-leak * safeDt);
    }
    let next = I + err * safeDt;
    // Soft anti-windup: if saturating same sign as error, apply leak on the excess.
    if (Math.abs(next) > clampMs && Math.sign(next) === Math.sign(err) && err !== 0) {
      next = I + err * safeDt - leak * I * safeDt;
    }
    return clamp(next, -clampMs, clampMs);
  };

  state.integralErrorXM = integrateAxis(state.integralErrorXM, errorX);
  state.integralErrorZM = integrateAxis(state.integralErrorZM, errorZ);
}

/**
 * Ankle strategy for small leans, hip/trunk strategy for larger leans.
 * Lateral error (+X / right) → right-leg push + left brace + opposite trunk lean.
 * Sagittal error (+Z / forward) → bilateral ankle plantarflex (extensor) + hip
 * extend + trunk extend (motor +X = extend for hips/knees; see config header).
 */
function computeDesiredActivations(
  obs: MuscleObservation,
  config: MuscleControllerConfig,
  state: MuscleControllerState,
): DesiredActivations {
  const geoErrorX = obs.centerOfMass[0] - obs.supportCenter[0];
  const geoErrorZ = obs.centerOfMass[2] - obs.supportCenter[2];
  // Geometric support error for phase / strategy scale / telemetry honesty.
  const supportError = Math.hypot(geoErrorX, geoErrorZ);
  const tiltDeg = pelvisTiltDegFromUp(obs.pelvisUp);

  // Stance bias shifts the joint-demand setpoint (ankle/hip regulation frame).
  const errorX = geoErrorX + config.stanceErrorBiasXM;
  const errorZ = geoErrorZ + config.stanceErrorBiasZM;

  const integralCorrectionXM = config.integralGainPerS * state.integralErrorXM;
  const integralCorrectionZM = config.integralGainPerS * state.integralErrorZM;
  // Joint-demand error includes integral bias cancel (still meters).
  const controlErrorX = errorX + integralCorrectionXM;
  const controlErrorZ = errorZ + integralCorrectionZM;

  // Linear + angular velocity feed-forward so the controller braces into a push
  // before support error peaks. wx (about +X) couples into sagittal demand;
  // wz couples into lateral demand.
  const velX = obs.pelvisVelocity[0];
  const velZ = obs.pelvisVelocity[2];
  // Clamp angular rates so a single noisy frame cannot saturate demand.
  const wx = clamp(obs.pelvisAngularVelocity[0], -4, 4);
  const wz = clamp(obs.pelvisAngularVelocity[2], -4, 4);
  const demandX = clamp(
    controlErrorX + config.velocityFeedforwardS * velX + config.angularFeedforwardS * wz,
    -0.45,
    0.45,
  );
  const demandZ = clamp(
    controlErrorZ + config.velocityFeedforwardS * velZ - config.angularFeedforwardS * wx,
    -0.45,
    0.45,
  );

  const tiltBoost = clamp01(tiltDeg * config.tiltActivationGain);

  // Strategy blend: ankles dominate small error; hips/trunk grow with lean.
  // Quiet stance (<~5 cm) is nearly pure ankle + knee lock.
  const ankleScale = clamp01(1.05 - supportError * 2.5);
  const hipScale = clamp01(Math.max(0, supportError - 0.03) * 6);

  const ankleLat = clamp01(Math.abs(demandX) * config.ankleStrategyGain) * ankleScale;
  const ankleSag = clamp01(Math.abs(demandZ) * config.ankleStrategyGain) * Math.max(ankleScale, 0.45);
  const hipLat = clamp01(Math.abs(demandX) * config.hipStrategyGain) * hipScale;
  const hipSag = clamp01(Math.abs(demandZ) * config.hipStrategyGain) * Math.max(hipScale, 0.45);
  const trunkLat = clamp01(Math.abs(demandX) * config.trunkGain);
  const trunkSag = clamp01(Math.abs(demandZ) * config.trunkGain);

  const targets = emptyActivations();

  // Sagittal: forward lean (demandZ > 0) → plantarflex / hip extend / trunk extend.
  // Knee extend scales with lean/tilt only — quiet stance uses stanceTone floor below.
  if (demandZ >= 0) {
    targets.leftAnkle.extensor = clamp01(ankleSag + tiltBoost * 0.35);
    targets.rightAnkle.extensor = clamp01(ankleSag + tiltBoost * 0.35);
    targets.leftHip.extensor = clamp01(hipSag + tiltBoost * 0.45);
    targets.rightHip.extensor = clamp01(hipSag + tiltBoost * 0.45);
    targets.leftKnee.extensor = clamp01(hipSag * 0.7 + tiltBoost * 0.35);
    targets.rightKnee.extensor = clamp01(hipSag * 0.7 + tiltBoost * 0.35);
    targets.trunk.extensor = clamp01(trunkSag + tiltBoost * 0.45);
  } else {
    targets.leftAnkle.flexor = clamp01(ankleSag + tiltBoost * 0.35);
    targets.rightAnkle.flexor = clamp01(ankleSag + tiltBoost * 0.35);
    targets.leftHip.flexor = clamp01(hipSag + tiltBoost * 0.45);
    targets.rightHip.flexor = clamp01(hipSag + tiltBoost * 0.45);
    // Prefer extend over flex while recovering aft lean (anti-buckle).
    targets.leftKnee.extensor = clamp01(hipSag * 0.45 + tiltBoost * 0.25);
    targets.rightKnee.extensor = clamp01(hipSag * 0.45 + tiltBoost * 0.25);
    targets.trunk.flexor = clamp01(trunkSag + tiltBoost * 0.45);
  }

  // Lateral: demandX > 0 (COM right of feet) → right side push, left side brace.
  if (demandX >= 0) {
    targets.rightAnkle.extensor = clamp01(targets.rightAnkle.extensor + ankleLat);
    targets.leftAnkle.flexor = clamp01(targets.leftAnkle.flexor + ankleLat * 0.75);
    targets.rightHip.extensor = clamp01(targets.rightHip.extensor + hipLat);
    targets.leftHip.flexor = clamp01(targets.leftHip.flexor + hipLat * 0.85);
    targets.rightKnee.extensor = clamp01(targets.rightKnee.extensor + hipLat * 0.55);
    targets.leftKnee.extensor = clamp01(targets.leftKnee.extensor + hipLat * 0.35);
    targets.trunk.flexor = clamp01(targets.trunk.flexor + trunkLat * 0.65);
    targets.trunk.extensor = clamp01(targets.trunk.extensor + trunkLat * 0.3);
  } else {
    targets.leftAnkle.extensor = clamp01(targets.leftAnkle.extensor + ankleLat);
    targets.rightAnkle.flexor = clamp01(targets.rightAnkle.flexor + ankleLat * 0.75);
    targets.leftHip.extensor = clamp01(targets.leftHip.extensor + hipLat);
    targets.rightHip.flexor = clamp01(targets.rightHip.flexor + hipLat * 0.85);
    targets.leftKnee.extensor = clamp01(targets.leftKnee.extensor + hipLat * 0.55);
    targets.rightKnee.extensor = clamp01(targets.rightKnee.extensor + hipLat * 0.35);
    targets.trunk.extensor = clamp01(targets.trunk.extensor + trunkLat * 0.65);
    targets.trunk.flexor = clamp01(targets.trunk.flexor + trunkLat * 0.3);
  }

  // Quiet-stance co-contraction: hold knees/hips/ankles against gravity.
  // Prefer stronger knee lock; keep ankles/hips lighter so micro-corrections win.
  const tone = config.stanceTone;
  targets.leftKnee.extensor = clamp01(Math.max(targets.leftKnee.extensor, tone + 0.2));
  targets.rightKnee.extensor = clamp01(Math.max(targets.rightKnee.extensor, tone + 0.2));
  targets.leftHip.extensor = clamp01(Math.max(targets.leftHip.extensor, tone));
  targets.rightHip.extensor = clamp01(Math.max(targets.rightHip.extensor, tone));
  targets.leftAnkle.extensor = clamp01(Math.max(targets.leftAnkle.extensor, tone * 0.7));
  targets.rightAnkle.extensor = clamp01(Math.max(targets.rightAnkle.extensor, tone * 0.7));
  targets.trunk.extensor = clamp01(Math.max(targets.trunk.extensor, tone * 0.6));

  return {
    targets,
    errorX,
    errorZ,
    controlErrorX,
    controlErrorZ,
    demandX,
    demandZ,
    supportError,
    tiltDeg,
    integralCorrectionXM,
    integralCorrectionZM,
  };
}

function smoothActivations(
  state: MuscleControllerState,
  desired: Record<MuscleGroupName, MuscleGroupActivation>,
  dt: number,
  config: MuscleControllerConfig,
): void {
  for (const name of MUSCLE_GROUP_NAMES) {
    const cur = state.activations[name];
    const tgt = desired[name];
    cur.flexor = approachActivation(cur.flexor, tgt.flexor, dt, config.activationRisePerS, config.activationFallPerS);
    cur.extensor = approachActivation(cur.extensor, tgt.extensor, dt, config.activationRisePerS, config.activationFallPerS);
  }
}

/** Per-group stance co-contraction floor matching computeDesiredActivations. */
function stanceActivationFloor(group: MuscleGroupName, tone: number): number {
  if (group === 'leftKnee' || group === 'rightKnee') return clamp01(tone + 0.2);
  if (group === 'leftAnkle' || group === 'rightAnkle') return clamp01(tone * 0.7);
  if (group === 'trunk') return clamp01(tone * 0.6);
  return clamp01(tone);
}

/**
 * Mean activation above required stance co-contraction.
 * Quiet upright (tone-only) → ~0 so fatigue recovers; brace/recover adds effort.
 */
export function effortAboveStanceTone(
  activations: Record<MuscleGroupName, MuscleGroupActivation>,
  stanceTone: number,
): number {
  let sum = 0;
  for (const name of MUSCLE_GROUP_NAMES) {
    const a = activations[name];
    const peak = Math.max(a.flexor, a.extensor);
    const floor = stanceActivationFloor(name, stanceTone);
    sum += Math.max(0, peak - floor);
  }
  return sum / MUSCLE_GROUP_NAMES.length;
}

function updateFatigue(
  state: MuscleControllerState,
  _meanActivation: number,
  dt: number,
  config: MuscleControllerConfig,
): void {
  const safeDt = Math.max(0, dt);
  if (safeDt === 0) return;
  const effort = effortAboveStanceTone(state.activations, config.stanceTone);
  if (effort > 0.05) {
    state.fatigue = clamp01(state.fatigue + config.fatigueGainPerS * effort * safeDt);
  } else {
    state.fatigue = clamp01(state.fatigue - config.fatigueRecoveryPerS * safeDt);
  }
}

function resolvePhase(
  state: MuscleControllerState,
  supportError: number,
  tiltDeg: number,
  pelvisHeightM: number,
  meanActivation: number,
  config: MuscleControllerConfig,
): MusclePhase {
  if (pelvisHeightM < config.fallenPelvisHeightM || tiltDeg > config.fallenTiltDeg) {
    return 'fallen';
  }
  if (state.stepPhase !== 'none') {
    return 'stepping';
  }
  if (state.fatigue >= config.fatiguePhaseLevel && meanActivation >= config.fatiguePhaseThreshold) {
    return 'fatigued';
  }
  if (tiltDeg >= config.recoverTiltDeg || supportError >= config.braceSupportErrorM * 2) {
    return 'recovering';
  }
  if (supportError >= config.braceSupportErrorM || meanActivation >= 0.2) {
    return 'bracing';
  }
  return 'idle';
}

function clearStepMotion(state: MuscleControllerState): void {
  const wasP1A = state.singleSupportHoldActive;
  state.stepPhase = 'none';
  state.stepPhaseElapsedS = 0;
  state.singleSupportHoldActive = false;
  // Hang latch + commanded forward telemetry survive step end so arms stay at
  // the pre-settled hang (re-driving to 0 would re-sat shoulders). Full clear
  // happens on resetMuscleController / emptyStepFields only.
  // P1A recovery must not retain Gate A explicit double-support plant override.
  if (wasP1A) {
    state.explicitStepActive = false;
    clearPlantedPose(state);
  }
}

function clearArmPreSettle(state: MuscleControllerState): void {
  state.armPreSettleActive = false;
  state.armPreSettleElapsedS = 0;
  state.armPreSettleReadyDwellS = 0;
  state.pendingArmPreSettleStep = null;
}

/**
 * Begin arm pre-settle for a commanded forward step. Ordinary balance stays
 * active (stepPhase remains `none`); prepare is deferred until the ready gate.
 */
function beginArmPreSettle(
  state: MuscleControllerState,
  request: ExplicitOneStepRequest,
): void {
  state.pendingOneStep = null;
  state.pendingArmPreSettleStep = {
    forwardDisplacementM: request.forwardDisplacementM,
    lateralDisplacementM: request.lateralDisplacementM ?? 0,
    swingSide: request.swingSide ?? null,
  };
  state.armPreSettleActive = true;
  state.armPreSettleElapsedS = 0;
  state.armPreSettleReadyDwellS = 0;
  // Soft-slew from current neutral (0) toward hang — residual stays small.
  state.armShoulderTargetRad = 0;
  state.armElbowTargetRad = 0;
  state.armHoldLatched = true;
  // Seed commanded geometry so arm hold resolves production hang targets.
  state.commandedStepForwardM = request.forwardDisplacementM;
  state.commandedStepLateralM = request.lateralDisplacementM ?? 0;
  state.explicitStepActive = true;
  // Episode lock: no second step / capture while pre-settling.
  state.stepArmed = false;
  state.recoveredDwellS = 0;
  // Drop integral windup from any pre-command residual so prepare starts clean.
  state.integralErrorXM = 0;
  state.integralErrorZM = 0;
}

/** Rate-limit arm targets toward hang goals (pure slew, residual-safe). */
function slewArmHoldTargets(state: MuscleControllerState, dt: number): void {
  const safeDt = Math.max(0, dt);
  if (safeDt <= 0) return;
  const goals = resolveEffectiveForwardArmHangTargets(state.p1aTrialStyleKnobs);
  const goalSh = goals.shoulderSagRad;
  const goalEl = goals.elbowFlexRad;
  const speed = state.armPreSettleActive
    ? P1A_FORWARD_ARM_PREPARE_MAX_SPEED_RAD_S
    : P1A_FORWARD_ARM_MAX_SPEED_RAD_S;
  const maxD = speed * safeDt;
  const dSh = clamp(goalSh - state.armShoulderTargetRad, -maxD, maxD);
  const dEl = clamp(goalEl - state.armElbowTargetRad, -maxD, maxD);
  state.armShoulderTargetRad += dSh;
  state.armElbowTargetRad += dEl;
}

function armHangTargetsReached(state: MuscleControllerState): boolean {
  const goals = resolveEffectiveForwardArmHangTargets(state.p1aTrialStyleKnobs);
  const tol = P1A_ARM_PRESETTLE_TARGET_TOL_RAD;
  return (
    Math.abs(state.armShoulderTargetRad - goals.shoulderSagRad) <= tol &&
    Math.abs(state.armElbowTargetRad - goals.elbowFlexRad) <= tol
  );
}

/**
 * Real contact stream when present; height fallback only when grounded flags
 * are absent (no native contact capability on the observation).
 */
function footGroundedFromObservation(
  observation: MuscleObservation,
  side: MuscleSwingSide,
  footY: number,
): boolean {
  const flag = side === 'left' ? observation.leftFootGrounded : observation.rightFootGrounded;
  if (flag !== undefined) return flag;
  return isFootNearGround(footY);
}

/**
 * Whether load/recovery may apply the explicit double-support plant override
 * (fixed-world hip→foot plant motors).
 *
 * - Gate A / explicit: load || none, when explicit + stance plant latched.
 * - P1A (`singleSupportHoldActive`): load only — special fixed-plant path via
 *   `applyP1AInPlaceLoadAcceptanceCommands`. Place contact-candidate still uses
 *   that helper via its own policy path; phase none does not re-enter here.
 * Pure helper for unit tests / policy matrix.
 */
export function shouldApplyExplicitDoubleSupportPlant(state: {
  stepPhase: MuscleStepPhase;
  explicitStepActive: boolean;
  stancePlantLatched: boolean;
  singleSupportHoldActive: boolean;
}): boolean {
  if (!state.stancePlantLatched) return false;
  // P1A: special fixed-plant path during load only (in-place load acceptance).
  if (state.singleSupportHoldActive) {
    return state.stepPhase === 'load';
  }
  if (!state.explicitStepActive) return false;
  return state.stepPhase === 'load' || state.stepPhase === 'none';
}

/**
 * Pure policy: during P1A place, when both stance and swing contact flags are
 * true, the joint path may use the existing symmetric in-place load-acceptance
 * motor commands (contact-candidate mode). This is not place→load authority —
 * load still requires finite positive swing point-count + normal impulse plus
 * replacement geometry. Never selects Gate A rear/front plant. When either
 * contact flag is false, the airborne place path remains selected.
 */
export function shouldApplyP1APlaceContactCandidateLoadMotor(args: {
  stepPhase: MuscleStepPhase;
  singleSupportHoldActive: boolean;
  stanceContact: boolean;
  swingContact: boolean;
}): boolean {
  return (
    args.singleSupportHoldActive &&
    args.stepPhase === 'place' &&
    args.stanceContact &&
    args.swingContact
  );
}

function clearPlantedPose(state: MuscleControllerState): void {
  state.plantedPoseActive = false;
  state.plantedSwingHipSag = 0;
  state.plantedSwingHipLat = 0;
  state.plantedSwingKnee = 0;
  state.plantedSwingAnkle = 0;
  state.plantedStanceHipSag = 0;
  state.plantedStanceHipLat = 0;
  state.plantedStanceKnee = 0;
  state.plantedStanceAnkle = 0;
  state.plantedSpineSag = 0;
  state.plantedSpineLat = 0;
  state.p1aSwingAnkleLat = 0;
  state.earlyLoadHipSagSlewActive = false;
  state.earlyLoadHipSagSlewRad = 0;
  state.momentumAbsorbTouchdownComVzMps = 0;
  state.momentumAbsorbTouchdownLatched = false;
  // Stance plant world target is step-episode metadata; clear with planted hold.
  state.stancePlantLatched = false;
  state.stancePlantWorldXM = 0;
  state.stancePlantWorldYM = 0;
  state.stancePlantWorldZM = 0;
}

/**
 * Live hip→fixed-world-foot joint targets (IK + horizontal plant feedback).
 * Joint motors only — keeps a foot planted at a fixed world target.
 */
function plantLegJointTargets(args: {
  hip: CaptureVec3;
  foot: CaptureVec3;
  footTarget: CaptureVec3;
  side: MuscleSwingSide;
  /** Horizontal plant gain (rad per meter). */
  plantGain?: number;
}): {
  hipSag: number;
  hipLat: number;
  knee: number;
  ankle: number;
} {
  const plantGain = args.plantGain ?? 2.4;
  const ik = solveTwoLinkLegIk({
    hip: args.hip,
    footTarget: args.footTarget,
    swingSide: args.side,
  });
  const errX = args.footTarget.x - args.foot.x;
  const errZ = args.footTarget.z - args.foot.z;
  // FK: sag− → +Z, lat+ → +X. Correct plant slip without vertical re-lift.
  const hipSag = clamp(ik.hipSagittalRad - plantGain * errZ, -1.0, 0.35);
  const hipLat = clamp(ik.hipLateralRad + plantGain * errX, -0.55, 0.55);
  // Prefer extended plant (less knee tuck than free swing IK).
  const knee = clamp(ik.kneeRad * 0.35, -0.35, 0.08);
  // Near-flat plant ankle. Avoid strong plantarflex (re-lift) and strong
  // dorsiflex (heel dig that pitches the body aft).
  const ankle = clamp(ik.ankleRad * 0.35, -0.08, 0.04);
  return { hipSag, hipLat, knee, ankle };
}

/** Physical recovered envelope (matches evaluation recovery gates, not idle). */
function isPhysicallyRecovered(
  desired: DesiredActivations,
  observation: MuscleObservation,
): boolean {
  return (
    observation.pelvisPosition[1] >= 0.65 &&
    desired.tiltDeg <= 15 &&
    desired.supportError <= 0.08
  );
}

function observationFeet(observation: MuscleObservation): {
  leftFoot: CaptureVec3;
  rightFoot: CaptureVec3;
  leftHip: CaptureVec3;
  rightHip: CaptureVec3;
} {
  const sc = observation.supportCenter;
  const pelvis = observation.pelvisPosition;
  const leftFoot: CaptureVec3 = observation.leftFootPosition
    ? {
        x: observation.leftFootPosition[0],
        y: observation.leftFootPosition[1],
        z: observation.leftFootPosition[2],
      }
    : { x: sc[0] - 0.1, y: 0.03, z: sc[2] };
  const rightFoot: CaptureVec3 = observation.rightFootPosition
    ? {
        x: observation.rightFootPosition[0],
        y: observation.rightFootPosition[1],
        z: observation.rightFootPosition[2],
      }
    : { x: sc[0] + 0.1, y: 0.03, z: sc[2] };
  const leftHip: CaptureVec3 = observation.leftHipPosition
    ? {
        x: observation.leftHipPosition[0],
        y: observation.leftHipPosition[1],
        z: observation.leftHipPosition[2],
      }
    : { x: pelvis[0] - 0.1, y: pelvis[1] - 0.05, z: pelvis[2] };
  const rightHip: CaptureVec3 = observation.rightHipPosition
    ? {
        x: observation.rightHipPosition[0],
        y: observation.rightHipPosition[1],
        z: observation.rightHipPosition[2],
      }
    : { x: pelvis[0] + 0.1, y: pelvis[1] - 0.05, z: pelvis[2] };
  return { leftFoot, rightFoot, leftHip, rightHip };
}

/**
 * Freeze planted-support joint targets from geometry IK at the world target.
 * Explicit Gate A: seed from live hip→fixed-world-foot IK for both plants
 * (no open-loop plantarflex / sagittal offsets that unplant the new foot).
 * Capture recovery keeps the prior frozen-pose path unchanged.
 */
function freezePlantedPoseFromStep(
  state: MuscleControllerState,
  observation: MuscleObservation,
): void {
  if (!state.swingSide) return;
  const { leftHip, rightHip, leftFoot, rightFoot } = observationFeet(observation);
  const swing = state.swingSide;
  const stance: MuscleSwingSide = swing === 'left' ? 'right' : 'left';

  if (state.explicitStepActive && state.stancePlantLatched) {
    const swingHip = swing === 'left' ? leftHip : rightHip;
    const stanceHip = stance === 'left' ? leftHip : rightHip;
    const swingFoot = swing === 'left' ? leftFoot : rightFoot;
    const stanceFoot = stance === 'left' ? leftFoot : rightFoot;
    const swingTarget: CaptureVec3 = {
      x: state.stepTargetWorldXM,
      y: state.stepTargetWorldYM,
      z: state.stepTargetWorldZM,
    };
    const stanceTarget: CaptureVec3 = {
      x: state.stancePlantWorldXM,
      y: state.stancePlantWorldYM,
      z: state.stancePlantWorldZM,
    };
    const swingPlant = plantLegJointTargets({
      hip: swingHip,
      foot: swingFoot,
      footTarget: swingTarget,
      side: swing,
    });
    const stancePlant = plantLegJointTargets({
      hip: stanceHip,
      foot: stanceFoot,
      footTarget: stanceTarget,
      side: stance,
    });
    state.plantedPoseActive = true;
    state.plantedSwingHipSag = swingPlant.hipSag;
    state.plantedSwingHipLat = swingPlant.hipLat;
    state.plantedSwingKnee = swingPlant.knee;
    state.plantedSwingAnkle = swingPlant.ankle;
    state.plantedStanceHipSag = stancePlant.hipSag;
    state.plantedStanceHipLat = stancePlant.hipLat;
    state.plantedStanceKnee = stancePlant.knee;
    state.plantedStanceAnkle = stancePlant.ankle;
    const midZ = (stanceTarget.z + swingTarget.z) * 0.5;
    const midX = (stanceTarget.x + swingTarget.x) * 0.5;
    const comBehind = observation.centerOfMass[2] - midZ;
    const comLat = observation.centerOfMass[0] - midX;
    state.plantedSpineSag = clamp(-0.06 - comBehind * 0.35, -0.18, 0.08);
    state.plantedSpineLat = clamp(-comLat * 0.9, -0.22, 0.22);
    return;
  }

  // Capture recovery: prior frozen asymmetric plant (unchanged).
  const hip = swing === 'left' ? leftHip : rightHip;
  const ik = solveTwoLinkLegIk({
    hip,
    footTarget: {
      x: state.stepTargetWorldXM,
      y: state.stepTargetWorldYM,
      z: state.stepTargetWorldZM,
    },
    swingSide: swing,
  });
  const hipLat = ik.hipLateralRad;
  const tx = state.stepTargetXM;
  const tz = state.stepTargetZM;

  state.plantedPoseActive = true;
  state.plantedSwingHipSag = clamp(ik.hipSagittalRad * 0.55, -0.55, 0.1);
  state.plantedSwingHipLat = hipLat;
  state.plantedSwingKnee = clamp(ik.kneeRad * 0.25, -0.2, 0.05);
  state.plantedSwingAnkle = clamp(ik.ankleRad - 0.04, -0.15, 0.12);
  state.plantedStanceHipSag = 0.08;
  state.plantedStanceHipLat = clamp(-hipLat * 0.45, -0.28, 0.28);
  state.plantedStanceKnee = 0.1;
  state.plantedStanceAnkle = clamp((swing === 'right' ? 1 : -1) * tx * 0.8 + tz * 0.5, -0.2, 0.28);
  state.plantedSpineSag = clamp(-tz * 0.35, -0.2, 0.2);
  state.plantedSpineLat = clamp(-hipLat * 0.65, -0.28, 0.28);
}

function shouldTriggerCaptureStep(
  state: MuscleControllerState,
  desired: DesiredActivations,
  observation: MuscleObservation,
  config: MuscleControllerConfig,
): boolean {
  if (!config.stepEnabled) return false;
  // Hard episode lock: no second step until recovered dwell re-arms.
  if (!state.stepArmed) return false;
  if (observation.pelvisPosition[1] < config.fallenPelvisHeightM + 0.08) return false;
  if (desired.tiltDeg > config.fallenTiltDeg - 5) return false;
  const speed = Math.hypot(observation.pelvisVelocity[0], observation.pelvisVelocity[2]);
  if (desired.supportError >= config.stepSupportErrorM) return true;
  if (
    desired.supportError >= config.stepSupportErrorWithSpeedM &&
    speed >= config.stepSpeedMps
  ) {
    return true;
  }
  if (
    desired.tiltDeg >= config.stepTiltDeg &&
    desired.supportError >= config.stepSupportErrorWithTiltM
  ) {
    return true;
  }
  return false;
}

function beginStepFromPlan(
  state: MuscleControllerState,
  plan: {
    swingSide: MuscleSwingSide;
    targetRelXM: number;
    targetRelZM: number;
    targetWorldX: number;
    targetWorldY: number;
    targetWorldZ: number;
    supportSpanBeforeM: number;
    supportSpanAfterM: number;
  },
  opts?: {
    explicit?: boolean;
    singleSupportHold?: boolean;
    commandedForwardM?: number;
    commandedLateralM?: number;
    targetClamped?: boolean;
  },
): void {
  // Checkpoint 15: enter prepare (load transfer) first — never lift immediately.
  state.stepPhase = 'prepare';
  state.stepPhaseElapsedS = 0;
  state.swingSide = plan.swingSide;
  state.stepTargetXM = plan.targetRelXM;
  state.stepTargetZM = plan.targetRelZM;
  state.stepTargetWorldXM = plan.targetWorldX;
  state.stepTargetWorldYM = plan.targetWorldY;
  state.stepTargetWorldZM = plan.targetWorldZ;
  state.stepLandingErrorM = 0;
  state.supportSpanBeforeM = plan.supportSpanBeforeM;
  state.supportSpanAfterM = plan.supportSpanAfterM;
  state.prepareDurationS = 0;
  state.comToStanceM = 0;
  state.minComToStanceM = Number.POSITIVE_INFINITY;
  state.comLateralToStanceM = 0;
  state.comSagittalToStanceM = 0;
  state.minComLateralToStanceM = Number.POSITIVE_INFINITY;
  state.minComSagittalToStanceM = Number.POSITIVE_INFINITY;
  state.swingLiftM = 0;
  state.prepareAborted = false;
  state.prepareStancePlantErrorM = 0;
  state.minPrepareStancePlantErrorM = Number.POSITIVE_INFINITY;
  state.maxPrepareStancePlantErrorM = 0;
  state.prepareReadyDwellS = 0;
  state.maxPrepareReadyDwellS = 0;
  state.prevLandingErrorM = Number.POSITIVE_INFINITY;
  state.stepCount += 1;
  // Disarm until sustained recovery — at most one step per episode/command.
  state.stepArmed = false;
  state.recoveredDwellS = 0;
  state.explicitStepActive = opts?.explicit === true || opts?.singleSupportHold === true;
  state.singleSupportHoldActive = opts?.singleSupportHold === true;
  state.commandedStepForwardM = opts?.commandedForwardM ?? 0;
  state.commandedStepLateralM = opts?.commandedLateralM ?? 0;
  state.stepTargetClamped = opts?.targetClamped === true;
  // New step supersedes any prior planted pose until this step plants.
  clearPlantedPose(state);
}

function planCaptureStep(
  state: MuscleControllerState,
  _desired: DesiredActivations,
  observation: MuscleObservation,
  config: MuscleControllerConfig,
): void {
  // Checkpoint 13: geometry-aware LIP capture + swing selection from real feet.
  const { leftFoot, rightFoot } = observationFeet(observation);
  // Prefer mass-weighted COM velocity when available; pelvis as fallback.
  const velX = observation.centerOfMassVelocity?.[0] ?? observation.pelvisVelocity[0];
  const velZ = observation.centerOfMassVelocity?.[2] ?? observation.pelvisVelocity[2];
  const plan = planGeometryCaptureStep({
    com: {
      x: observation.centerOfMass[0],
      y: observation.centerOfMass[1],
      z: observation.centerOfMass[2],
    },
    comVelocity: { x: velX, y: 0, z: velZ },
    supportCenter: {
      x: observation.supportCenter[0],
      y: observation.supportCenter[1],
      z: observation.supportCenter[2],
    },
    leftFoot,
    rightFoot,
    maxLengthM: config.stepMaxLengthM,
    maxWidthM: config.stepMaxWidthM,
    lipGain: config.stepLipGain,
    expandMarginM: config.stepHullExpandMarginM,
  });

  beginStepFromPlan(state, plan, { explicit: false });
}

/**
 * Explicit commanded one-step (Gate A / P1B): routes through the certified
 * P1A single-support pipeline with nonzero forward displacement so prepare
 * load-share, contact-driven hold, lateral capture replant, and measured-load
 * acceptance all apply. Independent of support-error capture heuristics and
 * does not require `stepEnabled`.
 */
function planExplicitOneStep(
  state: MuscleControllerState,
  observation: MuscleObservation,
  config: MuscleControllerConfig,
  request: ExplicitOneStepRequest,
): void {
  planSingleSupportHold(state, observation, config, {
    swingSide: request.swingSide,
    forwardDisplacementM: request.forwardDisplacementM,
    lateralDisplacementM: request.lateralDisplacementM,
  });
}

/**
 * Queue a single-use explicit one-step command. Does not require `stepEnabled`.
 * No-op if already stepping or a pending request already exists (single-use).
 * Consumed on the next armed control tick with phase `none`.
 */
export function queueMuscleOneStep(
  state: MuscleControllerState,
  request: {
    forwardDisplacementM?: number;
    lateralDisplacementM?: number;
    swingSide?: MuscleSwingSide | null;
  } = {},
): boolean {
  if (state.stepPhase !== 'none') return false;
  if (state.armPreSettleActive) return false;
  if (state.pendingOneStep) return false;
  if (state.pendingSingleSupportHold) return false;
  if (state.pendingArmPreSettleStep) return false;
  const forward =
    request.forwardDisplacementM !== undefined
      ? request.forwardDisplacementM
      : GATE_A_FORWARD_STEP_M;
  if (!Number.isFinite(forward) || forward <= 0) return false;
  state.pendingOneStep = {
    forwardDisplacementM: forward,
    lateralDisplacementM: Number.isFinite(request.lateralDisplacementM)
      ? (request.lateralDisplacementM as number)
      : 0,
    swingSide: request.swingSide ?? null,
  };
  return true;
}

/**
 * Queue P1A single-support hold-and-replace (in place). Does not require
 * `stepEnabled`. Mutually exclusive with pending Gate A one-step.
 */
export function queueMuscleSingleSupportHold(
  state: MuscleControllerState,
  request: {
    swingSide?: MuscleSwingSide | null;
  } = {},
): boolean {
  if (state.stepPhase !== 'none') return false;
  if (state.armPreSettleActive) return false;
  if (state.pendingOneStep) return false;
  if (state.pendingSingleSupportHold) return false;
  if (state.pendingArmPreSettleStep) return false;
  state.pendingSingleSupportHold = {
    swingSide: request.swingSide ?? null,
  };
  return true;
}

/**
 * Plan the certified P1A single-support pipeline, optionally with commanded
 * forward displacement (Gate A / P1B). Zero forward is byte-identical to the
 * certified in-place hold/replace: sagittal place target stays at the original
 * swing sole; nonzero forward sets sagittal place = origin + forward (clamped
 * to reach). Lateral place origin stays at the original swing plant X and is
 * retargeted during place via capture-point math. Lift clearance applied at
 * joint override time.
 */
function planSingleSupportHold(
  state: MuscleControllerState,
  observation: MuscleObservation,
  config: MuscleControllerConfig,
  request: ExplicitSingleSupportHoldRequest,
): void {
  const { leftFoot, rightFoot } = observationFeet(observation);
  const swingSide = request.swingSide ?? 'right';
  // Exact 0 (or omitted) preserves certified P1A; avoid NaN/undefined bleed.
  const forwardDisplacementM =
    typeof request.forwardDisplacementM === 'number' &&
    Number.isFinite(request.forwardDisplacementM)
      ? request.forwardDisplacementM
      : 0;
  const lateralDisplacementM =
    typeof request.lateralDisplacementM === 'number' &&
    Number.isFinite(request.lateralDisplacementM)
      ? request.lateralDisplacementM
      : 0;
  const plan = planExplicitForwardStep({
    leftFoot,
    rightFoot,
    supportCenter: {
      x: observation.supportCenter[0],
      y: observation.supportCenter[1],
      z: observation.supportCenter[2],
    },
    forwardDisplacementM,
    lateralDisplacementM,
    swingSide,
    maxLengthM: config.stepMaxLengthM,
    maxWidthM: config.stepMaxWidthM,
  });

  beginStepFromPlan(state, plan, {
    explicit: true,
    singleSupportHold: true,
    commandedForwardM: forwardDisplacementM,
    commandedLateralM: lateralDisplacementM,
    targetClamped: plan.targetClamped,
  });

  const stanceFoot = swingSide === 'left' ? rightFoot : leftFoot;
  const swingFoot = swingSide === 'left' ? leftFoot : rightFoot;
  // The planning tick returns before the regular prepare-frame measurement
  // update. Seed the P1A minima from the real planned stance geometry so the
  // evaluator cannot mistake an unmeasured default zero for perfect transfer.
  const initialComToStanceM = comToStanceDistanceM(observation.centerOfMass, stanceFoot);
  const initialComOffsets = comToStanceOffsetsM(observation.centerOfMass, stanceFoot);
  state.comToStanceM = initialComToStanceM;
  state.minComToStanceM = initialComToStanceM;
  state.comLateralToStanceM = initialComOffsets.lateralM;
  state.comSagittalToStanceM = initialComOffsets.sagittalM;
  state.minComLateralToStanceM = initialComOffsets.lateralM;
  state.minComSagittalToStanceM = initialComOffsets.sagittalM;
  state.stancePlantWorldXM = stanceFoot.x;
  state.stancePlantWorldYM = Math.min(stanceFoot.y, 0.04);
  state.stancePlantWorldZM = stanceFoot.z;
  state.stancePlantLatched = true;
  // Plant error is zero at the latch instant (live foot == plant). Seed so the
  // first output is measured, not an unmeasured zero default after empty init.
  const initialPlantErr = stancePlantHorizontalErrorM(
    stanceFoot,
    state.stancePlantWorldXM,
    state.stancePlantWorldZM,
  );
  state.prepareStancePlantErrorM = initialPlantErr;
  state.minPrepareStancePlantErrorM = initialPlantErr;
  state.maxPrepareStancePlantErrorM = initialPlantErr;
  state.prepareReadyDwellS = 0;
  state.maxPrepareReadyDwellS = 0;
  // Lateral place origin = original swing plant X (capture retargets X in place).
  // Sagittal place = origin Z when forward is 0 (certified P1A); otherwise the
  // reach-clamped planned target (origin + commanded forward).
  // Push 20: optional plant shift so quiet TD lands where the skid used to end.
  const plantShiftZ = resolveP1APlacePlantShiftSagittalM(state.p1aTrialStyleKnobs);
  const plantShiftX = resolveP1APlacePlantShiftLateralM(state.p1aTrialStyleKnobs);
  const basePlantZ =
    forwardDisplacementM === 0 ? swingFoot.z : plan.targetWorldZ;
  state.stepTargetWorldXM = p1aPlacePlantShiftedTargetM({
    commandedForwardM: forwardDisplacementM,
    plantWorldM: swingFoot.x,
    shiftM: plantShiftX,
  });
  state.stepTargetWorldYM = Math.min(swingFoot.y, 0.04);
  state.stepTargetWorldZM = p1aPlacePlantShiftedTargetM({
    commandedForwardM: forwardDisplacementM,
    plantWorldM: basePlantZ,
    shiftM: plantShiftZ,
  });
  state.p1aPlaceOriginWorldXM = swingFoot.x;
  state.p1aPlaceCaptureTargetFrozen = false;
}

/** Horizontal COM distance to the stance foot (m). Gate A / capture radial metric. */
export function comToStanceDistanceM(
  com: readonly [number, number, number],
  stanceFoot: CaptureVec3,
): number {
  return Math.hypot(com[0] - stanceFoot.x, com[2] - stanceFoot.z);
}

/**
 * Axis-aligned COM-to-stance foot-center errors (m).
 * Lateral = |Δx|, sagittal = |Δz| — used by the P1A rectangular prepare gate.
 */
export function comToStanceOffsetsM(
  com: readonly [number, number, number],
  stanceFoot: CaptureVec3,
): { lateralM: number; sagittalM: number } {
  return {
    lateralM: Math.abs(com[0] - stanceFoot.x),
    sagittalM: Math.abs(com[2] - stanceFoot.z),
  };
}

/**
 * Horizontal distance (m) from live stance foot center to latched world plant.
 * Pure helper for P1A prepare plant-slip gate and tests.
 */
export function stancePlantHorizontalErrorM(
  liveStanceFoot: Pick<CaptureVec3, 'x' | 'z'>,
  plantWorldXM: number,
  plantWorldZM: number,
): number {
  return Math.hypot(liveStanceFoot.x - plantWorldXM, liveStanceFoot.z - plantWorldZM);
}

/**
 * Prepare→lift gate (Gate A / capture-step): COM projection within a horizontal
 * radius of the stance foot, tilt/speed bounded, minimum prepare time elapsed.
 * Pure helper for unit tests.
 *
 * P1A does **not** use this radial gate — see {@link canLeavePrepareForLiftP1A}.
 * Pass `comRadiusM` to override `config.stepPrepareComRadiusM` for tests only.
 */
export function canLeavePrepareForLift(args: {
  prepareElapsedS: number;
  comToStanceM: number;
  tiltDeg: number;
  speedMps: number;
  bothFeetGrounded: boolean;
  config: Pick<
    MuscleControllerConfig,
    | 'stepPrepareMinS'
    | 'stepPrepareComRadiusM'
    | 'stepPrepareMaxTiltDeg'
    | 'stepPrepareMaxSpeedMps'
  >;
  /**
   * Optional COM-to-stance radius override (m). When set, replaces
   * `config.stepPrepareComRadiusM` for this gate evaluation only.
   */
  comRadiusM?: number;
}): boolean {
  const comRadiusM = args.comRadiusM ?? args.config.stepPrepareComRadiusM;
  if (args.prepareElapsedS < args.config.stepPrepareMinS) return false;
  if (!args.bothFeetGrounded) return false;
  if (args.comToStanceM > comRadiusM) return false;
  if (args.tiltDeg > args.config.stepPrepareMaxTiltDeg) return false;
  if (args.speedMps > args.config.stepPrepareMaxSpeedMps) return false;
  return true;
}

/**
 * P1A prepare ready condition (single frame): COM projection inside a rectangular
 * stance footprint (axis-aligned half-extents), stance plant horizontal error
 * ≤ 0.02 m, plus shared contact/tilt/speed/min-time safety gates. Pure helper
 * for unit tests.
 *
 * Does **not** encode the continuous ready dwell — the controller accumulates
 * {@link P1A_PREPARE_READY_DWELL_S} only while this returns true.
 *
 * Defaults: |Δx| ≤ {@link P1A_PREPARE_COM_LATERAL_MAX_M} (0.04 m),
 * |Δz| ≤ {@link P1A_PREPARE_COM_SAGITTAL_MAX_M} (0.12 m),
 * plant error ≤ {@link P1A_PREPARE_STANCE_PLANT_ERROR_MAX_M} (0.02 m).
 * Speed uses `config.stepPrepareMaxSpeedMps` (default 0.65), same as Gate A
 * {@link canLeavePrepareForLift}.
 */
export function canLeavePrepareForLiftP1A(args: {
  prepareElapsedS: number;
  comLateralM: number;
  comSagittalM: number;
  /** Horizontal live-stance-foot to latched-plant error (m). */
  stancePlantErrorM: number;
  tiltDeg: number;
  speedMps: number;
  bothFeetGrounded: boolean;
  config: Pick<
    MuscleControllerConfig,
    'stepPrepareMinS' | 'stepPrepareMaxTiltDeg' | 'stepPrepareMaxSpeedMps'
  >;
  /** Override lateral half-extent (m); default P1A_PREPARE_COM_LATERAL_MAX_M. */
  lateralMaxM?: number;
  /** Override sagittal half-extent (m); default P1A_PREPARE_COM_SAGITTAL_MAX_M. */
  sagittalMaxM?: number;
  /** Override plant error max (m); default P1A_PREPARE_STANCE_PLANT_ERROR_MAX_M. */
  stancePlantErrorMaxM?: number;
}): boolean {
  const lateralMaxM = args.lateralMaxM ?? P1A_PREPARE_COM_LATERAL_MAX_M;
  const sagittalMaxM = args.sagittalMaxM ?? P1A_PREPARE_COM_SAGITTAL_MAX_M;
  const plantMaxM = args.stancePlantErrorMaxM ?? P1A_PREPARE_STANCE_PLANT_ERROR_MAX_M;
  if (args.prepareElapsedS < args.config.stepPrepareMinS) return false;
  if (!args.bothFeetGrounded) return false;
  if (args.comLateralM > lateralMaxM) return false;
  if (args.comSagittalM > sagittalMaxM) return false;
  if (args.stancePlantErrorM > plantMaxM) return false;
  if (args.tiltDeg > args.config.stepPrepareMaxTiltDeg) return false;
  if (args.speedMps > args.config.stepPrepareMaxSpeedMps) return false;
  return true;
}

/**
 * Plant gate: near-ground, horizontal err decreasing and under threshold,
 * support span expanded vs pre-step (checkpoint 18: positive expansion required).
 * Pure helper for unit tests.
 */
export function canMarkPlanted(args: {
  grounded: boolean;
  landingErrorM: number;
  prevLandingErrorM: number;
  supportSpanBeforeM: number;
  supportSpanAfterM: number;
  /** Fixed land gate — default 0.12 m; do not loosen. */
  maxLandingErrorM?: number;
  /**
   * Minimum inter-foot span growth (m) required before plant is accepted.
   * Checkpoint 18: default 0.01 — targets inside the hull cannot plant.
   */
  minSpanExpandM?: number;
}): boolean {
  const maxErr = args.maxLandingErrorM ?? 0.12;
  const minExpand = args.minSpanExpandM ?? 0.01;
  if (!args.grounded) return false;
  if (args.landingErrorM > maxErr) return false;
  // Require non-increasing error (allow small noise).
  if (args.landingErrorM > args.prevLandingErrorM + 0.01) return false;
  // Positive support expansion only — never accept a shrink/inside-hull plant.
  if (args.supportSpanAfterM + 1e-6 < args.supportSpanBeforeM + minExpand) return false;
  return true;
}

/**
 * True if a place-phase error series is mostly decreasing (checkpoint 17).
 * At least `minFraction` of consecutive deltas must be negative beyond noise.
 */
export function isMostlyMonotonicDecreasing(
  series: readonly number[],
  opts?: { noise?: number; minFraction?: number },
): boolean {
  if (series.length < 3) return false;
  const noise = opts?.noise ?? 0.002;
  const minFraction = opts?.minFraction ?? 0.35;
  let dec = 0;
  let pairs = 0;
  for (let i = 1; i < series.length; i += 1) {
    pairs += 1;
    if (series[i]! < series[i - 1]! - noise) dec += 1;
  }
  return dec >= pairs * minFraction;
}

/**
 * Advance the capture-step state machine from physical balance observations.
 * Joint-motor foot placement only — never applies world impulses/forces.
 *
 * Lifecycle (cp8b + cp15):
 *  - prepare: load transfer to stance, both feet planted;
 *  - lift only when COM is over stance foot (or prepare timeout → abort, no lift);
 *  - place / load with strong stance brace; plant requires near-ground +
 *    decreasing landing error + support-span expansion;
 *  - at most one step per episode; re-arm only on controller reset.
 */
function updateCaptureStep(
  state: MuscleControllerState,
  desired: DesiredActivations,
  observation: MuscleObservation,
  config: MuscleControllerConfig,
  dt: number,
): void {
  const safeDt = Math.max(0, dt);

  // Abort motion + planted hold if the body has already fallen.
  if (
    observation.pelvisPosition[1] < config.fallenPelvisHeightM ||
    desired.tiltDeg > config.fallenTiltDeg
  ) {
    if (state.stepPhase !== 'none') clearStepMotion(state);
    if (state.armPreSettleActive) {
      clearArmPreSettle(state);
      state.armHoldLatched = false;
      state.explicitStepActive = false;
      state.commandedStepForwardM = 0;
      state.commandedStepLateralM = 0;
    }
    clearPlantedPose(state);
    state.recoveredDwellS = 0;
    return;
  }

  if (!state.stepArmed && state.stepPhase === 'none' && !state.armPreSettleActive) {
    const speed = Math.hypot(observation.pelvisVelocity[0], observation.pelvisVelocity[2]);
    const stable =
      isPhysicallyRecovered(desired, observation) &&
      desired.supportError <= 0.05 &&
      speed <= 0.08;
    if (stable) {
      state.recoveredDwellS += safeDt;
      if (state.recoveredDwellS >= config.stepEpisodeResetS && state.plantedPoseActive) {
        clearPlantedPose(state);
      }
    } else {
      state.recoveredDwellS = 0;
    }
  }

  if (state.stepPhase === 'none') {
    // Gate A push 8: arm pre-settle under ordinary balance before prepare.
    if (state.armPreSettleActive && state.pendingArmPreSettleStep) {
      state.armPreSettleElapsedS += safeDt;
      slewArmHoldTargets(state, safeDt);
      const comSpeed = Math.hypot(
        observation.centerOfMassVelocity?.[0] ?? observation.pelvisVelocity[0],
        observation.centerOfMassVelocity?.[2] ?? observation.pelvisVelocity[2],
      );
      const hangDone =
        state.armPreSettleElapsedS >= P1A_FORWARD_ARM_TARGET_RAMP_S &&
        armHangTargetsReached(state);
      if (
        hangDone &&
        comSpeed <= P1A_ARM_PRESETTLE_COM_SPEED_MPS &&
        desired.tiltDeg <= P1A_ARM_PRESETTLE_TILT_DEG &&
        desired.supportError <= 0.06
      ) {
        state.armPreSettleReadyDwellS += safeDt;
      } else if (!hangDone) {
        state.armPreSettleReadyDwellS = 0;
      } else {
        // Quiet broken after hang — decay dwell so prepare needs re-quiet.
        state.armPreSettleReadyDwellS = Math.max(
          0,
          state.armPreSettleReadyDwellS - safeDt,
        );
      }
      if (
        p1aArmPreSettleReady({
          elapsedS: state.armPreSettleElapsedS,
          rampS: P1A_FORWARD_ARM_TARGET_RAMP_S,
          comSpeedMps: comSpeed,
          tiltDeg: desired.tiltDeg,
          readyDwellS: state.armPreSettleReadyDwellS,
          timeoutS: P1A_ARM_PRESETTLE_TIMEOUT_S,
        }) &&
        (hangDone ||
          state.armPreSettleElapsedS >= P1A_ARM_PRESETTLE_TIMEOUT_S)
      ) {
        const req = state.pendingArmPreSettleStep;
        // Snap to full hang; hold through the step (residual≈0).
        const goals = resolveEffectiveForwardArmHangTargets(state.p1aTrialStyleKnobs);
        state.armShoulderTargetRad = goals.shoulderSagRad;
        state.armElbowTargetRad = goals.elbowFlexRad;
        clearArmPreSettle(state);
        state.armHoldLatched = true;
        state.integralErrorXM = 0;
        state.integralErrorZM = 0;
        planExplicitOneStep(state, observation, config, req);
        state.stepPhaseElapsedS = safeDt;
        state.prepareDurationS = safeDt;
      }
      return;
    }

    // Explicit one-step (Gate A): independent of stepEnabled / support-error trigger.
    if (state.pendingOneStep) {
      if (state.stepArmed) {
        const req = state.pendingOneStep;
        state.pendingOneStep = null;
        // Forward + hang active: pre-settle arms first (push 8 sequence).
        // Hang suppressed / production arms-up (push 12): direct prepare like
        // push 7 — no pre-settle, no armHold latch. Zero-forward never queued
        // here (queueMuscleOneStep rejects forward <= 0).
        const hangGoals = resolveEffectiveForwardArmHangTargets(
          state.p1aTrialStyleKnobs,
        );
        if (
          typeof req.forwardDisplacementM === 'number' &&
          Number.isFinite(req.forwardDisplacementM) &&
          req.forwardDisplacementM > 1e-4 &&
          hangGoals.hangActive
        ) {
          beginArmPreSettle(state, req);
        } else {
          // Arms-up / hang-suppressed: clear any stale hang latch so prepare
          // does not inherit hang COM bias or the +1.5 s hang prepare budget.
          state.armHoldLatched = false;
          state.armShoulderTargetRad = 0;
          state.armElbowTargetRad = 0;
          planExplicitOneStep(state, observation, config, req);
          state.stepPhaseElapsedS = safeDt;
          state.prepareDurationS = safeDt;
        }
      }
      // Keep pending until armed; do not also fire capture heuristic.
      return;
    }
    // P1A single-support hold/replace: independent diagnostic path.
    // Zero-forward identity — no arm pre-settle.
    if (state.pendingSingleSupportHold) {
      if (state.stepArmed) {
        const req = state.pendingSingleSupportHold;
        state.pendingSingleSupportHold = null;
        planSingleSupportHold(state, observation, config, req);
        state.stepPhaseElapsedS = safeDt;
        state.prepareDurationS = safeDt;
      }
      return;
    }
    if (shouldTriggerCaptureStep(state, desired, observation, config)) {
      planCaptureStep(state, desired, observation, config);
      state.stepPhaseElapsedS = safeDt;
      state.prepareDurationS = safeDt;
    }
    return;
  }

  state.stepPhaseElapsedS += safeDt;
  const { leftFoot, rightFoot } = observationFeet(observation);
  const swing = state.swingSide;
  if (!swing) {
    clearStepMotion(state);
    return;
  }
  const stance: MuscleSwingSide = swing === 'left' ? 'right' : 'left';
  const stanceFootLive = stance === 'left' ? leftFoot : rightFoot;
  const swingFoot = swing === 'left' ? leftFoot : rightFoot;
  // Prepare→lift gate uses live stance foot (load under the actual support).
  // Latched plant world target is used for motor plant retention only.
  const comToStance = comToStanceDistanceM(observation.centerOfMass, stanceFootLive);
  const comOffsets = comToStanceOffsetsM(observation.centerOfMass, stanceFootLive);
  state.comToStanceM = comToStance;
  state.comLateralToStanceM = comOffsets.lateralM;
  state.comSagittalToStanceM = comOffsets.sagittalM;
  if (comToStance < state.minComToStanceM) state.minComToStanceM = comToStance;
  if (comOffsets.lateralM < state.minComLateralToStanceM) {
    state.minComLateralToStanceM = comOffsets.lateralM;
  }
  if (comOffsets.sagittalM < state.minComSagittalToStanceM) {
    state.minComSagittalToStanceM = comOffsets.sagittalM;
  }

  // --- prepare: load transfer, both feet planted ---
  if (state.stepPhase === 'prepare') {
    state.prepareDurationS = state.stepPhaseElapsedS;
    // Live stance foot vs latched world plant (P1A plant-slip gate).
    const plantErr = state.stancePlantLatched
      ? stancePlantHorizontalErrorM(
          stanceFootLive,
          state.stancePlantWorldXM,
          state.stancePlantWorldZM,
        )
      : 0;
    state.prepareStancePlantErrorM = plantErr;
    if (plantErr < state.minPrepareStancePlantErrorM) {
      state.minPrepareStancePlantErrorM = plantErr;
    }
    if (plantErr > state.maxPrepareStancePlantErrorM) {
      state.maxPrepareStancePlantErrorM = plantErr;
    }
    const speed = Math.hypot(
      observation.centerOfMassVelocity?.[0] ?? observation.pelvisVelocity[0],
      observation.centerOfMassVelocity?.[2] ?? observation.pelvisVelocity[2],
    );
    const leftG = footGroundedFromObservation(observation, 'left', leftFoot.y);
    const rightG = footGroundedFromObservation(observation, 'right', rightFoot.y);
    // P1A: any real contact loss during prepare aborts honestly — do not allow
    // loss/recontact to masquerade as planted transfer (Gate A unchanged).
    if (state.singleSupportHoldActive && (!leftG || !rightG)) {
      state.prepareAborted = true;
      state.prepareDurationS = state.stepPhaseElapsedS;
      clearStepMotion(state);
      return;
    }
    if (state.singleSupportHoldActive) {
      // P1A: geometry/contact safety and measured load authority must hold
      // continuously. A touching flag alone cannot authorize swing-foot lift.
      const load = p1aFootLoadFractions(observation, stance);
      const knobs = state.p1aTrialStyleKnobs;
      const ready =
        canLeavePrepareForLiftP1A({
          prepareElapsedS: state.stepPhaseElapsedS,
          comLateralM: comOffsets.lateralM,
          comSagittalM: comOffsets.sagittalM,
          stancePlantErrorM: plantErr,
          tiltDeg: desired.tiltDeg,
          speedMps: speed,
          bothFeetGrounded: leftG && rightG,
          config,
        }) &&
        p1aHasPrepareLoadAuthority(load, {
          stanceLoadFractionMin: resolveP1APrepareStanceLoadFractionMin(knobs),
          swingLoadFractionMax: resolveP1APrepareSwingLoadFractionMax(knobs),
        });
      if (ready) {
        state.prepareReadyDwellS += safeDt;
        if (state.prepareReadyDwellS > state.maxPrepareReadyDwellS) {
          state.maxPrepareReadyDwellS = state.prepareReadyDwellS;
        }
        const readyDwellS = resolveP1APrepareReadyDwellS(knobs);
        if (state.prepareReadyDwellS + 1e-9 >= readyDwellS) {
          state.stepPhase = 'lift';
          state.stepPhaseElapsedS = safeDt;
          return;
        }
      } else {
        state.prepareReadyDwellS = 0;
      }
    } else {
      // Gate A / capture: radial 0.11 m, no plant-slip or ready-dwell gate.
      const gate = canLeavePrepareForLift({
        prepareElapsedS: state.stepPhaseElapsedS,
        comToStanceM: comToStance,
        tiltDeg: desired.tiltDeg,
        speedMps: speed,
        bothFeetGrounded: leftG && rightG,
        config,
      });
      if (gate) {
        state.stepPhase = 'lift';
        state.stepPhaseElapsedS = safeDt;
        return;
      }
    }
    // Explicit / P1A: longer prepare window (phase timer only — COM geometry is
    // the P1A-specific gate change; tilt/speed/min-time stay shared).
    // Forward hang-latched: allow extra prepare time for load-share after arm
    // pre-settle (COM may need a longer transfer without being a hard fail).
    // Search may raise the base prepareTimeoutS; hang still adds +1.5 s.
    const prepareBaseTimeoutS = resolveP1APrepareTimeoutS(state.p1aTrialStyleKnobs);
    const prepareTimeoutS = state.singleSupportHoldActive
      ? state.armHoldLatched && state.commandedStepForwardM > 1e-4
        ? prepareBaseTimeoutS + 1.5
        : prepareBaseTimeoutS
      : state.explicitStepActive
        ? Math.max(config.stepPrepareTimeoutS, 0.75)
        : config.stepPrepareTimeoutS;
    if (state.stepPhaseElapsedS >= prepareTimeoutS) {
      // Timeout: abort without lifting — stay disarmed, no thrash step.
      state.prepareAborted = true;
      state.prepareDurationS = state.stepPhaseElapsedS;
      clearStepMotion(state);
      return;
    }
    return;
  }

  // Track swing lift height during flight / hold.
  if (
    state.stepPhase === 'lift' ||
    state.stepPhase === 'hold' ||
    state.stepPhase === 'place'
  ) {
    const lift = Math.max(0, swingFoot.y - state.stepTargetWorldYM);
    if (lift > state.swingLiftM) state.swingLiftM = lift;
  }

  // --- P1A place: capture-point lateral target (no span-expansion) ---
  // Advance to load only on real swing re-contact + replacement error; never on timer alone.
  // C2 lower uses P1A_REPLACE_S; abort waits P1A_REPLACE_S + 2*safeDt for post-solver contact.
  if (state.stepPhase === 'place' && state.singleSupportHoldActive) {
    // Lateral-only capture-point replant while swing is still above the plant
    // freeze band; freeze once it descends so the final approach is not jerked.
    // Sagittal Z stays at the planned target (origin, or origin + commanded
    // forward); vertical Y stays at the latched plant height.
    if (
      p1aPlaceCaptureTargetMayUpdate({
        singleSupportHoldActive: state.singleSupportHoldActive,
        stepPhase: state.stepPhase,
        captureTargetFrozen: state.p1aPlaceCaptureTargetFrozen,
        swingFootYM: swingFoot.y,
        plantYM: state.stepTargetWorldYM,
        freezeAbovePlantM: resolveP1APlaceCaptureFreezeM(state.p1aTrialStyleKnobs),
      })
    ) {
      const comVX =
        observation.centerOfMassVelocity?.[0] ?? observation.pelvisVelocity[0];
      const captureX = p1aPlaceLateralCaptureTargetX({
        comX: observation.centerOfMass[0],
        comY: observation.centerOfMass[1],
        comVX,
        originalX: state.p1aPlaceOriginWorldXM,
        stancePlantX: state.stancePlantWorldXM,
        reachBoundM: resolveP1APlaceCaptureReachM(state.p1aTrialStyleKnobs),
      });
      // Push 20: lateral plant shift on top of capture (eval-only; default 0).
      state.stepTargetWorldXM = p1aPlacePlantShiftedTargetM({
        commandedForwardM: state.commandedStepForwardM,
        plantWorldM: captureX,
        shiftM: resolveP1APlacePlantShiftLateralM(state.p1aTrialStyleKnobs),
      });
      state.commandedStepLateralM =
        state.stepTargetWorldXM - state.p1aPlaceOriginWorldXM;
    } else if (!state.p1aPlaceCaptureTargetFrozen) {
      // Foot entered freeze band (or was already there): lock target for approach.
      state.p1aPlaceCaptureTargetFrozen = true;
    }
    const swingGrounded = footGroundedFromObservation(observation, swing, swingFoot.y);
    const stanceGrounded = footGroundedFromObservation(
      observation,
      stance,
      stanceFootLive.y,
    );
    const err = captureLandingErrorM(
      swingFoot,
      state.stepTargetWorldXM,
      state.stepTargetWorldZM,
    );
    state.supportSpanAfterM = supportSpanM(leftFoot, rightFoot);
    // Loss of stance during replace is an honest abort.
    if (!stanceGrounded) {
      state.stepLandingErrorM = err;
      clearStepMotion(state);
      return;
    }
    const placeMinS = 0.1;
    // In-place replace: grounded re-contact + horizontal proximity + impulse-bearing
    // swing-foot contact from the previous completed Box3D step (span does not grow).
    // replaceReady is checked first; timer alone never opens load.
    // Forward steps use the Gate A landing band (0.08 m); in-place P1A keeps
    // the tighter 0.06 m replace proximity that certified hold-and-replace.
    const replaceErrMaxM = state.commandedStepForwardM > 1e-4 ? 0.08 : 0.06;
    const replaceReady =
      state.stepPhaseElapsedS >= placeMinS &&
      swingGrounded &&
      err <= replaceErrMaxM &&
      p1aHasImpulseBearingSwingFootContact(observation, swing);
    if (replaceReady) {
      state.stepLandingErrorM = err;
      state.prevLandingErrorM = err;
      // Forward steps: re-latch the load plant to the measured touchdown sole so
      // plant IK does not fight a still-unreachable commanded target and skate
      // both feet. In-place P1A keeps the latched origin/capture target.
      if (state.commandedStepForwardM > 1e-4) {
        state.stepTargetWorldXM = swingFoot.x;
        state.stepTargetWorldYM = Math.min(swingFoot.y, 0.04);
        state.stepTargetWorldZM = swingFoot.z;
        state.p1aPlaceCaptureTargetFrozen = true;
      }
      // No Gate A planted-pose freeze at place→load; P1C plant runs live in load.
      state.stepPhase = 'load';
      state.stepPhaseElapsedS = safeDt;
      return;
    }
    state.prevLandingErrorM = err;
    // Forward steps get a short extra place budget so sagittal tracking can
    // close before the abort; zero-forward keeps certified P1A_REPLACE_S.
    const placeTimeoutS =
      state.commandedStepForwardM > 1e-4
        ? p1aPlaceTimeoutS(safeDt) + 0.25
        : p1aPlaceTimeoutS(safeDt);
    if (state.stepPhaseElapsedS >= placeTimeoutS) {
      // C2 lower + contact grace expired without impulse-bearing re-contact: abort
      // honestly (do not enter load on timer).
      state.stepLandingErrorM = err;
      clearStepMotion(state);
      return;
    }
    return;
  }

  // --- place: plant gate with decreasing error + span expansion ---
  if (state.stepPhase === 'place') {
    const grounded =
      swing === 'left'
        ? (observation.leftFootGrounded ?? isFootNearGround(swingFoot.y))
        : (observation.rightFootGrounded ?? isFootNearGround(swingFoot.y));
    const err = captureLandingErrorM(
      swingFoot,
      state.stepTargetWorldXM,
      state.stepTargetWorldZM,
    );
    const spanAfter = supportSpanM(leftFoot, rightFoot);
    state.supportSpanAfterM = spanAfter;
    // Explicit: allow plant as soon as land gates pass (no extra dwell that
    // invites sole bounce thrash). Capture recovery keeps longer place min.
    const placeMinS = state.explicitStepActive
      ? Math.max(config.stepPlaceS * 0.25, 0.12)
      : config.stepPlaceS * 0.4;
    const ready =
      state.stepPhaseElapsedS >= placeMinS &&
      canMarkPlanted({
        grounded,
        landingErrorM: err,
        prevLandingErrorM: state.prevLandingErrorM,
        supportSpanBeforeM: state.supportSpanBeforeM,
        supportSpanAfterM: spanAfter,
      });
    state.prevLandingErrorM = err;
    if (ready) {
      state.stepLandingErrorM = err;
      freezePlantedPoseFromStep(state, observation);
      state.stepPhase = 'load';
      state.stepPhaseElapsedS = safeDt;
      return;
    }
  }

  // Explicit one-step: slightly longer place/load dwell for plant acceptance +
  // double-support settle (phase timers only; plant safety gates unchanged).
  // P1A: contact-driven lift→hold (stance-only), consecutive hold 0.25 s, replace, recover 1.0 s.
  // Never enter/accrue hold from the lift ramp timer alone while both feet report grounded.
  if (state.singleSupportHoldActive) {
    const swingGrounded = footGroundedFromObservation(observation, swing, swingFoot.y);
    const stanceGrounded = footGroundedFromObservation(
      observation,
      stance,
      stanceFootLive.y,
    );
    const stanceOnly = stanceGrounded && !swingGrounded;

    if (state.stepPhase === 'lift') {
      // Loss of stance before single support is established → abort.
      if (!stanceGrounded) {
        clearStepMotion(state);
        return;
      }
      // Enter hold only on real swing contact-end while stance remains grounded.
      if (stanceOnly) {
        state.stepPhase = 'hold';
        state.stepPhaseElapsedS = safeDt;
        return;
      }
      // Stay in lift (joint ramp still drives clearance). No timer→hold transition.
      return;
    }

    if (state.stepPhase === 'hold') {
      // Hold must be consecutive real stance-only support.
      // Early re-contact or loss of stance aborts honestly.
      if (!stanceOnly) {
        clearStepMotion(state);
        return;
      }
      if (state.stepPhaseElapsedS >= P1A_HOLD_S) {
        state.stepPhase = 'place';
        state.stepPhaseElapsedS = safeDt;
        state.prevLandingErrorM = Number.POSITIVE_INFINITY;
        return;
      }
      return;
    }

    // Forward steps stand longer under measured-load acceptance before handing
    // off to free balance (P1C / Gate A 2 s settle); in-place keeps 1.0 s.
    const recoverS =
      state.commandedStepForwardM > 1e-4
        ? Math.max(P1A_RECOVER_S, 2.0)
        : P1A_RECOVER_S;
    if (state.stepPhase === 'load' && state.stepPhaseElapsedS >= recoverS) {
      clearStepMotion(state);
      return;
    }
    return;
  }

  const liftS = config.stepLiftS;
  const placeS = state.explicitStepActive
    ? Math.max(config.stepPlaceS, 0.7)
    : config.stepPlaceS;
  const loadS = state.explicitStepActive
    ? Math.max(config.stepLoadS, 0.7)
    : config.stepLoadS;

  if (state.stepPhase === 'lift' && state.stepPhaseElapsedS >= liftS) {
    state.stepPhase = 'place';
    state.stepPhaseElapsedS = safeDt;
    state.prevLandingErrorM = Number.POSITIVE_INFINITY;
  } else if (state.stepPhase === 'place' && state.stepPhaseElapsedS >= placeS) {
    // Time-out: only accept plant when land≤0.12 and span expansion is positive.
    const err = captureLandingErrorM(
      swingFoot,
      state.stepTargetWorldXM,
      state.stepTargetWorldZM,
    );
    state.stepLandingErrorM = err;
    const spanAfter = supportSpanM(leftFoot, rightFoot);
    state.supportSpanAfterM = spanAfter;
    const groundedTimeout =
      swing === 'left'
        ? (observation.leftFootGrounded ?? isFootNearGround(swingFoot.y))
        : (observation.rightFootGrounded ?? isFootNearGround(swingFoot.y));
    if (
      canMarkPlanted({
        grounded: groundedTimeout,
        landingErrorM: err,
        prevLandingErrorM: state.prevLandingErrorM,
        supportSpanBeforeM: state.supportSpanBeforeM,
        supportSpanAfterM: spanAfter,
      })
    ) {
      freezePlantedPoseFromStep(state, observation);
    }
    state.stepPhase = 'load';
    state.stepPhaseElapsedS = safeDt;
  } else if (state.stepPhase === 'load' && state.stepPhaseElapsedS >= loadS) {
    clearStepMotion(state);
  }
}

function setJointCmd(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  joint: HumanoidJointName,
  sagittal: number,
  lateral: number,
  maxTorqueNm: number,
  activation: number,
): void {
  const prev = commands[joint];
  const capacity = prev?.capacity ?? 1;
  commands[joint] = {
    targetOffsetRad: sagittal,
    targetRotationVectorRad: [sagittal, 0, lateral],
    maxTorqueNm: Math.max(maxTorqueNm, prev?.maxTorqueNm ?? 0),
    capacity,
    activation: Math.max(activation, prev?.activation ?? 0),
  };
}

/**
 * Checkpoint 16 prepare lateral signs (measured, mirror-consistent).
 *
 * With both feet planted, lat+ moves the thigh/pelvis coupling toward world +X
 * (FK). COM-to-stance shrinks when:
 *   stanceHipLat  ∝ +(com.x − stanceFoot.x)
 *   swingHipLat   ∝ −(com.x − stanceFoot.x)
 *   spineLat      ∝ −(com.x − stanceFoot.x)
 *
 * Equivalently for open-loop constant amp (stanceDirX = +1 right / −1 left):
 *   stanceHipLat = −stanceDirX · amp   (cp15 had the opposite sign)
 *   swingHipLat  = +stanceDirX · amp
 *   spineLat     = +stanceDirX · amp
 *
 * Calibration: artifacts/muscle-cp16-prepare-signs.json (transient dComMin≈−0.025).
 */
export function prepareLateralTargets(args: {
  comX: number;
  stanceFootX: number;
  /** Max |hip lat| rad. */
  maxLat?: number;
  /** Gain rad per meter of COM−stance error. */
  gainPerM?: number;
}): { stanceHipLat: number; swingHipLat: number; spineLat: number } {
  // cp16 calibration: slightly higher gain after sign flip to clear 0.10 m gate.
  const maxLat = args.maxLat ?? 0.2;
  const gain = args.gainPerM ?? 1.85;
  // error > 0 ⇒ COM right of stance foot ⇒ stanceHipLat > 0 (measured).
  const errX = args.comX - args.stanceFootX;
  const stanceHipLat = clamp(gain * errX, -maxLat, maxLat);
  const swingHipLat = clamp(-gain * errX * 0.75, -maxLat, maxLat);
  const spineLat = clamp(-gain * errX * 0.9, -maxLat, maxLat);
  return { stanceHipLat, swingHipLat, spineLat };
}

/**
 * C2 minimum-jerk blend on [0, 1]: s(u) = 10u³ − 15u⁴ + 6u⁵.
 * Endpoints: s(0)=0, s(1)=1; s'(0)=s'(1)=0; s''(0)=s''(1)=0.
 * Peak speed factor max s' = 1.875 at u = 0.5.
 */
export function minJerkBlend01(u: number): number {
  const x = clamp(u, 0, 1);
  const x2 = x * x;
  const x3 = x2 * x;
  const x4 = x2 * x2;
  const x5 = x4 * x;
  return 10 * x3 - 15 * x4 + 6 * x5;
}

/**
 * Deterministic P1A prepare lateral blend factor in [0, 1].
 * `prepareElapsedS / P1A_PREPARE_LATERAL_RAMP_S` through C2 min-jerk so the
 * first controlled frame commands only a small fraction of the full offset
 * and endpoint velocity/acceleration are zero.
 */
export function p1aPrepareLateralBlend01(
  prepareElapsedS: number,
  rampS: number = P1A_PREPARE_LATERAL_RAMP_S,
): number {
  return minJerkBlend01(prepareElapsedS / Math.max(rampS, 1e-3));
}

/**
 * P1A whole-body balance reference (lateral support transfer). Changes only
 * the joint controller's desired support point; physical contact/support
 * observation remains untouched for telemetry and phase gates.
 *
 * Lateral X: latched stance plant X ± inset, min-jerk phase blend.
 * Sagittal Z: remains physical support center Z (no remap).
 */
export function p1aSupportReference(args: {
  physicalSupportCenter: readonly [number, number, number];
  stanceSide: MuscleSwingSide;
  stanceFootX: number;
  stepPhase: MuscleStepPhase;
  phaseElapsedS: number;
}): {
  supportCenter: readonly [number, number, number];
  desiredStanceX: number;
  blend: number;
} {
  const desiredStanceX = p1aDesiredPelvisXM({
    stanceSide: args.stanceSide,
    stanceFootX: args.stanceFootX,
  });
  let blend = 0;
  if (args.stepPhase === 'prepare') {
    blend = p1aPrepareLateralBlend01(args.phaseElapsedS);
  } else if (
    args.stepPhase === 'lift' ||
    args.stepPhase === 'hold' ||
    args.stepPhase === 'place'
  ) {
    blend = 1;
  } else if (args.stepPhase === 'load') {
    // Return the whole-body reference to measured double support without a
    // target discontinuity after re-contact.
    blend = 1 - minJerkBlend01(args.phaseElapsedS / 0.5);
  }
  return {
    supportCenter: [
      args.physicalSupportCenter[0] +
        (desiredStanceX - args.physicalSupportCenter[0]) * blend,
      args.physicalSupportCenter[1],
      args.physicalSupportCenter[2],
    ],
    desiredStanceX,
    blend,
  };
}

function p1aBalanceObservation(
  state: MuscleControllerState,
  observation: MuscleObservation,
): MuscleObservation {
  if (
    !state.singleSupportHoldActive ||
    !state.swingSide ||
    !state.stancePlantLatched ||
    state.stepPhase === 'none'
  ) {
    return observation;
  }
  const stance: MuscleSwingSide =
    state.swingSide === 'left' ? 'right' : 'left';
  const reference = p1aSupportReference({
    physicalSupportCenter: observation.supportCenter,
    stanceSide: stance,
    stanceFootX: state.stancePlantWorldXM,
    stepPhase: state.stepPhase,
    phaseElapsedS: state.stepPhaseElapsedS,
  });
  return {
    ...observation,
    supportCenter: reference.supportCenter,
  };
}

/** Deterministic staged P1A weight-transfer blends. */
export function p1aPrepareStageBlends(prepareElapsedS: number): {
  preloadBlend: number;
  shiftBlend: number;
} {
  return {
    preloadBlend: minJerkBlend01(
      prepareElapsedS / Math.max(P1A_PREPARE_PRELOAD_S, 1e-3),
    ),
    shiftBlend: minJerkBlend01(
      (prepareElapsedS - P1A_PREPARE_PRELOAD_S) /
        Math.max(P1A_PREPARE_SHIFT_S, 1e-3),
    ),
  };
}

/**
 * Desired prepare pelvis X (m) inside the stance footprint.
 * stanceFootX ± {@link P1A_PREPARE_PELVIS_INSET_M} toward the inter-foot center
 * (left stance +inset, right stance −inset). Pure target construction — no body writes.
 */
export function p1aDesiredPelvisXM(args: {
  stanceSide: MuscleSwingSide;
  stanceFootX: number;
  /** Override inset (m); default P1A_PREPARE_PELVIS_INSET_M. */
  insetM?: number;
}): number {
  const inset = args.insetM ?? P1A_PREPARE_PELVIS_INSET_M;
  const towardCenter = args.stanceSide === 'left' ? 1 : -1;
  return args.stanceFootX + towardCenter * inset;
}

/**
 * Geometry-derived spine counter-lean (local Z, rad) for P1A prepare.
 *
 * desiredCOM.x = stanceFootX ± inset (see {@link p1aDesiredPelvisXM}).
 * spineLat = clamp(−atan2(COM.x − desiredCOM.x, torsoLength), ±maxRad).
 *
 * Sign matches existing rig convention ({@link lateralTargetRad} / prepare laterals):
 * +errorX (COM right of desired) → negative spine Z (counter-lean left).
 */
export function p1aSpineCounterLeanRad(args: {
  comX: number;
  desiredComX: number;
  /** Effective torso length (m); default {@link P1A_PREPARE_SPINE_TORSO_LENGTH_M}. */
  torsoLengthM?: number;
  /** Clamp (rad); default config.maxJointOffsetRad (0.28). */
  maxRad?: number;
}): number {
  const L = Math.max(args.torsoLengthM ?? P1A_PREPARE_SPINE_TORSO_LENGTH_M, 1e-3);
  const maxRad = args.maxRad ?? DEFAULT_MUSCLE_CONTROLLER_CONFIG.maxJointOffsetRad;
  return clamp(-Math.atan2(args.comX - args.desiredComX, L), -maxRad, maxRad);
}

/**
 * Geometry-derived bilateral ankle-roll CoP bias (local Z, rad) for P1A prepare.
 *
 * magnitude = clamp(atan2(COM.x − desiredCOM.x, max(pelvisHeight, 0.8)), ±maxRad)
 * Mirrored signs (established ankle CoP convention):
 *   +errorX (COM right of desired) → rightAnkle +, leftAnkle −
 * Preserve ankle pitch at the caller (this returns lateral roll only).
 */
export function p1aAnkleCopRollBiasRad(args: {
  comX: number;
  desiredComX: number;
  pelvisHeightM: number;
  /** Clamp (rad); default {@link P1A_PREPARE_ANKLE_COP_ROLL_MAX_RAD}. */
  maxRad?: number;
}): {
  magnitudeRad: number;
  leftAnkleRollRad: number;
  rightAnkleRollRad: number;
} {
  const maxRad = args.maxRad ?? P1A_PREPARE_ANKLE_COP_ROLL_MAX_RAD;
  const h = Math.max(args.pelvisHeightM, 0.8);
  const magnitudeRad = clamp(
    Math.atan2(args.comX - args.desiredComX, h),
    -maxRad,
    maxRad,
  );
  return {
    magnitudeRad,
    // right + / left − for positive COM-right error (matches buildJointCommands ankle roll).
    rightAnkleRollRad: magnitudeRad,
    leftAnkleRollRad: -magnitudeRad,
  };
}

/**
 * Pure clamp for P1A prepare ankle-roll targets near the CoP-useful ceiling.
 * Does not change sign; only bounds magnitude.
 */
export function p1aPrepareClampAnkleRollRad(
  rollRad: number,
  maxRad: number = P1A_PREPARE_STANCE_ANKLE_ROLL_MAX_RAD,
): number {
  const m = Math.max(0, maxRad);
  return clamp(rollRad, -m, m);
}

/**
 * P1A swing sagittal (world-Z) measured-error feedback gain (rad/m).
 *
 * Lift retains 1.6 for release/clearance; hold uses the existing place scale 2.0
 * (approx. inverse Jacobian for a ~0.5 m leg link). Lateral world-X keeps its
 * prior shared schedule separately — never reuse this gain for X.
 * When `commandedForwardM > 0`, place/hold use a higher gain so short/full
 * forward targets close before touchdown (in-place P1A keeps 2.0).
 * Gate A / capture use config stepLiftGainXZ / stepPlaceGainXZ, not this helper.
 */
export function p1aSwingSagittalFeedbackGainPerM(
  stepPhase: MuscleStepPhase,
  commandedForwardM: number = 0,
): number {
  const forwardBoost =
    typeof commandedForwardM === 'number' &&
    Number.isFinite(commandedForwardM) &&
    commandedForwardM > 1e-4;
  if (stepPhase === 'place') {
    // Place forward: Z gain so short-step advance reaches ≥0.06 m.
    return forwardBoost ? 2.9 : 2.0;
  }
  if (stepPhase === 'hold') {
    // Hold forward: track Z while raised so place starts closer to target.
    return forwardBoost ? 2.8 : 2.0;
  }
  // lift (and any other phase fallback)
  return forwardBoost ? 1.9 : 1.6;
}

/**
 * P1A relative swing ankle sagittal counterrotation (pure).
 *
 * Preserves latched sole pitch under hip/knee deltas:
 *   ankleSag = plantedAnkle − hipSagDelta − kneeSagDelta
 * Clamped to ±{@link P1A_SWING_ANKLE_SAG_MAX_RAD} (±0.25).
 * Gate A / capture use independent ankle laws and do not call this.
 */
export function p1aSwingAnkleSagFromChain(args: {
  plantedAnkleRad: number;
  hipSagDeltaRad: number;
  kneeSagDeltaRad: number;
  /** Clamp (rad); default {@link P1A_SWING_ANKLE_SAG_MAX_RAD}. */
  maxRad?: number;
}): number {
  const maxRad = args.maxRad ?? P1A_SWING_ANKLE_SAG_MAX_RAD;
  return clamp(
    args.plantedAnkleRad - args.hipSagDeltaRad - args.kneeSagDeltaRad,
    -maxRad,
    maxRad,
  );
}

/**
 * P1A canonical two-link vertical-shortening swing deltas (pure).
 *
 * Authoritative segment lengths (same as {@link solveTwoLinkLegIk}):
 *   L1 = {@link CAPTURE_THIGH_LENGTH_M}
 *   L2 = {@link CAPTURE_SHANK_LENGTH_M} + {@link CAPTURE_FOOT_DROP_M}
 *
 * For requested lift `h` (C2-scaled before solve):
 *   r = L1 + L2 − h
 *   kneeFlex = acos(clamp((r² − L1² − L2²) / (2 L1 L2), −1, 1))
 *   hipCounter = atan2(L2 sin(kneeFlex), L1 + L2 cos(kneeFlex))
 *   hipSagDelta = +hipCounter, kneeSagDelta = −kneeFlex
 *
 * At `h === 0` returns exact `+0 / +0` so the first C2 frame is continuous with
 * the latched prepare motor pose. No empirical coefficients. Callers add these
 * deltas to the latched prepare pose, then apply measured −kZ·errZ (phase
 * sagittal gain) and world-X lateral correction; ankle counterrotation uses
 * post-clamp applied hip+knee deltas.
 */
export interface P1ASwingVerticalShorteningDeltas {
  /** +hipCounter (rad). */
  hipSagDeltaRad: number;
  /** −kneeFlex (rad); more negative is more flexed. */
  kneeSagDeltaRad: number;
}

export function p1aSwingVerticalShorteningDeltas(
  liftHM: number,
): P1ASwingVerticalShorteningDeltas {
  // Exact continuity at the latched plant: do not rely on acos/atan2 of a
  // floating-point cos=1 edge case for the first C2 frame.
  if (liftHM === 0) {
    return { hipSagDeltaRad: 0, kneeSagDeltaRad: 0 };
  }
  const L1 = CAPTURE_THIGH_LENGTH_M;
  const L2 = CAPTURE_SHANK_LENGTH_M + CAPTURE_FOOT_DROP_M;
  const r = L1 + L2 - liftHM;
  const cosKnee = clamp(
    (r * r - L1 * L1 - L2 * L2) / (2 * L1 * L2),
    -1,
    1,
  );
  const kneeFlex = Math.acos(cosKnee);
  const hipCounter = Math.atan2(
    L2 * Math.sin(kneeFlex),
    L1 + L2 * Math.cos(kneeFlex),
  );
  return {
    hipSagDeltaRad: hipCounter,
    kneeSagDeltaRad: -kneeFlex,
  };
}

export interface P1AFootLoadFractions {
  hasMeasurement: boolean;
  combinedNormalImpulse: number;
  stanceLoadFraction: number;
  swingLoadFraction: number;
}

/**
 * True when the swing foot has impulse-bearing post-solver contact from the
 * previous completed Box3D step: finite normal impulse > 0 and contact point
 * count > 0. Missing, non-finite, or zero telemetry is never treated as success.
 * Used only by the P1A place→load gate (not Gate A / capture place).
 */
export function p1aHasImpulseBearingSwingFootContact(
  observation: Pick<
    MuscleObservation,
    | 'leftFootNormalImpulse'
    | 'rightFootNormalImpulse'
    | 'leftFootContactPointCount'
    | 'rightFootContactPointCount'
  >,
  swingSide: MuscleSwingSide,
): boolean {
  const impulse =
    swingSide === 'left'
      ? observation.leftFootNormalImpulse
      : observation.rightFootNormalImpulse;
  const pointCount =
    swingSide === 'left'
      ? observation.leftFootContactPointCount
      : observation.rightFootContactPointCount;
  if (impulse === undefined || pointCount === undefined) return false;
  if (!Number.isFinite(impulse) || !Number.isFinite(pointCount)) return false;
  return impulse > 0 && pointCount > 0;
}

/**
 * Measured P1A stance/swing load shares from the previous completed physics
 * step. Invalid or absent telemetry is not treated as zero load authority.
 */
export function p1aFootLoadFractions(
  observation: Pick<
    MuscleObservation,
    'leftFootNormalImpulse' | 'rightFootNormalImpulse'
  >,
  stanceSide: MuscleSwingSide,
): P1AFootLoadFractions {
  const left = observation.leftFootNormalImpulse;
  const right = observation.rightFootNormalImpulse;
  const valid =
    Number.isFinite(left) &&
    Number.isFinite(right) &&
    (left as number) >= 0 &&
    (right as number) >= 0;
  const combinedNormalImpulse = valid ? (left as number) + (right as number) : 0;
  if (!valid || combinedNormalImpulse <= 0) {
    return {
      hasMeasurement: false,
      combinedNormalImpulse: 0,
      stanceLoadFraction: 0,
      swingLoadFraction: 0,
    };
  }
  const stanceImpulse = stanceSide === 'left' ? (left as number) : (right as number);
  const stanceLoadFraction = stanceImpulse / combinedNormalImpulse;
  return {
    hasMeasurement: true,
    combinedNormalImpulse,
    stanceLoadFraction,
    swingLoadFraction: 1 - stanceLoadFraction,
  };
}

/**
 * True only when measured foot loads satisfy the prepare transfer gate.
 * Optional thresholds: absence → frozen production constants (P1A identity).
 */
export function p1aHasPrepareLoadAuthority(
  load: P1AFootLoadFractions,
  opts?: {
    stanceLoadFractionMin?: number;
    swingLoadFractionMax?: number;
  },
): boolean {
  const stanceMin =
    typeof opts?.stanceLoadFractionMin === 'number' &&
    Number.isFinite(opts.stanceLoadFractionMin)
      ? opts.stanceLoadFractionMin
      : P1A_PREPARE_STANCE_LOAD_FRACTION_MIN;
  const swingMax =
    typeof opts?.swingLoadFractionMax === 'number' &&
    Number.isFinite(opts.swingLoadFractionMax)
      ? opts.swingLoadFractionMax
      : P1A_PREPARE_SWING_LOAD_FRACTION_MAX;
  return (
    load.hasMeasurement &&
    load.stanceLoadFraction >= stanceMin &&
    load.swingLoadFraction <= swingMax
  );
}

/**
 * Feedback authority for fixed-foot hip translation. Time alone cannot open
 * this path: measured stance load must first cross the declared start threshold.
 * Optional thresholds preserve P1A identity when omitted.
 */
export function p1aPrepareLoadAuthorityBlend01(
  load: P1AFootLoadFractions,
  opts?: {
    stanceLoadFractionMin?: number;
    shiftLoadFractionStart?: number;
  },
): number {
  if (!load.hasMeasurement) return 0;
  const stanceMin =
    typeof opts?.stanceLoadFractionMin === 'number' &&
    Number.isFinite(opts.stanceLoadFractionMin)
      ? opts.stanceLoadFractionMin
      : P1A_PREPARE_STANCE_LOAD_FRACTION_MIN;
  const shiftStart =
    typeof opts?.shiftLoadFractionStart === 'number' &&
    Number.isFinite(opts.shiftLoadFractionStart)
      ? opts.shiftLoadFractionStart
      : P1A_PREPARE_SHIFT_LOAD_FRACTION_START;
  const span = stanceMin - shiftStart;
  return clamp(
    (load.stanceLoadFraction - shiftStart) / Math.max(span, 1e-6),
    0,
    1,
  );
}

/**
 * Per-leg differential IK deltas for P1A fixed-foot prepare (WBC-lite).
 *
 * delta = desiredIK(fixed foot from laterally shifted hip) − currentIK
 * (same fixed foot from observed hip). Ankle lateral counter-roll cancels
 * hip lateral delta in the foot frame so the sole stays flat (FK: hip lat+
 * and ankle lat+ both lean toward world +X → ankleLat = −hipLat).
 */
export interface P1AFixedFootDiffIkLeg {
  /** desiredIK.hipLateral − currentIK.hipLateral (rad). */
  deltaHipLateralRad: number;
  /** desiredIK.knee − currentIK.knee (rad). */
  deltaKneeRad: number;
  /** Sole counter-roll: −deltaHipLateralRad (rad). */
  deltaAnkleLateralRad: number;
  currentHipLateralRad: number;
  desiredHipLateralRad: number;
  currentKneeRad: number;
  desiredKneeRad: number;
}

/**
 * Fixed-foot / desired-pelvis differential IK targets (WBC-lite, joint angles).
 *
 * Both feet stay at fixed world plants. Desired hip X = observed hip X +
 * (desiredPelvisX − observedPelvisX); hip Y/Z unchanged. For each leg:
 *   currentIK = two-link(observed hip → latched foot)
 *   desiredIK = two-link(desired hip → same latched foot)
 *   deltas = desired − current
 * No plant-gain foot-error feedback, no live-hip→move-feet solve, no forces.
 */
export function p1aFixedFootDifferentialIkTargets(args: {
  desiredPelvisX: number;
  pelvisX: number;
  leftHip: CaptureVec3;
  rightHip: CaptureVec3;
  leftFootTarget: CaptureVec3;
  rightFootTarget: CaptureVec3;
}): {
  left: P1AFixedFootDiffIkLeg;
  right: P1AFixedFootDiffIkLeg;
  deltaXM: number;
} {
  const deltaXM = args.desiredPelvisX - args.pelvisX;

  const solveLeg = (
    hip: CaptureVec3,
    footTarget: CaptureVec3,
    side: MuscleSwingSide,
  ): P1AFixedFootDiffIkLeg => {
    const desiredHip: CaptureVec3 = {
      x: hip.x + deltaXM,
      y: hip.y,
      z: hip.z,
    };
    const currentIk = solveTwoLinkLegIk({
      hip,
      footTarget,
      swingSide: side,
    });
    const desiredIk = solveTwoLinkLegIk({
      hip: desiredHip,
      footTarget,
      swingSide: side,
    });
    const deltaHipLateralRad = desiredIk.hipLateralRad - currentIk.hipLateralRad;
    const deltaKneeRad = desiredIk.kneeRad - currentIk.kneeRad;
    // Parent-local Z: lat+ → world +X for both hip and ankle. Flat sole ⇒
    // ankle roll cancels the hip lateral delta (same sign both legs).
    const deltaAnkleLateralRad = -deltaHipLateralRad;
    return {
      deltaHipLateralRad,
      deltaKneeRad,
      deltaAnkleLateralRad,
      currentHipLateralRad: currentIk.hipLateralRad,
      desiredHipLateralRad: desiredIk.hipLateralRad,
      currentKneeRad: currentIk.kneeRad,
      desiredKneeRad: desiredIk.kneeRad,
    };
  };

  return {
    left: solveLeg(args.leftHip, args.leftFootTarget, 'left'),
    right: solveLeg(args.rightHip, args.rightFootTarget, 'right'),
    deltaXM,
  };
}

/**
 * @deprecated Prefer {@link p1aFixedFootDifferentialIkTargets}. Absolute desired
 * hip laterals (desiredIK only) — retained for callers that only need that.
 */
export function p1aFixedFootHipLateralTargets(args: {
  desiredPelvisX: number;
  pelvisX: number;
  leftHip: CaptureVec3;
  rightHip: CaptureVec3;
  leftFootTarget: CaptureVec3;
  rightFootTarget: CaptureVec3;
}): { leftHipLat: number; rightHipLat: number; deltaXM: number } {
  const diff = p1aFixedFootDifferentialIkTargets(args);
  return {
    leftHipLat: diff.left.desiredHipLateralRad,
    rightHipLat: diff.right.desiredHipLateralRad,
    deltaXM: diff.deltaXM,
  };
}

/**
 * Add a ramped lateral (local Z) delta onto a spherical joint command.
 * Preserves sagittal targetOffsetRad / vector X, capacity, activation,
 * existing maxTorqueNm (does not raise torque budgets), and optional
 * maxSpeedRadS when present.
 */
function overlaySphericalLateralDelta(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  joint: HumanoidJointName,
  deltaLat: number,
  blend01: number,
): void {
  const prev = commands[joint];
  if (!prev) return;
  const baseLat = prev.targetRotationVectorRad[2] ?? 0;
  const blended = baseLat + deltaLat * clamp(blend01, 0, 1);
  const sag = prev.targetOffsetRad;
  commands[joint] = {
    targetOffsetRad: sag,
    targetRotationVectorRad: [sag, 0, blended],
    maxTorqueNm: prev.maxTorqueNm,
    capacity: prev.capacity,
    activation: prev.activation,
    ...(prev.maxSpeedRadS !== undefined ? { maxSpeedRadS: prev.maxSpeedRadS } : {}),
  };
}

/**
 * Cap maxSpeedRadS on listed joints only. Does not alter target angles or torque.
 */
function applyP1AMotorSpeedCap(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  joints: readonly HumanoidJointName[],
  maxSpeedRadS: number,
): void {
  for (const joint of joints) {
    const prev = commands[joint];
    if (!prev) continue;
    commands[joint] = {
      ...prev,
      maxSpeedRadS,
    };
  }
}

/**
 * Cap maxSpeedRadS on listed joints to the prepare-only limit (after min-jerk overlay).
 */
function applyP1APrepareMotorSpeedCap(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  joints: readonly HumanoidJointName[],
): void {
  applyP1AMotorSpeedCap(commands, joints, P1A_PREPARE_MOTOR_SPEED_MAX_RAD_S);
}

/**
 * Cap swing hip/knee/ankle maxSpeedRadS during P1A lift/hold/place only.
 * Uses {@link P1A_SWING_MOTOR_SPEED_MAX_RAD_S} (2.25; prepare remains 0.75).
 */
function applyP1ASwingMotorSpeedCap(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  joints: readonly HumanoidJointName[],
): void {
  applyP1AMotorSpeedCap(commands, joints, P1A_SWING_MOTOR_SPEED_MAX_RAD_S);
}

/**
 * Blend a spherical joint's lateral (Z) toward an absolute target with min-jerk.
 * Preserves sagittal pitch/torque/capacity/activation.
 */
function overlaySphericalLateralToward(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  joint: HumanoidJointName,
  targetLat: number,
  blend01: number,
): void {
  const prev = commands[joint];
  if (!prev) return;
  const baseLat = prev.targetRotationVectorRad[2] ?? 0;
  const u = clamp(blend01, 0, 1);
  const blended = baseLat + (targetLat - baseLat) * u;
  const sag = prev.targetOffsetRad;
  commands[joint] = {
    targetOffsetRad: sag,
    targetRotationVectorRad: [sag, 0, blended],
    maxTorqueNm: prev.maxTorqueNm,
    capacity: prev.capacity,
    activation: prev.activation,
    ...(prev.maxSpeedRadS !== undefined ? { maxSpeedRadS: prev.maxSpeedRadS } : {}),
  };
}

/**
 * P1A prepare: upper-body counterweight + ankle CoP overlays on stationary base.
 *
 * Does NOT command hip/knee laterals under the pelvis (fixed-foot leg IK path).
 * Geometry targets from {@link p1aSpineCounterLeanRad} / {@link p1aAnkleCopRollBiasRad};
 * ramped with {@link p1aPrepareLateralBlend01}. Speed cap only on modified joints.
 */
function applyP1APrepareCounterweightCopCommands(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  state: Pick<
    MuscleControllerState,
    | 'stancePlantWorldXM'
    | 'stancePlantWorldYM'
    | 'stancePlantWorldZM'
    | 'swingSide'
  >,
  observation: MuscleObservation,
  blend01: number,
  config: MuscleControllerConfig,
  opts: { spine: boolean; ankleCop: boolean },
): void {
  const swing = state.swingSide;
  if (!swing) return;
  const stance: MuscleSwingSide = swing === 'left' ? 'right' : 'left';
  const desiredComX = p1aDesiredPelvisXM({
    stanceSide: stance,
    stanceFootX: state.stancePlantWorldXM,
  });
  const comX = observation.centerOfMass[0];
  const blend = clamp(blend01, 0, 1);
  const modified: HumanoidJointName[] = [];

  if (opts.spine) {
    const spineTarget = p1aSpineCounterLeanRad({
      comX,
      desiredComX,
      maxRad: config.maxJointOffsetRad,
    });
    // Absolute counter-lean target blended from base (not additive open-loop gain).
    overlaySphericalLateralToward(commands, 'spine', spineTarget, blend);
    modified.push('spine');
  }

  if (opts.ankleCop) {
    const cop = p1aAnkleCopRollBiasRad({
      comX,
      desiredComX,
      pelvisHeightM: observation.pelvisPosition[1],
    });
    // Additive bias on base balance roll; preserve pitch (X).
    overlaySphericalLateralDelta(commands, 'leftAnkle', cop.leftAnkleRollRad, blend);
    overlaySphericalLateralDelta(commands, 'rightAnkle', cop.rightAnkleRollRad, blend);
    modified.push('leftAnkle', 'rightAnkle');
  }

  if (modified.length > 0) {
    applyP1APrepareMotorSpeedCap(commands, modified);
  }
}

/**
 * P1A prepare command path: fixed-foot / desired-pelvis differential IK
 * overlaid on already-computed stationary balance commands (joint targets).
 *
 * - Both feet stay at latched world plants (stancePlantWorld*, stepTargetWorld*).
 * - Desired pelvis X = stance plant X ± {@link P1A_PREPARE_PELVIS_INSET_M}.
 * - Per-leg: currentIK(observed hip→plant) vs desiredIK(shifted hip→same plant).
 * - Ramped deltas applied: hip lateral + ankle lateral counter-roll only.
 *   Knee length delta is intentionally omitted (prior hip/knee/ankle ablation:
 *   hip+knee raised plant error vs hip+ankle; none strictly passed prepare).
 * - Preserves hip sagittal, ankle sagittal pitch, spine, torque caps, capacity,
 *   activation. No live-foot plant-gain feedback.
 *
 * Gate A / capture prepare continues to use {@link applyStanceBraceCommands}.
 * Spine/CoP strategies use {@link applyP1APrepareCounterweightCopCommands}.
 */
function applyP1APrepareFixedFootHipLateralCommands(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  state: Pick<
    MuscleControllerState,
    | 'stancePlantWorldXM'
    | 'stancePlantWorldYM'
    | 'stancePlantWorldZM'
    | 'stepTargetWorldXM'
    | 'stepTargetWorldYM'
    | 'stepTargetWorldZM'
    | 'swingSide'
  >,
  observation: MuscleObservation,
  blend01: number,
): void {
  const swing = state.swingSide;
  if (!swing) return;
  const stance: MuscleSwingSide = swing === 'left' ? 'right' : 'left';
  const { leftHip, rightHip } = observationFeet(observation);

  const stancePlant: CaptureVec3 = {
    x: state.stancePlantWorldXM,
    y: state.stancePlantWorldYM,
    z: state.stancePlantWorldZM,
  };
  const swingPlant: CaptureVec3 = {
    x: state.stepTargetWorldXM,
    y: state.stepTargetWorldYM,
    z: state.stepTargetWorldZM,
  };
  // Each leg uses its own latched plant: stance plant + original swing plant.
  const leftFootTarget = stance === 'left' ? stancePlant : swingPlant;
  const rightFootTarget = stance === 'right' ? stancePlant : swingPlant;

  const desiredPelvisX = p1aDesiredPelvisXM({
    stanceSide: stance,
    stanceFootX: stancePlant.x,
  });
  const diff = p1aFixedFootDifferentialIkTargets({
    desiredPelvisX,
    pelvisX: observation.pelvisPosition[0],
    leftHip,
    rightHip,
    leftFootTarget,
    rightFootTarget,
  });
  const blend = clamp(blend01, 0, 1);

  // Hip lateral deltas (preserve sagittal).
  overlaySphericalLateralDelta(commands, 'leftHip', diff.left.deltaHipLateralRad, blend);
  overlaySphericalLateralDelta(commands, 'rightHip', diff.right.deltaHipLateralRad, blend);
  // Knee length delta omitted (prior ablation: raised plant error vs hip+ankle).
  // Ankle lateral counter-roll (preserve sagittal pitch).
  overlaySphericalLateralDelta(
    commands,
    'leftAnkle',
    diff.left.deltaAnkleLateralRad,
    blend,
  );
  overlaySphericalLateralDelta(
    commands,
    'rightAnkle',
    diff.right.deltaAnkleLateralRad,
    blend,
  );

  // Soft motor speed cap on hips + ankles only (after min-jerk overlay).
  applyP1APrepareMotorSpeedCap(commands, [
    'leftHip',
    'rightHip',
    'leftAnkle',
    'rightAnkle',
  ]);
}

/**
 * P1A load-gated hip strategy using the sign convention physically calibrated
 * by the cp16 prepare ablation. Only hip lateral targets are overlaid; base
 * sagittal targets, ankle CoP control, torque caps, and all body transforms are
 * preserved. This replaces the differential-IK production path whose local
 * angle signs drove COM away from the stance foot in the real rig.
 */
function applyP1APrepareCalibratedHipShiftCommands(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  state: Pick<
    MuscleControllerState,
    'stancePlantWorldXM' | 'swingSide'
  >,
  observation: MuscleObservation,
  blend01: number,
): void {
  const swing = state.swingSide;
  if (!swing) return;
  const stance: MuscleSwingSide = swing === 'left' ? 'right' : 'left';
  const hipStance = stance === 'left' ? 'leftHip' : 'rightHip';
  const hipSwing = swing === 'left' ? 'leftHip' : 'rightHip';
  const targets = prepareLateralTargets({
    comX: observation.centerOfMass[0],
    stanceFootX: state.stancePlantWorldXM,
  });
  const blend = clamp(blend01, 0, 1);
  overlaySphericalLateralToward(
    commands,
    hipStance,
    targets.stanceHipLat,
    blend,
  );
  overlaySphericalLateralToward(
    commands,
    hipSwing,
    targets.swingHipLat,
    blend,
  );
  applyP1APrepareMotorSpeedCap(commands, [hipStance, hipSwing]);
}

function scaleJointMaxTorque(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  joint: HumanoidJointName,
  scale: number,
): void {
  const prev = commands[joint];
  if (!prev || scale === 1) return;
  commands[joint] = {
    ...prev,
    maxTorqueNm: prev.maxTorqueNm * scale,
  };
}

/**
 * Scale spherical ankle roll (Z) by gain, then clamp magnitude.
 */
function scaleAndClampAnkleRoll(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  joint: HumanoidJointName,
  gainScale: number,
  maxRad: number,
): void {
  const prev = commands[joint];
  if (!prev) return;
  const lat = (prev.targetRotationVectorRad[2] ?? 0) * gainScale;
  const capped = p1aPrepareClampAnkleRollRad(lat, maxRad);
  const sag = prev.targetOffsetRad;
  commands[joint] = {
    targetOffsetRad: sag,
    targetRotationVectorRad: [sag, 0, capped],
    maxTorqueNm: prev.maxTorqueNm,
    capacity: prev.capacity,
    activation: prev.activation,
    ...(prev.maxSpeedRadS !== undefined ? { maxSpeedRadS: prev.maxSpeedRadS } : {}),
  };
}

/**
 * P1A prepare lateral load-share rebalance (Stage-1 evidence):
 * 1. Cap both ankle rolls near the CoP-useful ceiling (wasted dual-support sat).
 * 2. Mild stance-ankle torque headroom so useful CoP torque is not continuous-sat.
 * 3. Route residual COM→stance shift through ramped {@link prepareLateralTargets}
 *    (cp16 signs) — balance-layer hip signs differ and do not substitute.
 *
 * Does not touch sagittal targets, spine, or Gate A / capture prepare.
 */
function applyP1APrepareLateralLoadShareCommands(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  state: Pick<
    MuscleControllerState,
    | 'stancePlantWorldXM'
    | 'swingSide'
    | 'stepPhaseElapsedS'
    | 'p1aTrialStyleKnobs'
  >,
  observation: MuscleObservation,
): void {
  const swing = state.swingSide;
  if (!swing) return;
  const stance: MuscleSwingSide = swing === 'left' ? 'right' : 'left';
  const ankleStance = stance === 'left' ? 'leftAnkle' : 'rightAnkle';
  const hipStance = stance === 'left' ? 'leftHip' : 'rightHip';
  const hipSwing = swing === 'left' ? 'leftHip' : 'rightHip';

  // Soft ankle-roll ceiling is always on during prepare (CoP-useful bound).
  scaleAndClampAnkleRoll(
    commands,
    'leftAnkle',
    P1A_PREPARE_ANKLE_ROLL_GAIN_SCALE,
    P1A_PREPARE_STANCE_ANKLE_ROLL_MAX_RAD,
  );
  scaleAndClampAnkleRoll(
    commands,
    'rightAnkle',
    P1A_PREPARE_ANKLE_ROLL_GAIN_SCALE,
    P1A_PREPARE_STANCE_ANKLE_ROLL_MAX_RAD,
  );

  // Skip first-frame dust: planning/first control frame keeps balance-layer hip
  // laterals and torque budgets (preload identity). Rebalance opens with ramp.
  const blend = p1aPrepareLateralBlend01(state.stepPhaseElapsedS);
  if (blend < 1e-4) return;

  scaleJointMaxTorque(commands, ankleStance, P1A_PREPARE_ANKLE_TORQUE_SCALE);

  const knobs = state.p1aTrialStyleKnobs;
  const targets = prepareLateralTargets({
    comX: observation.centerOfMass[0],
    stanceFootX: state.stancePlantWorldXM,
    maxLat: resolveP1APrepareHipLatMaxRad(knobs),
    gainPerM: resolveP1APrepareHipLatGainPerM(knobs),
  });
  overlaySphericalLateralToward(commands, hipStance, targets.stanceHipLat, blend);
  overlaySphericalLateralToward(commands, hipSwing, targets.swingHipLat, blend);
  applyP1APrepareMotorSpeedCap(commands, [hipStance, hipSwing]);
}

/**
 * P1A staged weight transfer:
 * 1. preload the stance with upper-body counterweight + ankle CoP control;
 * 2. hold that preload while adding measured-load-gated calibrated hip shift.
 */
/** @deprecated Diagnostic ablation retained for reproducibility; not used by production P1A. */
export function applyP1APrepareStagedWeightTransferCommands(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  state: Pick<
    MuscleControllerState,
    | 'stancePlantWorldXM'
    | 'stancePlantWorldYM'
    | 'stancePlantWorldZM'
    | 'stepTargetWorldXM'
    | 'stepTargetWorldYM'
    | 'stepTargetWorldZM'
    | 'swingSide'
  >,
  observation: MuscleObservation,
  prepareElapsedS: number,
  config: MuscleControllerConfig,
): void {
  const { preloadBlend, shiftBlend } = p1aPrepareStageBlends(prepareElapsedS);
  const swing = state.swingSide;
  if (!swing) return;
  const stance: MuscleSwingSide = swing === 'left' ? 'right' : 'left';
  const load = p1aFootLoadFractions(observation, stance);
  const loadAuthorityBlend = p1aPrepareLoadAuthorityBlend01(load);
  applyP1APrepareCounterweightCopCommands(
    commands,
    state,
    observation,
    preloadBlend,
    config,
    { spine: true, ankleCop: true },
  );
  const effectiveShiftBlend = shiftBlend * loadAuthorityBlend;
  if (effectiveShiftBlend > 0) {
    applyP1APrepareCalibratedHipShiftCommands(
      commands,
      state,
      observation,
      effectiveShiftBlend,
    );
  }
}

/**
 * Strong stance-leg brace used during prepare / lift / place / load.
 * Joint motors only — shifts load onto stance without lifting swing during prepare.
 * Gate A / capture prepare uses this path; P1A prepare uses
 * {@link applyP1APrepareFixedFootHipLateralCommands} instead.
 */
function applyStanceBraceCommands(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  swing: MuscleSwingSide,
  config: MuscleControllerConfig,
  opts: {
    /** Bias COM toward stance via hip laterals (cp16 measured signs). */
    comShift: boolean;
    comX?: number;
    stanceFootX?: number;
    stanceHipSag?: number;
    stanceHipLat?: number;
    spineLat?: number;
    spineSag?: number;
  },
): void {
  const stance: MuscleSwingSide = swing === 'left' ? 'right' : 'left';
  const hipStance = stance === 'left' ? 'leftHip' : 'rightHip';
  const kneeStance = stance === 'left' ? 'leftKnee' : 'rightKnee';
  const ankleStance = stance === 'left' ? 'leftAnkle' : 'rightAnkle';
  const hipSwing = swing === 'left' ? 'leftHip' : 'rightHip';
  const kneeSwing = swing === 'left' ? 'leftKnee' : 'rightKnee';
  const ankleSwing = swing === 'left' ? 'leftAnkle' : 'rightAnkle';
  const stanceCap = commands[hipStance]?.capacity ?? clamp01(config.strength);
  const swingCap = commands[hipSwing]?.capacity ?? clamp01(config.strength);

  const stanceDirX = stance === 'right' ? 1 : -1;
  let stanceHipLat = opts.stanceHipLat;
  let swingHipLat = 0;
  let spineLat = opts.spineLat;
  if (opts.comShift) {
    if (opts.comX !== undefined && opts.stanceFootX !== undefined) {
      const lat = prepareLateralTargets({
        comX: opts.comX,
        stanceFootX: opts.stanceFootX,
      });
      stanceHipLat = lat.stanceHipLat;
      swingHipLat = lat.swingHipLat;
      spineLat = lat.spineLat;
    } else {
      // Open-loop fallback: flipped vs cp15 (measured).
      stanceHipLat = -stanceDirX * 0.14;
      swingHipLat = stanceDirX * 0.1;
      spineLat = stanceDirX * 0.12;
    }
  } else {
    stanceHipLat = stanceHipLat ?? 0;
    spineLat = spineLat ?? 0;
  }
  const stanceHipSag = opts.stanceHipSag ?? 0.1;
  const spineSag = opts.spineSag ?? 0.04;

  const shT = (config.baseMaxTorqueNm[hipStance] ?? 1000) * stanceCap * 1.5;
  const skT = (config.baseMaxTorqueNm[kneeStance] ?? 250) * stanceCap * 1.5;
  const saT = (config.baseMaxTorqueNm[ankleStance] ?? 70) * stanceCap * 1.4;
  setJointCmd(commands, hipStance, stanceHipSag, stanceHipLat ?? 0, shT, 1);
  setJointCmd(commands, kneeStance, 0.12, 0, skT, 1);
  setJointCmd(commands, ankleStance, 0.08, 0, saT, 1); // plant

  // Soft unload on swing (still planted during prepare — no knee tuck).
  const swT = (config.baseMaxTorqueNm[hipSwing] ?? 1000) * swingCap * 0.85;
  setJointCmd(commands, hipSwing, 0.02, swingHipLat, swT, 0.7);
  setJointCmd(commands, kneeSwing, 0.05, 0, (config.baseMaxTorqueNm[kneeSwing] ?? 250) * swingCap * 0.7, 0.6);
  setJointCmd(commands, ankleSwing, 0.02, 0, (config.baseMaxTorqueNm[ankleSwing] ?? 70) * swingCap * 0.7, 0.6);

  const spineCap = commands.spine?.capacity ?? clamp01(config.strength);
  setJointCmd(
    commands,
    'spine',
    spineSag,
    spineLat ?? 0,
    (config.baseMaxTorqueNm.spine ?? 700) * spineCap * 1.3,
    0.95,
  );
}

// ---------------------------------------------------------------------------
// P1A place: lateral capture-point replant (not Gate A step)
// ---------------------------------------------------------------------------

/** Max |targetX − original swing plant X| during P1A place capture replant (m). */
export const P1A_PLACE_CAPTURE_REACH_M = 0.13998360320645176 as const; // search v6 idx183
/** Min |targetX − stance plant X| so place does not cross legs (m). */
export const P1A_PLACE_CAPTURE_MIN_SEP_M = 0.1 as const;
/** COM height floor for place capture ω = √(g / max(floor, comY)) (m). */
export const P1A_PLACE_CAPTURE_COM_Y_FLOOR_M = 0.5 as const;
/**
 * Freeze the place capture target once the swing foot is this close above the
 * latched plant Y (final approach). Absolute world-Y near-ground (0.08 m) never
 * opens on the P1A ~4 cm lift apex, so freeze is plant-relative.
 */
export const P1A_PLACE_CAPTURE_FREEZE_ABOVE_PLANT_M = 0.025214004770127647 as const; // search v6 idx183

/**
 * Pure P1A place-phase lateral capture-point target (world X only).
 *
 * ```text
 * omega    = sqrt(g / max(comYFloor, comY))
 * captureX = comX + comVX / omega
 * targetX  = clamp(captureX, originalX ± reach)
 * targetX  = keep ≥ minSep lateral separation from stance plant (no crossing)
 * ```
 *
 * Swing side is inferred from originalX vs stancePlantX (original side of stance).
 */
export function p1aPlaceLateralCaptureTargetX(args: {
  comX: number;
  comY: number;
  comVX: number;
  originalX: number;
  stancePlantX: number;
  reachBoundM?: number;
  minSeparationM?: number;
  comYFloorM?: number;
  gravityMps2?: number;
}): number {
  const g = args.gravityMps2 ?? CAPTURE_GRAVITY_MPS2;
  const yFloor = args.comYFloorM ?? P1A_PLACE_CAPTURE_COM_Y_FLOOR_M;
  const reach = args.reachBoundM ?? P1A_PLACE_CAPTURE_REACH_M;
  const minSep = args.minSeparationM ?? P1A_PLACE_CAPTURE_MIN_SEP_M;
  const omega = Math.sqrt(g / Math.max(yFloor, args.comY));
  const captureX = args.comX + args.comVX / omega;
  let targetX = clamp(captureX, args.originalX - reach, args.originalX + reach);
  // No leg crossing: keep the swing on its original side of the stance plant.
  if (args.originalX >= args.stancePlantX) {
    targetX = Math.max(targetX, args.stancePlantX + minSep);
  } else {
    targetX = Math.min(targetX, args.stancePlantX - minSep);
  }
  return targetX;
}

/**
 * Whether the P1A place path may still update the latched lateral capture target.
 * Gate A / non-place / already-frozen / within freeze-above-plant band → false.
 *
 * Freeze is plant-relative (`swingFootYM - plantYM`), not absolute world-Y
 * near-ground: P1A lift apex (~4 cm) never clears CAPTURE_FOOT_NEAR_GROUND (8 cm).
 */
export function p1aPlaceCaptureTargetMayUpdate(args: {
  singleSupportHoldActive: boolean;
  stepPhase: MuscleStepPhase;
  captureTargetFrozen: boolean;
  swingFootYM: number;
  /** Latched place plant world Y (freeze measured above this). */
  plantYM: number;
  freezeAbovePlantM?: number;
}): boolean {
  if (!args.singleSupportHoldActive) return false;
  if (args.stepPhase !== 'place') return false;
  if (args.captureTargetFrozen) return false;
  const freezeH = args.freezeAbovePlantM ?? P1A_PLACE_CAPTURE_FREEZE_ABOVE_PLANT_M;
  if (args.swingFootYM - args.plantYM <= freezeH) return false;
  return true;
}

// ---------------------------------------------------------------------------
// P1A place: closed-loop touchdown-velocity nulling (Gate A push 18)
// ---------------------------------------------------------------------------

/**
 * Place-phase swing-foot velocity-null gain (s):
 * damp target = foot − gain × V_measured (signed world velocity).
 * Push 15 open-loop used max(0,Vz) + asymmetric clamp → rear-slide overshoot.
 * Push 18 closes the loop: drive |V| → 0 in the pre-touchdown band.
 */
export const P1A_PLACE_SWING_RETRACT_GAIN_S = 0.06 as const;
/**
 * Production scale on place swing velocity nulling. **0 = off** (production-safe;
 * empty style / P1A identity unchanged). Forward search opt-in sets >0.
 * Forward-only (zero-forward identity preserved by the pure helper).
 */
export const P1A_PLACE_SWING_RETRACT_SCALE = 0 as const;
/**
 * Max |goal − plant| so velocity nulling cannot invert the step or yank past
 * the plant by more than a foot-length-scale margin. Symmetric (push 18): the
 * old plant+0.02 forward cap blocked rear-velocity correction.
 */
export const P1A_PLACE_SWING_RETRACT_BIAS_MAX_M = 0.12 as const;

/**
 * Pure place-phase closed-loop ground-speed match for one world axis (m).
 *
 * Once the swing foot is in the pre-touchdown freeze band, blend the axis
 * target from the latched plant toward a **signed** velocity-damped target so
 * measured world-frame horizontal foot velocity approaches 0 at first contact:
 *
 * ```text
 * bandWeight ∈ [0,1]  (0 above freezeH; 1 at/below plant)
 * damp       = foot  − gainS × footV     // signed: +V → goal behind foot;
 *                                         //          −V → goal ahead of foot
 * goal       = (1 − w) × plant + w × damp
 * w          = clamp(scale,0,1) × bandWeight
 * goal       ∈ [plant − maxBias, plant + maxBias]
 * ```
 *
 * Indexed only on measured foot height / foot position / foot velocity
 * (+ commanded forward); **no timers**. Returns `plantWorldM` when commanded
 * forward is 0, scale 0, or outside the freeze band (P1A identity + production
 * default off).
 *
 * Axis-agnostic: call with Z (sagittal) and optionally X (lateral).
 */
export function p1aPlaceSwingGroundSpeedMatchZM(args: {
  commandedForwardM: number;
  plantWorldZM: number;
  swingFootZM: number;
  footHeightAbovePlantM: number;
  freezeAbovePlantM: number;
  swingFootVzMps: number;
  /** Gain (s); default {@link P1A_PLACE_SWING_RETRACT_GAIN_S}. */
  retractGainS?: number;
  /** Scale [0, 2]; default {@link P1A_PLACE_SWING_RETRACT_SCALE} (0 = off). */
  scale?: number;
  /** Optional |goal−plant| clamp (m); default {@link P1A_PLACE_SWING_RETRACT_BIAS_MAX_M}. */
  biasMaxM?: number;
}): number {
  const plantZ =
    typeof args.plantWorldZM === 'number' && Number.isFinite(args.plantWorldZM)
      ? args.plantWorldZM
      : 0;
  if (
    !(
      typeof args.commandedForwardM === 'number' &&
      Number.isFinite(args.commandedForwardM) &&
      args.commandedForwardM > 1e-4
    )
  ) {
    return plantZ;
  }
  const scale =
    typeof args.scale === 'number' && Number.isFinite(args.scale)
      ? clamp(args.scale, 0, 2)
      : P1A_PLACE_SWING_RETRACT_SCALE;
  if (!(scale > 0)) return plantZ;
  const gain =
    typeof args.retractGainS === 'number' && Number.isFinite(args.retractGainS)
      ? Math.max(0, args.retractGainS)
      : P1A_PLACE_SWING_RETRACT_GAIN_S;
  const freezeH = Math.max(
    typeof args.freezeAbovePlantM === 'number' && Number.isFinite(args.freezeAbovePlantM)
      ? args.freezeAbovePlantM
      : P1A_PLACE_CAPTURE_FREEZE_ABOVE_PLANT_M,
    1e-4,
  );
  const h = Number.isFinite(args.footHeightAbovePlantM)
    ? args.footHeightAbovePlantM
    : freezeH + 1;
  if (h > freezeH) return plantZ;
  const bandWeight = h <= 0 ? 1 : 1 - h / freezeH;
  const w = clamp(scale, 0, 1) * bandWeight;
  if (w < 1e-6) return plantZ;
  const footZ =
    typeof args.swingFootZM === 'number' && Number.isFinite(args.swingFootZM)
      ? args.swingFootZM
      : plantZ;
  const footVz =
    typeof args.swingFootVzMps === 'number' && Number.isFinite(args.swingFootVzMps)
      ? args.swingFootVzMps
      : 0;
  // Closed-loop: full signed velocity (push 18). Open-loop max(0,Vz) overshot rear.
  const dampZ = footZ - gain * footVz;
  // Blend plant approach → velocity-nulled damp target.
  let goalZ = (1 - w) * plantZ + w * dampZ;
  // Symmetric band around plant so rear-velocity (+ahead goal) can correct.
  const maxBias =
    typeof args.biasMaxM === 'number' && Number.isFinite(args.biasMaxM)
      ? Math.max(0, args.biasMaxM)
      : P1A_PLACE_SWING_RETRACT_BIAS_MAX_M;
  goalZ = clamp(goalZ, plantZ - maxBias, plantZ + maxBias);
  return goalZ;
}

/**
 * Diagnostic: signed plant − goal (positive = goal behind plant = rear bias).
 * Closed-loop can return negative when correcting rear-slide (goal ahead).
 */
export function p1aPlaceSwingRetractionBiasM(args: {
  commandedForwardM: number;
  footHeightAbovePlantM: number;
  freezeAbovePlantM: number;
  swingFootVzMps: number;
  swingFootZM?: number;
  plantWorldZM?: number;
  retractGainS?: number;
  scale?: number;
  biasMaxM?: number;
}): number {
  const plantZ =
    typeof args.plantWorldZM === 'number' && Number.isFinite(args.plantWorldZM)
      ? args.plantWorldZM
      : 0;
  const footZ =
    typeof args.swingFootZM === 'number' && Number.isFinite(args.swingFootZM)
      ? args.swingFootZM
      : plantZ;
  const goalZ = p1aPlaceSwingGroundSpeedMatchZM({
    commandedForwardM: args.commandedForwardM,
    plantWorldZM: plantZ,
    swingFootZM: footZ,
    footHeightAbovePlantM: args.footHeightAbovePlantM,
    freezeAbovePlantM: args.freezeAbovePlantM,
    swingFootVzMps: args.swingFootVzMps,
    retractGainS: args.retractGainS,
    scale: args.scale,
    biasMaxM: args.biasMaxM,
  });
  return plantZ - goalZ;
}

// ---------------------------------------------------------------------------
// P1A place: sole-pitch flatten (Gate A push 19 foot-slap)
// ---------------------------------------------------------------------------

/**
 * Sole pitch about world +X from foot local forward (toe = local +Z).
 * pitch = atan2(forwardY, forwardZ): 0 = flat, + = toe up, − = toe down.
 */
export function footSolePitchRadFromForward(forwardY: number, forwardZ: number): number {
  if (!Number.isFinite(forwardY) || !Number.isFinite(forwardZ)) return 0;
  return Math.atan2(forwardY, forwardZ);
}

/**
 * Production scale on place/hold sole-flatten. **0 = off** (production-safe;
 * empty style / P1A identity unchanged). Forward search opt-in sets >0.
 */
export const P1A_PLACE_SOLE_FLATTEN_SCALE = 0 as const;
/**
 * Default pitch→ankle gain (rad ankle / rad pitch). +pitch (toe up) →
 * +ankle (plantarflex) lowers toes toward flat sole.
 */
export const P1A_PLACE_SOLE_FLATTEN_GAIN = 1.0 as const;
/** Max |ankle delta| from sole-flatten (rad); keeps within swing ankle band. */
export const P1A_PLACE_SOLE_FLATTEN_MAX_RAD = 0.18 as const;

/**
 * Pure pre-touchdown sole-pitch flatten ankle delta (rad).
 *
 * Spec §8.6: near-flat foot pitch before expected touchdown. Push 18 nulled
 * approach Vz but residual −1.3 m/s rebound + skate is hypothesized FOOT SLAP:
 * sole lands pitched (edge contact), then rotates flat about the contact edge
 * and sweeps the heel-biased anatomical sole backward.
 *
 * ```text
 * bandWeight ∈ [0,1]  (0 above freezeH; 1 at/below plant)
 * w          = clamp(scale,0,1) × bandWeight
 * delta      = w × gain × footPitch   // +pitch → +plantarflex
 * delta      ∈ [−maxRad, +maxRad]
 * ```
 *
 * Indexed only on measured foot height / measured sole pitch (+ commanded
 * forward); **no timers**. Returns 0 when commanded forward is 0, scale 0,
 * or outside the freeze band (P1A identity + production default off).
 */
export function p1aPlaceSwingSoleFlattenAnkleDeltaRad(args: {
  commandedForwardM: number;
  footHeightAbovePlantM: number;
  freezeAbovePlantM: number;
  /** Measured sole pitch (rad); + = toe up. */
  footPitchRad: number;
  /** Gain (rad ankle / rad pitch); default {@link P1A_PLACE_SOLE_FLATTEN_GAIN}. */
  gain?: number;
  /** Scale [0, 2]; default {@link P1A_PLACE_SOLE_FLATTEN_SCALE} (0 = off). */
  scale?: number;
  /** |delta| clamp (rad); default {@link P1A_PLACE_SOLE_FLATTEN_MAX_RAD}. */
  maxRad?: number;
}): number {
  if (
    !(
      typeof args.commandedForwardM === 'number' &&
      Number.isFinite(args.commandedForwardM) &&
      args.commandedForwardM > 1e-4
    )
  ) {
    return 0;
  }
  const scale =
    typeof args.scale === 'number' && Number.isFinite(args.scale)
      ? clamp(args.scale, 0, 2)
      : P1A_PLACE_SOLE_FLATTEN_SCALE;
  if (!(scale > 0)) return 0;
  const gain =
    typeof args.gain === 'number' && Number.isFinite(args.gain)
      ? Math.max(0, args.gain)
      : P1A_PLACE_SOLE_FLATTEN_GAIN;
  const freezeH = Math.max(
    typeof args.freezeAbovePlantM === 'number' && Number.isFinite(args.freezeAbovePlantM)
      ? args.freezeAbovePlantM
      : P1A_PLACE_CAPTURE_FREEZE_ABOVE_PLANT_M,
    1e-4,
  );
  const h = Number.isFinite(args.footHeightAbovePlantM)
    ? args.footHeightAbovePlantM
    : freezeH + 1;
  if (h > freezeH) return 0;
  const bandWeight = h <= 0 ? 1 : 1 - h / freezeH;
  const w = clamp(scale, 0, 1) * bandWeight;
  if (w < 1e-6) return 0;
  const pitch =
    typeof args.footPitchRad === 'number' && Number.isFinite(args.footPitchRad)
      ? args.footPitchRad
      : 0;
  // +pitch (toe up) → +ankle plantarflex lowers toes toward flat sole.
  const raw = w * gain * pitch;
  const maxRad =
    typeof args.maxRad === 'number' && Number.isFinite(args.maxRad)
      ? Math.max(0, args.maxRad)
      : P1A_PLACE_SOLE_FLATTEN_MAX_RAD;
  return clamp(raw, -maxRad, maxRad);
}

/**
 * Swing knee sagittal floor (rad) so vertical-shortening can clear the
 * millimeter under P1B sole-clearance.
 *
 * In-place P1A keeps the certified −0.55 clamp. Under −0.55, two-link geometry
 * saturates near h≈0.036 m center (oriented sole often 0.029–0.034). Forward
 * SS opens a *small* extra flex band so center can reach ~0.042 m (sole ≥0.032
 * with margin). Deep floors (≤−0.75) collapse upright on this mass model —
 * measured push-15; hard floor stays at −0.62.
 *
 * `liftClearanceHM` is accepted for API symmetry with the command path but the
 * forward floor is a fixed small opening (not full-command tracking).
 */
export function p1aSwingKneeSagMinRad(args: {
  commandedForwardM: number;
  /** Reserved (actual liftHM); forward floor is a fixed small opening. */
  liftClearanceHM: number;
  /** In-place floor (rad); default −0.55. */
  inplaceMinRad?: number;
  /**
   * Forward knee floor (rad). Default {@link P1A_FORWARD_SWING_KNEE_SAG_MIN_RAD}.
   * Must stay ≥ hardMin; more negative = more flex.
   */
  forwardMinRad?: number;
  /** Absolute most-negative safety floor (rad); default −0.62. */
  hardMinRad?: number;
}): number {
  const inplace =
    typeof args.inplaceMinRad === 'number' && Number.isFinite(args.inplaceMinRad)
      ? args.inplaceMinRad
      : -0.55;
  if (
    !(
      typeof args.commandedForwardM === 'number' &&
      Number.isFinite(args.commandedForwardM) &&
      args.commandedForwardM > 1e-4
    )
  ) {
    return inplace;
  }
  const hardMin =
    typeof args.hardMinRad === 'number' && Number.isFinite(args.hardMinRad)
      ? args.hardMinRad
      : -0.62;
  const forwardMin =
    typeof args.forwardMinRad === 'number' && Number.isFinite(args.forwardMinRad)
      ? args.forwardMinRad
      : P1A_FORWARD_SWING_KNEE_SAG_MIN_RAD;
  // Never less flexed than inplace; never past hard safety floor.
  return clamp(Math.min(inplace, forwardMin), hardMin, inplace);
}

// Note: resolveP1AForwardSwingKneeSagMinRad is the knob path; pure helper above
// is used when the caller already resolved the floor.

/**
 * Forward SS swing knee sagittal floor (rad). −0.55 → geometric peak center
 * ≈0.036 m / sole often 0.029; −0.60 opens ~0.042 m center for sole ≥0.032.
 * Measured: deeper than −0.62 collapses upright on cand-201 / production.
 */
/**
 * Default forward knee floor (rad) = in-place −0.55. Openings past ~−0.56
 * collapse cand-201 upright unless co-tuned (search knob
 * `forwardSwingKneeSagMinRad`).
 */
export const P1A_FORWARD_SWING_KNEE_SAG_MIN_RAD = -0.55 as const;

// ---------------------------------------------------------------------------
// P1A load-only: in-place contact-driven load acceptance (not Gate A step)
// ---------------------------------------------------------------------------

/**
 * Pure P1A in-place load corrections from measured COM / contact / load.
 * Symmetric about the two fixed plants — no forward-step rear/front roles.
 * Velocity terms damp existing COM motion only (zero when vel = 0).
 */
export function p1aInPlaceLoadAcceptancePolicy(args: {
  comX: number;
  comY: number;
  comZ: number;
  comVelX: number;
  comVelZ: number;
  midPlantX: number;
  midPlantZ: number;
  pelvisY: number;
  /** New (just-landed) foot load fraction in [0, 1]; 0 if unmeasured. */
  newFootLoadFraction: number;
  hasLoadMeasurement: boolean;
  newFootImpulseBearing: boolean;
  stanceFootImpulseBearing: boolean;
  bothGrounded: boolean;
}): {
  /** Ankle sagittal overlay (same both legs): pos + vel damping. */
  ankleSagOverlay: number;
  /**
   * Left/right ankle roll from lateral COM error.
   * Same world-sign law on both ankles (FK: lat+ leans toward world +X on both).
   */
  ankleLatLeft: number;
  ankleLatRight: number;
  /** Lateral hip overlay: right +, left − for COM right of mid (current rig). */
  hipLatOverlayLeft: number;
  hipLatOverlayRight: number;
  spineSag: number;
  spineLat: number;
  /** Extra plant gain (rad/m) on the new foot when under-loaded / re-seeking contact. */
  newFootPlantGainBoost: number;
  /** Mild bilateral knee soften for touchdown compliance (rad, negative = flex). */
  kneeComplianceRad: number;
  /** Mild bilateral knee extension when pelvis is low (rad, positive = extend). */
  heightRecoverKneeRad: number;
} {
  const errX = args.comX - args.midPlantX;
  const errZ = args.comZ - args.midPlantZ;
  // Capture-point-ish sagittal for upright recovery over mid support.
  const height = Math.max(CAPTURE_MIN_COM_HEIGHT_M, args.comY - 0.03);
  const omega = Math.sqrt(CAPTURE_GRAVITY_MPS2 / height);
  const captureErrZ = errZ + args.comVelZ / omega;

  // Ankle strategy: +errZ (COM forward) → +plantarflex; +velZ → same-sign damp.
  const ankleSagOverlay = clamp(
    0.85 * captureErrZ + 0.22 * args.comVelZ,
    -0.14,
    0.12,
  );
  // Lateral ankle roll from raw COM vs mid-plant; anatomical clamp ±0.20.
  // FK: hip/ankle lat+ both lean toward world +X, so both ankles share the same
  // world-sign correction (−errX), not mirrored joint signs. Position-only.
  const ankleLatRight = clamp(-errX * 1.2, -0.2, 0.2);
  const ankleLatLeft = clamp(-errX * 1.2, -0.2, 0.2);
  // Hip lateral from raw COM error: +errX → right + / left −.
  const hipLatOverlayLeft = clamp(-errX * 1.4, -0.28, 0.28);
  const hipLatOverlayRight = clamp(errX * 1.4, -0.28, 0.28);

  // Spine: upright toward mid plant; damp sagittal velocity (no open-loop lean).
  // Rig convention: +X = extension (pushes COM aft), −X = flexion. Match
  // sphericalSagittalFromErrorZ polarity (+captureErrZ → +spine = extend).
  const spineSag = clamp(
    0.9 * captureErrZ + 0.12 * args.comVelZ,
    -0.22,
    0.18,
  );
  // Lateral spine from position error + fitted lateral velocity damp.
  const spineLat = clamp(
    -errX * 1.1 - 0.08 * args.comVelX,
    -0.28,
    0.28,
  );

  // Contact/load-driven plant boost on the new foot only.
  let newFootPlantGainBoost = 0;
  if (!args.newFootImpulseBearing) {
    // Lost or never held impulse: re-seek plant without moving the world target.
    newFootPlantGainBoost = 1.6;
  } else if (args.hasLoadMeasurement && args.newFootLoadFraction < 0.35) {
    // Under-loaded vs in-place share: press harder into the latched plant.
    newFootPlantGainBoost = 1.2 * (0.35 - args.newFootLoadFraction) / 0.35;
  } else if (args.hasLoadMeasurement && args.newFootLoadFraction < 0.45) {
    newFootPlantGainBoost = 0.4 * (0.45 - args.newFootLoadFraction) / 0.45;
  }

  // Touchdown compliance when both down and new foot just taking load.
  let kneeComplianceRad = 0;
  if (
    args.bothGrounded &&
    args.newFootImpulseBearing &&
    args.hasLoadMeasurement &&
    args.newFootLoadFraction < 0.4
  ) {
    kneeComplianceRad = -0.06 * (1 - args.newFootLoadFraction / 0.4);
  }

  // Height recover only when double-support and low pelvis — joint motors only.
  let heightRecoverKneeRad = 0;
  if (args.bothGrounded && args.pelvisY < 0.95) {
    heightRecoverKneeRad = clamp((0.95 - args.pelvisY) * 0.35, 0, 0.1);
  }

  return {
    ankleSagOverlay,
    ankleLatLeft,
    ankleLatRight,
    hipLatOverlayLeft,
    hipLatOverlayRight,
    spineSag,
    spineLat,
    newFootPlantGainBoost,
    kneeComplianceRad,
    heightRecoverKneeRad,
  };
}

/**
 * Balanced new-foot load share at which measured-load contact motor authority
 * reaches full (1). Matches the existing plant-policy balanced threshold.
 */
export const P1A_CONTACT_MOTOR_AUTHORITY_LOAD_FRACTION = 0.45;

/**
 * Final anatomical clamp on P1A load-acceptance hip laterals (both legs).
 * Shared by stance-column and new-leg residual construction.
 */
export const P1A_LOAD_HIP_LAT_CLAMP_RAD = 0.45 as const;

/**
 * Forward-plant load sagittal residual gains (rad/m of plant−foot Z).
 * FK: sag− → +Z, so residual hip sag = −gain · footErrZ.
 * Lower than plant IK plantGain (2.8–3.4) — residual hold only, not IK magnitude.
 */
export const P1A_FORWARD_LOAD_STANCE_SAG_RESIDUAL_GAIN_PER_M = 1.2 as const;
/** Higher new-foot residual to cut post-touchdown skate (P1C slip ≤0.04). */
export const P1A_FORWARD_LOAD_NEW_SAG_RESIDUAL_GAIN_PER_M = 1.6 as const;
/** COM capture → rear-leg extension gain (rad per m of captureErrZ behind mid). */
export const P1A_FORWARD_LOAD_REAR_EXTEND_GAIN_PER_M = 0.55 as const;
/** Baseline rear hip extension when double-support (rad, positive = extend). */
export const P1A_FORWARD_LOAD_REAR_EXTEND_BASE_RAD = 0.08 as const;

/**
 * P1A new-leg hip lateral during in-place load acceptance / place contact-candidate.
 *
 * Stage-1 swing-hip sat attribution (`artifacts/humanoid-p1-swing-hip-sat-attribution.json`):
 * continuous saturation is plant-IK magnitude under contact (not rate jumps): load
 * frames show |plantHipLat| ~0.35–0.55 dominating the issued target while the
 * COM overlay is small. The analytical `solveTwoLinkLegIk().hipLateralRad`
 * baseline is therefore omitted for the new leg only; construction is the
 * measured fixed-world foot-error residual plus the existing load-policy overlay
 * (same class of fix as stance-column composition for the stance hip).
 *
 * ```text
 * hipLat = clamp(plantGain * (footTarget.x − foot.x) + overlay, ±clamp)
 * ```
 *
 * Stance hip continues to use {@link P1A_STANCE_COLUMN_LAT_GAIN_PER_M}. Sagittal
 * plant IK is unchanged on the in-place path; forward plant uses
 * {@link p1aForwardPlantLoadSagittalComposition}. Not a motor-speed cap, latch,
 * or torque scale.
 */
export function p1aNewLegHipLatFromFootError(args: {
  plantGain: number;
  /** World X: fixed plant target − live foot (m). */
  footErrXM: number;
  /** Load-policy COM lateral overlay for this leg (rad). */
  overlayRad: number;
  maxAbsRad?: number;
}): number {
  const maxAbs = args.maxAbsRad ?? P1A_LOAD_HIP_LAT_CLAMP_RAD;
  return clamp(
    args.plantGain * args.footErrXM + args.overlayRad,
    -maxAbs,
    maxAbs,
  );
}

/**
 * Forward-plant (staggered front/rear) load-acceptance sagittal composition.
 *
 * Hybrid (push-5 Stage 1 evidence): keep a damped plant-IK sagittal seed so the
 * new sole does not bounce (pure residual caused a second contact cycle), then
 * add residual foot hold + rear-extension so COM settles toward mid-plant
 * rather than skating both feet under full plant-IK magnitude.
 *
 * **Default (push-7 identity — evaluation knobs absent):**
 * - COM capture target = geometric mid (`midPlantZ`; no `supportPlantZ`)
 * - capture vel term full (`comVelZ / ω`); spine `0.65·cap + 0.1·vel`
 * - new knee clamp [−0.3, 0.1]; no absorb flex
 *
 * **Opt-in (push 12 Part B, evaluation knobs only):**
 * - Pass `supportPlantZ` = new plant Z → settle to new support, milder capture
 *   vel (0.35×) and spine (0.55 / 0.05) so momentum can ride forward.
 * - Pass `momentumAbsorbKneeFlexRad` from {@link p1aMomentumAbsorbKneeFlexRad}
 *   → load-fraction flex/extend on the new knee (wider clamp to −0.45).
 *
 * Pure; only used when commandedForward > 0 and stepPhase === 'load'.
 */
export function p1aForwardPlantLoadSagittalComposition(args: {
  comZ: number;
  comY: number;
  comVelZ: number;
  midPlantZ: number;
  /**
   * Sagittal COM settle plant Z (m). Opt-in new-support settle (push 12b knob).
   * Absent → midPlantZ (push-7 mid-stop).
   */
  supportPlantZ?: number;
  /** Plant-IK hip sagittal (rad) for stance / new — damped seed only. */
  stancePlantHipSagRad: number;
  newPlantHipSagRad: number;
  stancePlantKneeRad: number;
  newPlantKneeRad: number;
  stancePlantAnkleRad: number;
  newPlantAnkleRad: number;
  /** World plant Z − live foot Z (m). */
  stanceFootErrZM: number;
  newFootErrZM: number;
  bothGrounded: boolean;
  ankleSagOverlay: number;
  kneeComplianceRad: number;
  heightRecoverKneeRad: number;
  /**
   * Extra new-knee flex (rad, negative = flex) from momentum-absorb profile.
   * Opt-in via evaluation knob; absent / 0 → push-7 knee law.
   */
  momentumAbsorbKneeFlexRad?: number;
  stanceHipOff: number;
  newHipOff: number;
  stanceKneeOff: number;
  newKneeOff: number;
  stanceAnkleOff: number;
  newAnkleOff: number;
}): {
  stanceHipSag: number;
  newHipSag: number;
  stanceKnee: number;
  newKnee: number;
  stanceAnkleSag: number;
  newAnkleSag: number;
  spineSag: number;
  comErrZ: number;
  captureErrZ: number;
  rearExtendRad: number;
} {
  const settleToNew =
    typeof args.supportPlantZ === 'number' && Number.isFinite(args.supportPlantZ);
  const settleZ = settleToNew ? (args.supportPlantZ as number) : args.midPlantZ;
  const comErrZ = args.comZ - settleZ;
  const height = Math.max(CAPTURE_MIN_COM_HEIGHT_M, args.comY - 0.03);
  const omega = Math.sqrt(CAPTURE_GRAVITY_MPS2 / height);
  // Push-7: full capture-point vel stop at mid. New-support opt-in: mild vel
  // so momentum can ride past the old midpoint toward the new plant.
  const captureVelScale = settleToNew ? 0.35 : 1;
  const captureErrZ = comErrZ + captureVelScale * (args.comVelZ / omega);

  const residualStance = clamp(
    -P1A_FORWARD_LOAD_STANCE_SAG_RESIDUAL_GAIN_PER_M * args.stanceFootErrZM,
    -0.1,
    0.1,
  );
  const residualNew = clamp(
    -P1A_FORWARD_LOAD_NEW_SAG_RESIDUAL_GAIN_PER_M * args.newFootErrZM,
    -0.14,
    0.14,
  );

  const rearExtend = args.bothGrounded
    ? clamp(
        P1A_FORWARD_LOAD_REAR_EXTEND_BASE_RAD +
          P1A_FORWARD_LOAD_REAR_EXTEND_GAIN_PER_M * (-captureErrZ),
        0.05,
        0.2,
      )
    : P1A_FORWARD_LOAD_REAR_EXTEND_BASE_RAD;

  // Damped plant-IK seed + residual + rear extend (stance) / accept (new).
  // Holds upright through 2 s load (tilt ~8°, height ~1.01) under push-7 law.
  const stanceHipSag = clamp(
    0.35 * args.stancePlantHipSagRad +
      residualStance +
      rearExtend +
      0.1 * args.ankleSagOverlay +
      args.stanceHipOff,
    -0.4,
    0.28,
  );
  const newHipSag = clamp(
    0.45 * args.newPlantHipSagRad +
      residualNew +
      0.08 * args.ankleSagOverlay +
      args.newHipOff,
    -0.4,
    0.18,
  );

  const kneeSoft = args.kneeComplianceRad + args.heightRecoverKneeRad;
  const absorbFlex =
    typeof args.momentumAbsorbKneeFlexRad === 'number' &&
    Number.isFinite(args.momentumAbsorbKneeFlexRad)
      ? args.momentumAbsorbKneeFlexRad
      : 0;
  const stanceKnee = clamp(
    0.4 * args.stancePlantKneeRad +
      0.05 +
      0.5 * args.heightRecoverKneeRad +
      args.stanceKneeOff,
    -0.25,
    0.14,
  );
  // Push-7 knee clamp [-0.3, 0.1]; widen when absorb flex is active.
  const newKneeMin = absorbFlex !== 0 ? -0.45 : -0.3;
  const newKnee = clamp(
    0.5 * args.newPlantKneeRad + kneeSoft + absorbFlex + args.newKneeOff,
    newKneeMin,
    0.1,
  );

  const stanceAnkleSag = clamp(
    0.5 * args.stancePlantAnkleRad +
      0.85 * args.ankleSagOverlay +
      args.stanceAnkleOff,
    -0.14,
    0.1,
  );
  const newAnkleSag = clamp(
    0.5 * args.newPlantAnkleRad +
      0.85 * args.ankleSagOverlay +
      args.newAnkleOff,
    -0.14,
    0.1,
  );

  // Push-7 spine; milder coefficients only with new-support settle opt-in.
  const spineSag = settleToNew
    ? clamp(0.55 * captureErrZ + 0.05 * args.comVelZ, -0.16, 0.14)
    : clamp(0.65 * captureErrZ + 0.1 * args.comVelZ, -0.16, 0.14);

  return {
    stanceHipSag,
    newHipSag,
    stanceKnee,
    newKnee,
    stanceAnkleSag,
    newAnkleSag,
    spineSag,
    comErrZ,
    captureErrZ,
    rearExtendRad: rearExtend,
  };
}

/**
 * Pure measured-load contact motor authority for the P1A new leg.
 *
 * ```text
 * authority = 0
 *   when the new foot has no real impulse-bearing contact or no valid load
 * authority = clamp(newFootLoadFraction / 0.45, 0, 1)
 *   otherwise
 * ```
 *
 * Non-finite load fractions reject to zero. No floor, curve, state, ramp,
 * timer, smoothing, gain, or configuration seam.
 *
 * Forward double-support reuses this as the **desired load-share** schedule
 * ({@link p1aForwardCompliantDoubleSupportActive}): new-leg maxTorque and soft
 * plant track both ramp with the same 0.45 balanced threshold.
 */
export function p1aMeasuredLoadContactMotorAuthority(args: {
  newFootImpulseBearing: boolean;
  hasLoadMeasurement: boolean;
  newFootLoadFraction: number;
}): number {
  if (!args.newFootImpulseBearing || !args.hasLoadMeasurement) return 0;
  if (!Number.isFinite(args.newFootLoadFraction)) return 0;
  return clamp(
    args.newFootLoadFraction / P1A_CONTACT_MOTOR_AUTHORITY_LOAD_FRACTION,
    0,
    1,
  );
}

/**
 * Gate A push-6: compliant double-support load share is active only for a
 * commanded forward step. Zero-forward P1A identity is preserved exactly when
 * this returns false (no soft-track / no extra impedance scaling).
 *
 * Applies during place contact-candidate and load (both use
 * {@link applyP1AInPlaceLoadAcceptanceCommands}); phase timers already keep
 * forward load for the 2 s settle window.
 */
export function p1aForwardCompliantDoubleSupportActive(args: {
  commandedStepForwardM: number;
}): boolean {
  return (
    typeof args.commandedStepForwardM === 'number' &&
    Number.isFinite(args.commandedStepForwardM) &&
    args.commandedStepForwardM > 1e-4
  );
}

/**
 * Soft-track a joint from measured pose toward the plant pose.
 *
 * ```text
 * δ = clamp(plant − measured, ±maxDelta)
 * target = measured + α · δ
 *   α = load-share soft-track weight ∈ [0, 1]
 * ```
 *
 * Pure; no state. Caps plant error so dual-support closed-loop wrench stays
 * bounded even when α→1 (stance remains the stiff column).
 */
export function p1aCompliantSoftTrackTarget(
  measuredRad: number,
  plantRad: number,
  authority01: number,
  maxDeltaRad: number = P1A_FORWARD_COMPLIANT_MAX_PLANT_DELTA_RAD,
): number {
  const a = clamp(
    Number.isFinite(authority01) ? authority01 : 0,
    0,
    1,
  );
  if (!Number.isFinite(measuredRad) && !Number.isFinite(plantRad)) return 0;
  if (!Number.isFinite(measuredRad)) return plantRad;
  if (!Number.isFinite(plantRad)) return measuredRad;
  const maxD = Math.max(0, maxDeltaRad);
  const delta = clamp(plantRad - measuredRad, -maxD, maxD);
  return measuredRad + a * delta;
}

/**
 * Soft-track weight from measured-load authority (impedance spring gain).
 *
 * ```text
 * α = floor + span · authority    ∈ [floor, floor+span] ⊆ [0, 1]
 * ```
 *
 * Deliberately low ceiling: the new leg is a compliant load acceptor, not a
 * second stiff plant column. Stance remains the position support.
 */
export function p1aCompliantSoftTrackWeight(
  authority01: number,
  floor: number = P1A_FORWARD_COMPLIANT_SOFT_TRACK_FLOOR,
  span: number = P1A_FORWARD_COMPLIANT_SOFT_TRACK_SPAN,
): number {
  const a = clamp(Number.isFinite(authority01) ? authority01 : 0, 0, 1);
  const f = clamp(Number.isFinite(floor) ? floor : 0, 0, 1);
  const s = clamp(Number.isFinite(span) ? span : 0, 0, 1 - f);
  return f + s * a;
}

/**
 * Forward DS impedance: new-leg maxTorque scale relative to the existing
 * load-acceptance formula (before measured-load authority). Stance keeps 1.
 * Keep near 1 — Stage-1 fight is lateral target conflict, not torque budget.
 * (No forward stiffening boost; scale replaces that path.)
 */
export const P1A_FORWARD_COMPLIANT_NEW_LEG_TORQUE_SCALE = 1.0 as const;

/**
 * Soft-track weight for NEW-LEG HIP LATERAL only (the opposing DOF).
 * Sagittal/knee/ankle keep full plant composition for upright support.
 */
export const P1A_FORWARD_COMPLIANT_SOFT_TRACK_FLOOR = 0.2 as const;
export const P1A_FORWARD_COMPLIANT_SOFT_TRACK_SPAN = 0.45 as const;

/** Unused for sag path (kept for pure-helper API / tests). */
export const P1A_FORWARD_COMPLIANT_MAX_PLANT_DELTA_RAD = 0.08 as const;

/** Lateral hip soft-track delta cap (Stage-1 latOppose = 100%). */
export const P1A_FORWARD_COMPLIANT_MAX_LAT_DELTA_RAD = 0.06 as const;

/** Mild plant fraction for new-leg hip lateral only. */
export const P1A_FORWARD_COMPLIANT_MILD_PLANT_HIP_LAT = 0.5 as const;

/**
 * Gate A push 8: early-load underload threshold (measured new-foot load fraction).
 * Below this, whole-leg soft-track uses a lower α floor so plant residuals do
 * not accelerate the sole (no fixed timers — load fraction is the clock).
 */
export const P1A_FORWARD_EARLY_LOAD_FRACTION = 0.28 as const;
/** Soft-track floor while under early-load fraction. */
export const P1A_FORWARD_EARLY_LOAD_SOFT_FLOOR = 0.1 as const;
/** Soft-track span while under early-load fraction. */
export const P1A_FORWARD_EARLY_LOAD_SOFT_SPAN = 0.4 as const;
/**
 * @deprecated Push 9 α-impedance delta; replaced by rate-limited slew (push 11).
 * Kept exported so older probes/tests that import the name still resolve.
 */
export const P1A_FORWARD_COMPLIANT_MAX_SAG_DELTA_RAD = 0.03 as const;
/** @deprecated Push 9 α floors; hip-sag now uses {@link p1aEarlyLoadHipSagSlewTarget}. */
export const P1A_FORWARD_EARLY_LOAD_HIP_SAG_SOFT_FLOOR = 0.05 as const;
export const P1A_FORWARD_EARLY_LOAD_HIP_SAG_SOFT_SPAN = 0.3 as const;

/**
 * Gate A push 12 / 12b — momentum-absorbing landing helpers (eval-only knobs).
 *
 * Quasi-static mid-plant COM stop at touchdown caused the early-load 3.4 m/s
 * foot skate. Push 12 Part B tried: NEW knee flex→extend with load share + COM
 * settle to new support. That cut foot peak (3.45→2.05) but collapsed ladder
 * uprightness (height 0.633 / tilt 54°). Push 12b: both mechanisms are
 * **evaluation-only knobs defaulting OFF** so production absence = push-7 load
 * law exactly. Enable via `momentumAbsorbKnee` / `comSettleToNewSupport` on
 * {@link P1ATrialStyleKnobs}. Zero-forward P1A never engages these helpers.
 */
/** Load fraction at which new-leg authority / absorb profile completes. */
export const P1A_MOMENTUM_ABSORB_LOAD_FRACTION_FULL = 0.45 as const;
/** Load-fraction progress where knee flex peaks (then extends back). */
export const P1A_MOMENTUM_ABSORB_KNEE_PEAK_U = 0.35 as const;
/** Knee flex gain (rad per m/s of touchdown COM +Z speed). Negative = flex. */
export const P1A_MOMENTUM_ABSORB_KNEE_FLEX_GAIN_PER_MPS = 0.07 as const;
/** Bound on |new-knee flex| from momentum absorb (rad). */
export const P1A_MOMENTUM_ABSORB_KNEE_FLEX_MAX_RAD = 0.22 as const;

/**
 * Load-fraction-indexed new-knee flexion-then-extend for forward-plant landing.
 *
 * ```text
 * u = clamp(newFootLoadFraction / 0.45, 0, 1)
 * shape peaks at u = peakU, 0 at u=0 and u=1
 * flexRad = −min(maxFlex, gain · max(0, comVzTouchdown)) · shape
 * ```
 *
 * Pure; no timers. Zero-forward callers never invoke this.
 */
export function p1aMomentumAbsorbKneeFlexRad(args: {
  newFootLoadFraction: number;
  comForwardSpeedAtTouchdownMps: number;
  loadFractionFull?: number;
  peakU?: number;
  flexGainPerMps?: number;
  maxFlexRad?: number;
}): number {
  const full =
    args.loadFractionFull ?? P1A_MOMENTUM_ABSORB_LOAD_FRACTION_FULL;
  const peakU = clamp(
    args.peakU ?? P1A_MOMENTUM_ABSORB_KNEE_PEAK_U,
    1e-3,
    1 - 1e-3,
  );
  const gain =
    args.flexGainPerMps ?? P1A_MOMENTUM_ABSORB_KNEE_FLEX_GAIN_PER_MPS;
  const maxFlex =
    args.maxFlexRad ?? P1A_MOMENTUM_ABSORB_KNEE_FLEX_MAX_RAD;
  if (!Number.isFinite(args.newFootLoadFraction) || full <= 0) return 0;
  const u = clamp(args.newFootLoadFraction / full, 0, 1);
  // Triangle flex-then-extend over load share (no time base).
  const shape =
    u <= peakU ? u / peakU : (1 - u) / Math.max(1e-6, 1 - peakU);
  const speed = Number.isFinite(args.comForwardSpeedAtTouchdownMps)
    ? Math.max(0, args.comForwardSpeedAtTouchdownMps)
    : 0;
  const peakFlex = Math.min(maxFlex, Math.max(0, gain) * speed);
  return -peakFlex * shape;
}

/**
 * Gate A push 11: early-load new-leg hip-SAGITTAL slew rate (rad/s).
 * Derived from the muscle motor trackable speed (global 8 rad/s): use 1/4 so
 * the commanded target cannot step faster than motors reliably follow while
 * torque is still authority-ramped under low load. At 60 Hz: max ≈0.033 rad/step.
 */
export const P1A_EARLY_LOAD_HIP_SAG_SLEW_RAD_S = 2.0 as const;
/** Physics fixed-step used when load acceptance has no dt argument. */
export const P1A_CONTROLLER_FIXED_DT_S = 1 / 60;

/**
 * Rate-limit new-leg hip sagittal from MEASURED pose toward plant target.
 * Pure helper (push 11). While under early-load fraction the issued target must
 * not jump to the plant residual in one frame (that produced the 3.45 m/s
 * early foot-speed spike). Above threshold the caller uses exact plant law.
 *
 * ```text
 * base = measured (or continuous prevSlew if provided and finite)
 * issued = base + clamp(plant − base, ±rate · dt)
 * ```
 */
export function p1aEarlyLoadHipSagSlewTarget(args: {
  measuredRad: number;
  plantRad: number;
  /** Previous under-load slew target (rad); null reseeds from measured. */
  prevSlewRad: number | null;
  maxSlewRadS?: number;
  dtS?: number;
}): { targetRad: number; slewRad: number } {
  const rate = Math.max(
    0,
    Number.isFinite(args.maxSlewRadS)
      ? (args.maxSlewRadS as number)
      : P1A_EARLY_LOAD_HIP_SAG_SLEW_RAD_S,
  );
  const dt =
    Number.isFinite(args.dtS) && (args.dtS as number) > 0
      ? (args.dtS as number)
      : P1A_CONTROLLER_FIXED_DT_S;
  const measured = Number.isFinite(args.measuredRad) ? args.measuredRad : 0;
  const plant = Number.isFinite(args.plantRad) ? args.plantRad : measured;
  const prev =
    args.prevSlewRad !== null && Number.isFinite(args.prevSlewRad)
      ? (args.prevSlewRad as number)
      : measured;
  // If the integrator drifted far from measured (motor lag / reseed), re-anchor.
  const maxStep = rate * dt;
  const base = Math.abs(prev - measured) > 2 * maxStep ? measured : prev;
  const delta = clamp(plant - base, -maxStep, maxStep);
  const slewRad = base + delta;
  return { targetRad: slewRad, slewRad };
}

/**
 * Estimate leg joint configuration from live hip→foot geometry (2-link IK).
 * Used by the force-distribution solver when observation.jointAngles is absent.
 */
function forceDistLegConfigFromGeometry(
  hip: CaptureVec3,
  foot: CaptureVec3,
  side: MuscleSwingSide,
): LegJointConfig {
  const ik = solveTwoLinkLegIk({
    hip,
    footTarget: foot,
    swingSide: side,
  });
  return {
    hipSagRad: ik.hipSagittalRad,
    hipLatRad: ik.hipLateralRad,
    kneeRad: ik.kneeRad,
    ankleSagRad: ik.ankleRad,
    ankleLatRad: 0,
  };
}

/**
 * Gate A push 16: force-distribution dual-support path.
 *
 * Owns forward dual-contact place/load only (bypasses absorb/soft-track/fades).
 *
 * Architecture (measured on this motor bridge):
 * - **Plant targets** from the proven push-5/7 front/rear residual composition
 *   (structural column — pure torque-source wander collapsed upright).
 * - **Torque budgets** from body wrench → load-share ramp → friction cone →
 *   Jᵀ F (force-consistent; lightly-loaded feet cannot demand large shear).
 * - Knees keep the low-gain floor spring inside the force solver.
 *
 * Zero-forward never enters (caller gates).
 */
function applyForceDistributionDualSupportCommands(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  state: MuscleControllerState,
  config: MuscleControllerConfig,
  observation: MuscleObservation,
  args: {
    newSide: MuscleSwingSide;
    stanceSide: MuscleSwingSide;
    leftHip: CaptureVec3;
    rightHip: CaptureVec3;
    leftFoot: CaptureVec3;
    rightFoot: CaptureVec3;
    leftTarget: CaptureVec3;
    rightTarget: CaptureVec3;
    newFootLoadFraction: number;
    leftNormalImpulse: number;
    rightNormalImpulse: number;
    leftImpulse: boolean;
    rightImpulse: boolean;
    leftGrounded: boolean;
    rightGrounded: boolean;
  },
): void {
  const {
    newSide,
    stanceSide,
    leftHip,
    rightHip,
    leftFoot,
    rightFoot,
    leftTarget,
    rightTarget,
    newFootLoadFraction,
  } = args;

  const geometricMidPlantZ = (leftTarget.z + rightTarget.z) * 0.5;
  const midPlantX = (leftTarget.x + rightTarget.x) * 0.5;
  const supportCenter = {
    x: midPlantX,
    y: (leftTarget.y + rightTarget.y) * 0.5,
    z: geometricMidPlantZ,
  };
  const comVelX =
    observation.centerOfMassVelocity?.[0] ?? observation.pelvisVelocity[0];
  const comVelZ =
    observation.centerOfMassVelocity?.[2] ?? observation.pelvisVelocity[2];

  const leftCfg = forceDistLegConfigFromGeometry(leftHip, leftFoot, 'left');
  const rightCfg = forceDistLegConfigFromGeometry(rightHip, rightFoot, 'right');

  const massKg = FORCE_DIST_DEFAULT_MASS_KG;
  const leftNormalN = forceDistNormalForceN({
    normalImpulse: args.leftNormalImpulse,
    loadFraction:
      newSide === 'left' ? newFootLoadFraction : 1 - newFootLoadFraction,
    massKg,
  });
  const rightNormalN = forceDistNormalForceN({
    normalImpulse: args.rightNormalImpulse,
    loadFraction:
      newSide === 'right' ? newFootLoadFraction : 1 - newFootLoadFraction,
    massKg,
  });

  const solved = solveDualSupportForceDistribution({
    massKg,
    com: {
      x: observation.centerOfMass[0],
      y: observation.centerOfMass[1],
      z: observation.centerOfMass[2],
    },
    comVel: {
      x: comVelX,
      y: observation.centerOfMassVelocity?.[1] ?? observation.pelvisVelocity[1],
      z: comVelZ,
    },
    supportCenter,
    pelvisUp: {
      x: observation.pelvisUp[0],
      y: observation.pelvisUp[1],
      z: observation.pelvisUp[2],
    },
    pelvisAngularVel: {
      x: observation.pelvisAngularVelocity[0],
      y: observation.pelvisAngularVelocity[1],
      z: observation.pelvisAngularVelocity[2],
    },
    leftIsNew: newSide === 'left',
    newFootLoadFraction,
    leftNormalN,
    rightNormalN,
    leftConfig: leftCfg,
    rightConfig: rightCfg,
  });

  // --- Plant targets: push-7 forward residual composition (no absorb / soft-track) ---
  const newImpulse = newSide === 'left' ? args.leftImpulse : args.rightImpulse;
  const stanceImpulse =
    stanceSide === 'left' ? args.leftImpulse : args.rightImpulse;
  const policy = p1aInPlaceLoadAcceptancePolicy({
    comX: observation.centerOfMass[0],
    comY: observation.centerOfMass[1],
    comZ: observation.centerOfMass[2],
    comVelX,
    comVelZ,
    midPlantX,
    midPlantZ: geometricMidPlantZ,
    pelvisY: observation.pelvisPosition[1],
    newFootLoadFraction,
    hasLoadMeasurement: true,
    newFootImpulseBearing: newImpulse,
    stanceFootImpulseBearing: stanceImpulse,
    bothGrounded: args.leftGrounded && args.rightGrounded,
  });
  const basePlantGain = 2.8;
  const leftGain =
    basePlantGain +
    (args.leftGrounded || args.leftImpulse ? 0.6 : 0) +
    (newSide === 'left' ? policy.newFootPlantGainBoost : 0);
  const rightGain =
    basePlantGain +
    (args.rightGrounded || args.rightImpulse ? 0.6 : 0) +
    (newSide === 'right' ? policy.newFootPlantGainBoost : 0);
  const leftPlant = plantLegJointTargets({
    hip: leftHip,
    foot: leftFoot,
    footTarget: leftTarget,
    side: 'left',
    plantGain: leftGain,
  });
  const rightPlant = plantLegJointTargets({
    hip: rightHip,
    foot: rightFoot,
    footTarget: rightTarget,
    side: 'right',
    plantGain: rightGain,
  });

  const stancePlantZ =
    stanceSide === 'left' ? leftTarget.z : rightTarget.z;
  const newPlantZ = newSide === 'left' ? leftTarget.z : rightTarget.z;
  const stanceFootErrZM =
    stancePlantZ - (stanceSide === 'left' ? leftFoot.z : rightFoot.z);
  const newFootErrZM =
    newPlantZ - (newSide === 'left' ? leftFoot.z : rightFoot.z);

  // Push-7 residual composition + search sagittal offsets (P1B winner knobs).
  // No absorb flex / settle-to-new (superseded patch pile).
  const sagPol = state.singleSupportHoldActive
    ? resolveP1ASagittalPolicyOffsets(config.p1aSagittalPolicyOffsets)
    : ZERO_P1A_SAGITTAL_POLICY_OFFSETS;
  const sag = p1aForwardPlantLoadSagittalComposition({
    comZ: observation.centerOfMass[2],
    comY: observation.centerOfMass[1],
    comVelZ,
    midPlantZ: geometricMidPlantZ,
    stancePlantHipSagRad:
      stanceSide === 'left' ? leftPlant.hipSag : rightPlant.hipSag,
    newPlantHipSagRad:
      newSide === 'left' ? leftPlant.hipSag : rightPlant.hipSag,
    stancePlantKneeRad:
      stanceSide === 'left' ? leftPlant.knee : rightPlant.knee,
    newPlantKneeRad: newSide === 'left' ? leftPlant.knee : rightPlant.knee,
    stancePlantAnkleRad:
      stanceSide === 'left' ? leftPlant.ankle : rightPlant.ankle,
    newPlantAnkleRad: newSide === 'left' ? leftPlant.ankle : rightPlant.ankle,
    stanceFootErrZM,
    newFootErrZM,
    bothGrounded: args.leftGrounded && args.rightGrounded,
    ankleSagOverlay: policy.ankleSagOverlay,
    kneeComplianceRad: policy.kneeComplianceRad,
    heightRecoverKneeRad: policy.heightRecoverKneeRad,
    stanceHipOff: sagPol.loadStanceHipRad,
    newHipOff: sagPol.loadNewHipRad,
    stanceKneeOff: sagPol.loadStanceKneeRad,
    newKneeOff: sagPol.loadNewKneeRad,
    stanceAnkleOff: sagPol.loadStanceAnkleRad,
    newAnkleOff: sagPol.loadNewAnkleRad,
  });

  let leftHipSag: number;
  let rightHipSag: number;
  let leftKnee: number;
  let rightKnee: number;
  let leftAnkleSag: number;
  let rightAnkleSag: number;
  if (stanceSide === 'left') {
    leftHipSag = sag.stanceHipSag;
    leftKnee = sag.stanceKnee;
    leftAnkleSag = sag.stanceAnkleSag;
    rightHipSag = sag.newHipSag;
    rightKnee = sag.newKnee;
    rightAnkleSag = sag.newAnkleSag;
  } else {
    rightHipSag = sag.stanceHipSag;
    rightKnee = sag.stanceKnee;
    rightAnkleSag = sag.stanceAnkleSag;
    leftHipSag = sag.newHipSag;
    leftKnee = sag.newKnee;
    leftAnkleSag = sag.newAnkleSag;
  }

  const stanceColumnErrX =
    observation.centerOfMass[0] - state.stancePlantWorldXM;
  const stanceColGain = resolveP1AStanceColumnLatGainPerM(state.p1aTrialStyleKnobs);
  const stanceColMax = resolveP1AStanceColumnLatMaxRad(state.p1aTrialStyleKnobs);
  const stanceColumnHipLat = clamp(
    stanceColGain * stanceColumnErrX,
    -stanceColMax,
    stanceColMax,
  );
  const leftFootErrXM = leftTarget.x - leftFoot.x;
  const rightFootErrXM = rightTarget.x - rightFoot.x;
  const leftHipLatRaw =
    stanceSide === 'left'
      ? clamp(
          stanceColumnHipLat + policy.hipLatOverlayLeft,
          -P1A_LOAD_HIP_LAT_CLAMP_RAD,
          P1A_LOAD_HIP_LAT_CLAMP_RAD,
        )
      : p1aNewLegHipLatFromFootError({
          plantGain: leftGain,
          footErrXM: leftFootErrXM,
          overlayRad: policy.hipLatOverlayLeft,
        });
  const rightHipLatRaw =
    stanceSide === 'right'
      ? clamp(
          stanceColumnHipLat + policy.hipLatOverlayRight,
          -P1A_LOAD_HIP_LAT_CLAMP_RAD,
          P1A_LOAD_HIP_LAT_CLAMP_RAD,
        )
      : p1aNewLegHipLatFromFootError({
          plantGain: rightGain,
          footErrXM: rightFootErrXM,
          overlayRad: policy.hipLatOverlayRight,
        });

  // New-leg hip lat: load-share soft-track (push-6 pattern) — prevents dual
  // stiff lateral fight. Force distribution owns shear budget; this is the
  // plant-target side of the same force-consistent dual-support window.
  const softA = p1aCompliantSoftTrackWeight(solved.authority01);
  const fullNewHipLat = newSide === 'left' ? leftHipLatRaw : rightHipLatRaw;
  const mildPlantHipLat = fullNewHipLat * P1A_FORWARD_COMPLIANT_MILD_PLANT_HIP_LAT;
  const mHipLat = state.plantedPoseActive
    ? state.plantedSwingHipLat
    : mildPlantHipLat;
  const softLat = p1aCompliantSoftTrackTarget(
    mHipLat,
    mildPlantHipLat,
    softA,
    P1A_FORWARD_COMPLIANT_MAX_LAT_DELTA_RAD,
  );
  const leftHipLat = newSide === 'left' ? softLat : leftHipLatRaw;
  const rightHipLat = newSide === 'right' ? softLat : rightHipLatRaw;

  // Torque: plant-path base budgets (full stance). New leg uses force-share
  // authority so lightly-loaded feet cannot push hard (friction-consistent).
  const leftCap = commands.leftHip?.capacity ?? clamp01(config.strength);
  const rightCap = commands.rightHip?.capacity ?? clamp01(config.strength);
  const spineCap = commands.spine?.capacity ?? clamp01(config.strength);
  const tScale = 1.55;
  const auth = solved.authority01;
  // Keep new-leg budget high enough for upright (push-6 used scale 1.0 × auth
  // on maxTorque only via contactMotorAuthority). Match that: authority on new
  // leg only, min 0.05 when impulse-bearing so motor stays enabled.
  const newAuth = Math.max(0.05, auth);
  const leftIsNew = newSide === 'left';
  const leftMotorAuth = leftIsNew ? newAuth : 1;
  const rightMotorAuth = leftIsNew ? 1 : newAuth;

  const leftHipT =
    (config.baseMaxTorqueNm.leftHip ?? 1000) * leftCap * tScale * leftMotorAuth;
  const rightHipT =
    (config.baseMaxTorqueNm.rightHip ?? 1000) * rightCap * tScale * rightMotorAuth;
  const leftKneeT =
    (config.baseMaxTorqueNm.leftKnee ?? 250) * leftCap * tScale * leftMotorAuth;
  const rightKneeT =
    (config.baseMaxTorqueNm.rightKnee ?? 250) * rightCap * tScale * rightMotorAuth;
  const leftAnkleT =
    (config.baseMaxTorqueNm.leftAnkle ?? 70) * leftCap * 1.65 * leftMotorAuth;
  const rightAnkleT =
    (config.baseMaxTorqueNm.rightAnkle ?? 70) * rightCap * 1.65 * rightMotorAuth;
  const spineT = (config.baseMaxTorqueNm.spine ?? 700) * spineCap * 1.4;

  setJointCmd(commands, 'leftHip', leftHipSag, leftHipLat, leftHipT, 1);
  setJointCmd(commands, 'rightHip', rightHipSag, rightHipLat, rightHipT, 1);
  setJointCmd(commands, 'leftKnee', leftKnee, 0, leftKneeT, 1);
  setJointCmd(commands, 'rightKnee', rightKnee, 0, rightKneeT, 1);
  setJointCmd(
    commands,
    'leftAnkle',
    leftAnkleSag,
    policy.ankleLatLeft,
    leftAnkleT,
    1,
  );
  setJointCmd(
    commands,
    'rightAnkle',
    rightAnkleSag,
    policy.ankleLatRight,
    rightAnkleT,
    1,
  );
  const spineSagCmd = clamp(
    sag.spineSag + sagPol.loadSpineRad,
    -0.14,
    0.12,
  );
  setJointCmd(commands, 'spine', spineSagCmd, policy.spineLat, spineT, 0.95);

  // Exact force budgets (override setJointCmd Math.max with base-path).
  commands.leftHip!.maxTorqueNm = leftHipT;
  commands.rightHip!.maxTorqueNm = rightHipT;
  commands.leftKnee!.maxTorqueNm = leftKneeT;
  commands.rightKnee!.maxTorqueNm = rightKneeT;
  commands.leftAnkle!.maxTorqueNm = leftAnkleT;
  commands.rightAnkle!.maxTorqueNm = rightAnkleT;
  commands.spine!.maxTorqueNm = spineT;

  diagP1aLoadPolicyLateral = {
    hipLatOverlayLeft: policy.hipLatOverlayLeft,
    hipLatOverlayRight: policy.hipLatOverlayRight,
    ankleLatLeft: policy.ankleLatLeft,
    ankleLatRight: policy.ankleLatRight,
    spineLat: policy.spineLat,
    plantHipLatLeft: leftPlant.hipLat,
    plantHipLatRight: rightPlant.hipLat,
    plantHipSagLeft: leftPlant.hipSag,
    plantHipSagRight: rightPlant.hipSag,
    comMinusMidPlantZM: observation.centerOfMass[2] - geometricMidPlantZ,
    forwardPlantSagittal: true,
    issuedHipSagLeft: leftHipSag,
    issuedHipSagRight: rightHipSag,
    issuedKneeLeft: leftKnee,
    issuedKneeRight: rightKnee,
    issuedAnkleSagLeft: leftAnkleSag,
    issuedAnkleSagRight: rightAnkleSag,
    issuedSpineSag: spineSagCmd,
  };

  state.plantedSwingHipSag = newSide === 'left' ? leftHipSag : rightHipSag;
  state.plantedSwingHipLat = newSide === 'left' ? leftHipLat : rightHipLat;
  state.plantedSwingKnee = newSide === 'left' ? leftKnee : rightKnee;
  state.plantedSwingAnkle = newSide === 'left' ? leftAnkleSag : rightAnkleSag;
  state.plantedStanceHipSag = stanceSide === 'left' ? leftHipSag : rightHipSag;
  state.plantedStanceHipLat = stanceSide === 'left' ? leftHipLat : rightHipLat;
  state.plantedStanceKnee = stanceSide === 'left' ? leftKnee : rightKnee;
  state.plantedStanceAnkle =
    stanceSide === 'left' ? leftAnkleSag : rightAnkleSag;
  state.plantedSpineSag = spineSagCmd;
  state.plantedSpineLat = policy.spineLat;
  state.plantedPoseActive = true;

  // Superseded patches: clear so they do not re-enter.
  state.earlyLoadHipSagSlewActive = false;
  state.earlyLoadHipSagSlewRad = 0;
  state.momentumAbsorbTouchdownLatched = false;
  state.momentumAbsorbTouchdownComVzMps = 0;
}

/**
 * P1A load-only: retain both fixed world plants and accept load on the newly
 * landed foot through joint motors. Contact/load telemetry gates plant press;
 * COM velocity is damped, not injected.
 *
 * Stance-hip lateral baseline is the measured P1A stance-column law
 * ({@link P1A_STANCE_COLUMN_LAT_GAIN_PER_M}), not plant IK. New-leg hip lateral
 * is the foot-error residual + overlay ({@link p1aNewLegHipLatFromFootError}),
 * not the analytical plant-IK hipLateralRad baseline (Gate A push 2/4 sat
 * attribution).
 *
 * Sagittal:
 * - In-place (`commandedStepForwardM === 0`): plant-IK sagittal on both legs
 *   (certified P1A identity).
 * - Forward dual-contact (`commandedStepForwardM > 0` + both feet): Gate A
 *   push 16 **force-distribution** (body wrench → load-share → Jᵀ → torque
 *   motors). Supersedes absorb-knee / soft-track / early-load fades in that
 *   window. Single-support place still uses plant-IK until dual contact.
 *
 * Place / contact-candidate reuses this function with the same live hips and
 * fixed plant targets; phase-specific authority comes from contact/load gates.
 */
export function applyP1AInPlaceLoadAcceptanceCommands(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  state: MuscleControllerState,
  config: MuscleControllerConfig,
  observation: MuscleObservation,
): void {
  if (!state.swingSide || !state.stancePlantLatched) return;
  const newSide = state.swingSide;
  const stanceSide: MuscleSwingSide = newSide === 'left' ? 'right' : 'left';
  const {
    leftHip,
    rightHip,
    leftFoot,
    rightFoot,
  } = observationFeet(observation);

  const leftTarget: CaptureVec3 =
    newSide === 'left'
      ? {
          x: state.stepTargetWorldXM,
          y: state.stepTargetWorldYM,
          z: state.stepTargetWorldZM,
        }
      : {
          x: state.stancePlantWorldXM,
          y: state.stancePlantWorldYM,
          z: state.stancePlantWorldZM,
        };
  const rightTarget: CaptureVec3 =
    newSide === 'right'
      ? {
          x: state.stepTargetWorldXM,
          y: state.stepTargetWorldYM,
          z: state.stepTargetWorldZM,
        }
      : {
          x: state.stancePlantWorldXM,
          y: state.stancePlantWorldYM,
          z: state.stancePlantWorldZM,
        };

  const leftGrounded =
    observation.leftFootGrounded ?? isFootNearGround(leftFoot.y);
  const rightGrounded =
    observation.rightFootGrounded ?? isFootNearGround(rightFoot.y);
  const leftImpulse = p1aHasImpulseBearingSwingFootContact(observation, 'left');
  const rightImpulse = p1aHasImpulseBearingSwingFootContact(observation, 'right');
  const newImpulse = newSide === 'left' ? leftImpulse : rightImpulse;
  const stanceImpulse = stanceSide === 'left' ? leftImpulse : rightImpulse;

  // Load shares relative to the new (previous-swing) foot.
  const load = p1aFootLoadFractions(observation, stanceSide);
  const newFootLoadFraction = load.hasMeasurement ? load.swingLoadFraction : 0;

  // Gate A push 16: force-distribution owns forward dual-contact place/load.
  // Both contact flags (height or impulse) — place|L1R1 enters here; load stays
  // here while both soles remain in the support set. Bypasses absorb / soft-track.
  const bothFeetContact =
    (leftGrounded || leftImpulse) && (rightGrounded || rightImpulse);
  // Force distribution owns forward LOAD only (place contact-candidate keeps
  // plant-IK posts so replaceReady / touchdown geometry stay certified).
  if (
    state.stepPhase === 'load' &&
    forceDistDualSupportActive({
      commandedStepForwardM: state.commandedStepForwardM,
      stepPhase: state.stepPhase,
      bothFeetContact,
    })
  ) {
    applyForceDistributionDualSupportCommands(
      commands,
      state,
      config,
      observation,
      {
        newSide,
        stanceSide,
        leftHip,
        rightHip,
        leftFoot,
        rightFoot,
        leftTarget,
        rightTarget,
        newFootLoadFraction,
        leftNormalImpulse:
          typeof observation.leftFootNormalImpulse === 'number' &&
          Number.isFinite(observation.leftFootNormalImpulse)
            ? observation.leftFootNormalImpulse
            : 0,
        rightNormalImpulse:
          typeof observation.rightFootNormalImpulse === 'number' &&
          Number.isFinite(observation.rightFootNormalImpulse)
            ? observation.rightFootNormalImpulse
            : 0,
        leftImpulse,
        rightImpulse,
        leftGrounded,
        rightGrounded,
      },
    );
    return;
  }

  // Measured-load contact motor authority: scales only new-leg maxTorqueNm.
  // Targets, plant gains, capacity, activation, and stance/spine torques stay
  // for in-place; forward compliant path also soft-tracks new-leg targets.
  const contactMotorAuthority = p1aMeasuredLoadContactMotorAuthority({
    newFootImpulseBearing: newImpulse,
    hasLoadMeasurement: load.hasMeasurement,
    newFootLoadFraction,
  });
  const leftMotorAuthority = newSide === 'left' ? contactMotorAuthority : 1;
  const rightMotorAuthority = newSide === 'right' ? contactMotorAuthority : 1;
  const compliantDs = p1aForwardCompliantDoubleSupportActive({
    commandedStepForwardM: state.commandedStepForwardM,
  });

  const midPlantX = (leftTarget.x + rightTarget.x) * 0.5;
  const geometricMidPlantZ = (leftTarget.z + rightTarget.z) * 0.5;
  const newPlantZ = newSide === 'left' ? leftTarget.z : rightTarget.z;
  // Push 12b: COM settle-to-new-support is evaluation-only (default OFF = push-7
  // geometric mid). Zero-forward always uses geometric mid (identity).
  const settleToNewSupport =
    compliantDs && resolveP1AComSettleToNewSupport(state.p1aTrialStyleKnobs);
  const midPlantZ = settleToNewSupport ? newPlantZ : geometricMidPlantZ;
  const absorbKneeScale = compliantDs
    ? resolveP1AMomentumAbsorbKneeScale(state.p1aTrialStyleKnobs)
    : 0;
  const absorbKneeOn = absorbKneeScale > 0;
  const comVelX =
    observation.centerOfMassVelocity?.[0] ?? observation.pelvisVelocity[0];
  const comVelZ =
    observation.centerOfMassVelocity?.[2] ?? observation.pelvisVelocity[2];

  // Latch COM forward speed only when absorb-knee knob is on (no timers).
  if (
    absorbKneeOn &&
    state.stepPhase === 'load' &&
    newImpulse &&
    !state.momentumAbsorbTouchdownLatched
  ) {
    state.momentumAbsorbTouchdownComVzMps = Number.isFinite(comVelZ)
      ? comVelZ
      : 0;
    state.momentumAbsorbTouchdownLatched = true;
  }
  const momentumAbsorbKneeFlex =
    absorbKneeOn &&
    state.stepPhase === 'load' &&
    state.momentumAbsorbTouchdownLatched
      ? p1aMomentumAbsorbKneeFlexRad({
          newFootLoadFraction,
          comForwardSpeedAtTouchdownMps: state.momentumAbsorbTouchdownComVzMps,
          flexGainPerMps:
            P1A_MOMENTUM_ABSORB_KNEE_FLEX_GAIN_PER_MPS * absorbKneeScale,
          maxFlexRad: P1A_MOMENTUM_ABSORB_KNEE_FLEX_MAX_RAD * absorbKneeScale,
        })
      : 0;

  const policy = p1aInPlaceLoadAcceptancePolicy({
    comX: observation.centerOfMass[0],
    comY: observation.centerOfMass[1],
    comZ: observation.centerOfMass[2],
    comVelX,
    comVelZ,
    midPlantX,
    midPlantZ,
    pelvisY: observation.pelvisPosition[1],
    newFootLoadFraction,
    hasLoadMeasurement: load.hasMeasurement,
    newFootImpulseBearing: newImpulse,
    stanceFootImpulseBearing: stanceImpulse,
    bothGrounded: leftGrounded && rightGrounded,
  });
  const basePlantGain = 2.8;
  const leftGain =
    basePlantGain +
    (leftGrounded || leftImpulse ? 0.6 : 0) +
    (newSide === 'left' ? policy.newFootPlantGainBoost : 0);
  const rightGain =
    basePlantGain +
    (rightGrounded || rightImpulse ? 0.6 : 0) +
    (newSide === 'right' ? policy.newFootPlantGainBoost : 0);

  const leftPlant = plantLegJointTargets({
    hip: leftHip,
    foot: leftFoot,
    footTarget: leftTarget,
    side: 'left',
    plantGain: leftGain,
  });
  const rightPlant = plantLegJointTargets({
    hip: rightHip,
    foot: rightFoot,
    footTarget: rightTarget,
    side: 'right',
    plantGain: rightGain,
  });
  // Diagnostic only: expose plant-IK baselines + COM overlays to the trace
  // side-channel. Does not affect targets, torques, or phase selection.
  // Sagittal plant/issued fields filled after target construction below.
  diagP1aLoadPolicyLateral = {
    hipLatOverlayLeft: policy.hipLatOverlayLeft,
    hipLatOverlayRight: policy.hipLatOverlayRight,
    ankleLatLeft: policy.ankleLatLeft,
    ankleLatRight: policy.ankleLatRight,
    spineLat: policy.spineLat,
    plantHipLatLeft: leftPlant.hipLat,
    plantHipLatRight: rightPlant.hipLat,
    plantHipSagLeft: leftPlant.hipSag,
    plantHipSagRight: rightPlant.hipSag,
    comMinusMidPlantZM: observation.centerOfMass[2] - geometricMidPlantZ,
    forwardPlantSagittal: state.commandedStepForwardM > 1e-4,
  };

  // Offline P1A policy offsets (zero when absent / not in single-support hold).
  // Applied after measured IK + load overlays, before anatomical clamps.
  const sagPol = state.singleSupportHoldActive
    ? resolveP1ASagittalPolicyOffsets(config.p1aSagittalPolicyOffsets)
    : ZERO_P1A_SAGITTAL_POLICY_OFFSETS;
  const leftIsNew = newSide === 'left';
  const leftHipOff = leftIsNew ? sagPol.loadNewHipRad : sagPol.loadStanceHipRad;
  const rightHipOff = leftIsNew ? sagPol.loadStanceHipRad : sagPol.loadNewHipRad;
  const leftKneeOff = leftIsNew ? sagPol.loadNewKneeRad : sagPol.loadStanceKneeRad;
  const rightKneeOff = leftIsNew ? sagPol.loadStanceKneeRad : sagPol.loadNewKneeRad;
  const leftAnkleOff = leftIsNew ? sagPol.loadNewAnkleRad : sagPol.loadStanceAnkleRad;
  const rightAnkleOff = leftIsNew ? sagPol.loadStanceAnkleRad : sagPol.loadNewAnkleRad;

  // Stance-hip lateral: Stage-1 slip attribution — column law + overlay.
  // New-leg hip lateral: Stage-1 swing-hip sat attribution — foot-error
  // residual + overlay only (no analytical plant-IK hipLateralRad baseline).
  // plantLegJointTargets still supplies sagittal hip/knee/ankle and diagnostic
  // plantHipLat* side-channel baselines (pre-residual) for attribution.
  const stanceColumnErrX =
    observation.centerOfMass[0] - state.stancePlantWorldXM;
  const stanceColGain = resolveP1AStanceColumnLatGainPerM(state.p1aTrialStyleKnobs);
  const stanceColMax = resolveP1AStanceColumnLatMaxRad(state.p1aTrialStyleKnobs);
  const stanceColumnHipLat = clamp(
    stanceColGain * stanceColumnErrX,
    -stanceColMax,
    stanceColMax,
  );

  // Sagittal: in-place keeps plant-IK posts; forward plant uses residual
  // front/rear composition only during load (Gate A push 5). Place
  // contact-candidate must keep plant-IK posts so replaceReady can fire.
  const forwardPlant =
    state.commandedStepForwardM > 1e-4 && state.stepPhase === 'load';
  let leftHipSag: number;
  let rightHipSag: number;
  let leftKnee: number;
  let rightKnee: number;
  let leftAnkleSag: number;
  let rightAnkleSag: number;
  // Forward load spine override (milder capture); in-place keeps policy.spineSag.
  let forwardSpineSag: number | null = null;
  if (forwardPlant) {
    const stancePlantZ =
      stanceSide === 'left' ? leftTarget.z : rightTarget.z;
    const newPlantZ = newSide === 'left' ? leftTarget.z : rightTarget.z;
    const stanceFootErrZM =
      stancePlantZ - (stanceSide === 'left' ? leftFoot.z : rightFoot.z);
    const newFootErrZM =
      newPlantZ - (newSide === 'left' ? leftFoot.z : rightFoot.z);
    const sag = p1aForwardPlantLoadSagittalComposition({
      comZ: observation.centerOfMass[2],
      comY: observation.centerOfMass[1],
      comVelZ,
      midPlantZ: geometricMidPlantZ,
      // Opt-in only (push 12b): absent → push-7 mid settle.
      ...(settleToNewSupport ? { supportPlantZ: newPlantZ } : {}),
      stancePlantHipSagRad:
        stanceSide === 'left' ? leftPlant.hipSag : rightPlant.hipSag,
      newPlantHipSagRad:
        newSide === 'left' ? leftPlant.hipSag : rightPlant.hipSag,
      stancePlantKneeRad:
        stanceSide === 'left' ? leftPlant.knee : rightPlant.knee,
      newPlantKneeRad: newSide === 'left' ? leftPlant.knee : rightPlant.knee,
      stancePlantAnkleRad:
        stanceSide === 'left' ? leftPlant.ankle : rightPlant.ankle,
      newPlantAnkleRad: newSide === 'left' ? leftPlant.ankle : rightPlant.ankle,
      stanceFootErrZM,
      newFootErrZM,
      bothGrounded: leftGrounded && rightGrounded,
      ankleSagOverlay: policy.ankleSagOverlay,
      kneeComplianceRad: policy.kneeComplianceRad,
      heightRecoverKneeRad: policy.heightRecoverKneeRad,
      // Opt-in only: 0 / absent → push-7 knee (no absorb).
      ...(momentumAbsorbKneeFlex !== 0
        ? { momentumAbsorbKneeFlexRad: momentumAbsorbKneeFlex }
        : {}),
      stanceHipOff: leftIsNew ? rightHipOff : leftHipOff,
      newHipOff: leftIsNew ? leftHipOff : rightHipOff,
      stanceKneeOff: leftIsNew ? rightKneeOff : leftKneeOff,
      newKneeOff: leftIsNew ? leftKneeOff : rightKneeOff,
      stanceAnkleOff: leftIsNew ? rightAnkleOff : leftAnkleOff,
      newAnkleOff: leftIsNew ? leftAnkleOff : rightAnkleOff,
    });
    forwardSpineSag = sag.spineSag;
    if (stanceSide === 'left') {
      leftHipSag = sag.stanceHipSag;
      leftKnee = sag.stanceKnee;
      leftAnkleSag = sag.stanceAnkleSag;
      rightHipSag = sag.newHipSag;
      rightKnee = sag.newKnee;
      rightAnkleSag = sag.newAnkleSag;
    } else {
      rightHipSag = sag.stanceHipSag;
      rightKnee = sag.stanceKnee;
      rightAnkleSag = sag.stanceAnkleSag;
      leftHipSag = sag.newHipSag;
      leftKnee = sag.newKnee;
      leftAnkleSag = sag.newAnkleSag;
    }
  } else {
    leftHipSag = clamp(
      leftPlant.hipSag + 0.15 * policy.ankleSagOverlay + leftHipOff,
      -0.55,
      0.2,
    );
    rightHipSag = clamp(
      rightPlant.hipSag + 0.15 * policy.ankleSagOverlay + rightHipOff,
      -0.55,
      0.2,
    );
    const kneeSoft = policy.kneeComplianceRad + policy.heightRecoverKneeRad;
    leftKnee = clamp(leftPlant.knee + kneeSoft + leftKneeOff, -0.35, 0.14);
    rightKnee = clamp(rightPlant.knee + kneeSoft + rightKneeOff, -0.35, 0.14);
    leftAnkleSag = clamp(
      leftPlant.ankle + policy.ankleSagOverlay + leftAnkleOff,
      -0.14,
      0.1,
    );
    rightAnkleSag = clamp(
      rightPlant.ankle + policy.ankleSagOverlay + rightAnkleOff,
      -0.14,
      0.1,
    );
  }

  const leftFootErrXM = leftTarget.x - leftFoot.x;
  const rightFootErrXM = rightTarget.x - rightFoot.x;
  const leftHipLat =
    stanceSide === 'left'
      ? clamp(
          stanceColumnHipLat + policy.hipLatOverlayLeft,
          -P1A_LOAD_HIP_LAT_CLAMP_RAD,
          P1A_LOAD_HIP_LAT_CLAMP_RAD,
        )
      : p1aNewLegHipLatFromFootError({
          plantGain: leftGain,
          footErrXM: leftFootErrXM,
          overlayRad: policy.hipLatOverlayLeft,
        });
  const rightHipLat =
    stanceSide === 'right'
      ? clamp(
          stanceColumnHipLat + policy.hipLatOverlayRight,
          -P1A_LOAD_HIP_LAT_CLAMP_RAD,
          P1A_LOAD_HIP_LAT_CLAMP_RAD,
        )
      : p1aNewLegHipLatFromFootError({
          plantGain: rightGain,
          footErrXM: rightFootErrXM,
          overlayRad: policy.hipLatOverlayRight,
        });

  const leftAnkleLat = policy.ankleLatLeft;
  const rightAnkleLat = policy.ankleLatRight;

  // Gate A push-6/8/11/13: forward DS compliant load-share on the NEW leg only.
  // Stance stays the stiff position column (targets + full torque unchanged).
  //
  // Hip lat: soft-track measured/prior → mild plant (push 6/8) — always on.
  // Hip sag early-load (push 9 mild α): evaluation-only knob
  //   {@link resolveP1AEarlyLoadHipSagSoftTrack} (push 13). Always-on collapse
  //   arms-up uprightness (bisect: push8 both0 h=1.008 → push9 both0 h=0.641).
  //   Push 11 slew/latch experiments regressed nSlip; mild soft-track retained
  //   as opt-in only. Default OFF = push-7/8 full plant hip-sag composition.
  let issuedLeftHipSag = leftHipSag;
  let issuedRightHipSag = rightHipSag;
  let issuedLeftKnee = leftKnee;
  let issuedRightKnee = rightKnee;
  let issuedLeftAnkleSag = leftAnkleSag;
  let issuedRightAnkleSag = rightAnkleSag;
  let issuedLeftHipLat = leftHipLat;
  let issuedRightHipLat = rightHipLat;
  if (compliantDs) {
    const earlyUnderload =
      state.stepPhase === 'load' &&
      load.hasMeasurement &&
      newFootLoadFraction < P1A_FORWARD_EARLY_LOAD_FRACTION;
    const softA = earlyUnderload
      ? p1aCompliantSoftTrackWeight(
          contactMotorAuthority,
          P1A_FORWARD_EARLY_LOAD_SOFT_FLOOR,
          P1A_FORWARD_EARLY_LOAD_SOFT_SPAN,
        )
      : p1aCompliantSoftTrackWeight(contactMotorAuthority);
    const dLat = P1A_FORWARD_COMPLIANT_MAX_LAT_DELTA_RAD;
    const fullHipLat = newSide === 'left' ? leftHipLat : rightHipLat;
    const mildPlantHipLat = fullHipLat * P1A_FORWARD_COMPLIANT_MILD_PLANT_HIP_LAT;
    const mHipLat = state.plantedPoseActive
      ? state.plantedSwingHipLat
      : mildPlantHipLat;
    const softLat = p1aCompliantSoftTrackTarget(
      mHipLat,
      mildPlantHipLat,
      softA,
      dLat,
    );
    if (newSide === 'left') {
      issuedLeftHipLat = softLat;
    } else {
      issuedRightHipLat = softLat;
    }

    // Early-load hip-sag soft-track: opt-in only (push 13). Integrator state
    // cleared so a future latch/slew experiment starts clean.
    state.earlyLoadHipSagSlewActive = false;
    state.earlyLoadHipSagSlewRad = 0;
    if (
      earlyUnderload &&
      resolveP1AEarlyLoadHipSagSoftTrack(state.p1aTrialStyleKnobs)
    ) {
      const sagA = p1aCompliantSoftTrackWeight(
        contactMotorAuthority,
        P1A_FORWARD_EARLY_LOAD_HIP_SAG_SOFT_FLOOR,
        P1A_FORWARD_EARLY_LOAD_HIP_SAG_SOFT_SPAN,
      );
      const dSag = P1A_FORWARD_COMPLIANT_MAX_SAG_DELTA_RAD;
      const plantHipSag = newSide === 'left' ? leftHipSag : rightHipSag;
      const mHipSag = state.plantedPoseActive
        ? state.plantedSwingHipSag
        : plantHipSag;
      const softSag = p1aCompliantSoftTrackTarget(
        mHipSag,
        plantHipSag,
        sagA,
        dSag,
      );
      if (newSide === 'left') {
        issuedLeftHipSag = softSag;
      } else {
        issuedRightHipSag = softSag;
      }
    }
  } else {
    state.earlyLoadHipSagSlewActive = false;
    state.earlyLoadHipSagSlewRad = 0;
    // Clear absorb latch outside forward compliant DS (zero-forward identity).
    state.momentumAbsorbTouchdownLatched = false;
    state.momentumAbsorbTouchdownComVzMps = 0;
  }

  const leftCap = commands.leftHip?.capacity ?? clamp01(config.strength);
  const rightCap = commands.rightHip?.capacity ?? clamp01(config.strength);
  const spineCap = commands.spine?.capacity ?? clamp01(config.strength);
  const tScale = 1.55;

  // Prefer stronger plant authority on the new foot while under-loaded (in-place).
  // Forward compliant DS: impedance scale instead of stiffening boost (push-6).
  const forwardNewBoost =
    !compliantDs &&
    state.commandedStepForwardM > 1e-4 &&
    state.stepPhase === 'load'
      ? 0.25
      : 0;
  const newBoost =
    1 +
    0.15 * (policy.newFootPlantGainBoost > 0 ? 1 : 0) +
    forwardNewBoost;
  const compliantNewScale = compliantDs
    ? P1A_FORWARD_COMPLIANT_NEW_LEG_TORQUE_SCALE
    : 1;
  const leftT =
    tScale *
    (newSide === 'left' ? newBoost * compliantNewScale : 1);
  const rightT =
    tScale *
    (newSide === 'right' ? newBoost * compliantNewScale : 1);

  // Existing torque formulas × measured-load contact motor authority on the
  // new leg only. Stance leg and spine keep full existing authority.
  const leftHipTorque =
    (config.baseMaxTorqueNm.leftHip ?? 1000) * leftCap * leftT * leftMotorAuthority;
  const rightHipTorque =
    (config.baseMaxTorqueNm.rightHip ?? 1000) * rightCap * rightT * rightMotorAuthority;
  const leftKneeTorque =
    (config.baseMaxTorqueNm.leftKnee ?? 250) * leftCap * leftT * leftMotorAuthority;
  const rightKneeTorque =
    (config.baseMaxTorqueNm.rightKnee ?? 250) * rightCap * rightT * rightMotorAuthority;
  const leftAnkleTorque =
    (config.baseMaxTorqueNm.leftAnkle ?? 70) * leftCap * 1.65 * leftMotorAuthority *
    (newSide === 'left' ? compliantNewScale : 1);
  const rightAnkleTorque =
    (config.baseMaxTorqueNm.rightAnkle ?? 70) * rightCap * 1.65 * rightMotorAuthority *
    (newSide === 'right' ? compliantNewScale : 1);

  setJointCmd(
    commands,
    'leftHip',
    issuedLeftHipSag,
    issuedLeftHipLat,
    leftHipTorque,
    1,
  );
  setJointCmd(
    commands,
    'rightHip',
    issuedRightHipSag,
    issuedRightHipLat,
    rightHipTorque,
    1,
  );
  setJointCmd(
    commands,
    'leftKnee',
    issuedLeftKnee,
    0,
    leftKneeTorque,
    1,
  );
  setJointCmd(
    commands,
    'rightKnee',
    issuedRightKnee,
    0,
    rightKneeTorque,
    1,
  );
  setJointCmd(
    commands,
    'leftAnkle',
    issuedLeftAnkleSag,
    leftAnkleLat,
    leftAnkleTorque,
    1,
  );
  setJointCmd(
    commands,
    'rightAnkle',
    issuedRightAnkleSag,
    rightAnkleLat,
    rightAnkleTorque,
    1,
  );
  // setJointCmd retains Math.max with any prior base-path torque. When authority
  // is below full, force the scaled new-leg torque so flags-only / under-load
  // can disable or scale motors. At authority === 1, leave Math.max baseline.
  if (contactMotorAuthority !== 1) {
    if (newSide === 'left') {
      commands.leftHip!.maxTorqueNm = leftHipTorque;
      commands.leftKnee!.maxTorqueNm = leftKneeTorque;
      commands.leftAnkle!.maxTorqueNm = leftAnkleTorque;
    } else {
      commands.rightHip!.maxTorqueNm = rightHipTorque;
      commands.rightKnee!.maxTorqueNm = rightKneeTorque;
      commands.rightAnkle!.maxTorqueNm = rightAnkleTorque;
    }
  }
  // No new-leg maxSpeedRadS cap: spherical hip couples sag+lat; speed-limiting
  // the hip starved sagittal support and collapsed the 2 s load window.
  // Spine: policy offset after measured construction, final anatomical clamp.
  // Forward load uses milder capture spine from residual composition.
  const spineSagBase =
    forwardSpineSag !== null ? forwardSpineSag : policy.spineSag;
  const spineSagCmd = clamp(
    spineSagBase + sagPol.loadSpineRad,
    forwardSpineSag !== null ? -0.14 : -0.22,
    forwardSpineSag !== null ? 0.12 : 0.18,
  );
  setJointCmd(
    commands,
    'spine',
    spineSagCmd,
    policy.spineLat,
    (config.baseMaxTorqueNm.spine ?? 700) * spineCap * 1.4,
    0.95,
  );

  // Diagnostic: record issued sagittal targets (side-channel only).
  if (diagP1aLoadPolicyLateral) {
    diagP1aLoadPolicyLateral.issuedHipSagLeft = issuedLeftHipSag;
    diagP1aLoadPolicyLateral.issuedHipSagRight = issuedRightHipSag;
    diagP1aLoadPolicyLateral.issuedKneeLeft = issuedLeftKnee;
    diagP1aLoadPolicyLateral.issuedKneeRight = issuedRightKnee;
    diagP1aLoadPolicyLateral.issuedAnkleSagLeft = issuedLeftAnkleSag;
    diagP1aLoadPolicyLateral.issuedAnkleSagRight = issuedRightAnkleSag;
    diagP1aLoadPolicyLateral.issuedSpineSag = spineSagCmd;
  }

  // Telemetry planted pose (mirrors Gate A plant path for phase diagnostics).
  state.plantedSwingHipSag =
    newSide === 'left' ? issuedLeftHipSag : issuedRightHipSag;
  state.plantedSwingHipLat =
    newSide === 'left' ? issuedLeftHipLat : issuedRightHipLat;
  state.plantedSwingKnee =
    newSide === 'left' ? issuedLeftKnee : issuedRightKnee;
  state.plantedSwingAnkle =
    newSide === 'left' ? issuedLeftAnkleSag : issuedRightAnkleSag;
  state.plantedStanceHipSag =
    stanceSide === 'left' ? issuedLeftHipSag : issuedRightHipSag;
  state.plantedStanceHipLat =
    stanceSide === 'left' ? issuedLeftHipLat : issuedRightHipLat;
  state.plantedStanceKnee =
    stanceSide === 'left' ? issuedLeftKnee : issuedRightKnee;
  state.plantedStanceAnkle =
    stanceSide === 'left' ? issuedLeftAnkleSag : issuedRightAnkleSag;
  state.plantedSpineSag = spineSagCmd;
  state.plantedSpineLat = policy.spineLat;
  state.plantedPoseActive = true;
}

/**
 * Explicit load / double-support hold (Gate A forward step).
 *
 * Critical geometry: full hip→rear-foot IK recenters the pelvis over the
 * trailing plant and drives COM backward. Instead:
 * - front (new) foot: live hip→touchdown plant IK (accept weight, no re-lift)
 * - rear (stance) foot: hip extension push + ankle/knee plant feedback only
 * - COM target: mid of the two world plants (joint targets only)
 * Contact is the truth gate for plant retention.
 *
 * Not used for P1A in-place replacement — see applyP1AInPlaceLoadAcceptanceCommands.
 */
function applyExplicitDoubleSupportPlantCommands(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  state: MuscleControllerState,
  config: MuscleControllerConfig,
  observation: MuscleObservation,
): void {
  if (!state.swingSide || !state.stancePlantLatched) return;
  const swing = state.swingSide;
  const stance: MuscleSwingSide = swing === 'left' ? 'right' : 'left';
  const { leftHip, rightHip, leftFoot, rightFoot } = observationFeet(observation);
  const swingHipPos = swing === 'left' ? leftHip : rightHip;
  const stanceHipPos = stance === 'left' ? leftHip : rightHip;
  const swingFoot = swing === 'left' ? leftFoot : rightFoot;
  const stanceFoot = stance === 'left' ? leftFoot : rightFoot;
  const hipSwing = swing === 'left' ? 'leftHip' : 'rightHip';
  const kneeSwing = swing === 'left' ? 'leftKnee' : 'rightKnee';
  const ankleSwing = swing === 'left' ? 'leftAnkle' : 'rightAnkle';
  const hipStance = stance === 'left' ? 'leftHip' : 'rightHip';
  const kneeStance = stance === 'left' ? 'leftKnee' : 'rightKnee';
  const ankleStance = stance === 'left' ? 'leftAnkle' : 'rightAnkle';

  const swingTarget: CaptureVec3 = {
    x: state.stepTargetWorldXM,
    y: state.stepTargetWorldYM,
    z: state.stepTargetWorldZM,
  };
  const stanceTarget: CaptureVec3 = {
    x: state.stancePlantWorldXM,
    y: state.stancePlantWorldYM,
    z: state.stancePlantWorldZM,
  };

  const swingGrounded =
    swing === 'left'
      ? (observation.leftFootGrounded ?? isFootNearGround(swingFoot.y))
      : (observation.rightFootGrounded ?? isFootNearGround(swingFoot.y));
  const stanceGrounded =
    stance === 'left'
      ? (observation.leftFootGrounded ?? isFootNearGround(stanceFoot.y))
      : (observation.rightFootGrounded ?? isFootNearGround(stanceFoot.y));

  // Front (new) foot: plant IK to commanded touchdown — hold sole, no re-lift.
  const frontPlant = plantLegJointTargets({
    hip: swingHipPos,
    foot: swingFoot,
    footTarget: swingTarget,
    side: swing,
    plantGain: swingGrounded ? 3.6 : 2.2,
  });

  // Rear (stance) foot: plant feedback on ankle/knee + lateral hip only.
  // Do NOT full sagittal hip IK to rear plant (that pulls COM backward).
  const rearPlant = plantLegJointTargets({
    hip: stanceHipPos,
    foot: stanceFoot,
    footTarget: stanceTarget,
    side: stance,
    plantGain: stanceGrounded ? 2.0 : 1.4,
  });

  const midZ = (stanceTarget.z + swingTarget.z) * 0.5;
  // Bias COM target slightly forward of geometric mid so weight enters the new foot.
  const comTargetZ = midZ + (swingTarget.z - stanceTarget.z) * 0.15;
  const comErrZ = observation.centerOfMass[2] - comTargetZ; // <0 ⇒ COM behind target
  const bothDown = swingGrounded && stanceGrounded;

  // Rear hip extension pushes pelvis forward; front leg is a post (near-extended plant).
  const rearExtend = bothDown ? clamp(0.12 + (-comErrZ) * 0.45, 0.08, 0.22) : 0.1;
  const stanceHipSag = clamp(rearExtend, -0.15, 0.28);
  const stanceHipLat = 0;
  // Prefer extended front plant over deep flex that can unplant the new sole.
  const swingHipSag = clamp(
    Math.max(frontPlant.hipSag, -0.25) +
      (bothDown ? clamp(comErrZ * 0.1, -0.04, 0.04) : 0),
    -0.55,
    0.12,
  );
  const swingKnee = clamp(Math.max(frontPlant.knee, -0.18), -0.3, 0.08);
  const spineSag = bothDown ? clamp(-0.08 - comErrZ * 0.4, -0.18, 0.05) : -0.04;
  const spineLat = 0;

  const swingCap = commands[hipSwing]?.capacity ?? clamp01(config.strength);
  const stanceCap = commands[hipStance]?.capacity ?? clamp01(config.strength);
  const spineCap = commands.spine?.capacity ?? clamp01(config.strength);
  const tScale = 1.5;

  setJointCmd(
    commands,
    hipSwing,
    swingHipSag,
    0,
    (config.baseMaxTorqueNm[hipSwing] ?? 1000) * swingCap * tScale,
    1,
  );
  setJointCmd(
    commands,
    kneeSwing,
    swingKnee,
    0,
    (config.baseMaxTorqueNm[kneeSwing] ?? 250) * swingCap * tScale,
    1,
  );
  setJointCmd(
    commands,
    ankleSwing,
    frontPlant.ankle,
    0,
    (config.baseMaxTorqueNm[ankleSwing] ?? 70) * swingCap * 1.65,
    1,
  );
  setJointCmd(
    commands,
    hipStance,
    stanceHipSag,
    stanceHipLat,
    (config.baseMaxTorqueNm[hipStance] ?? 1000) * stanceCap * tScale,
    1,
  );
  setJointCmd(
    commands,
    kneeStance,
    clamp(0.5 * rearPlant.knee + 0.5 * 0.1, -0.25, 0.16),
    0,
    (config.baseMaxTorqueNm[kneeStance] ?? 250) * stanceCap * tScale,
    1,
  );
  setJointCmd(
    commands,
    ankleStance,
    rearPlant.ankle,
    0,
    (config.baseMaxTorqueNm[ankleStance] ?? 70) * stanceCap * 1.55,
    1,
  );
  setJointCmd(
    commands,
    'spine',
    spineSag,
    spineLat,
    (config.baseMaxTorqueNm.spine ?? 700) * spineCap * 1.45,
    0.95,
  );

  state.plantedSwingHipSag = swingHipSag;
  state.plantedSwingHipLat = 0;
  state.plantedSwingKnee = swingKnee;
  state.plantedSwingAnkle = frontPlant.ankle;
  state.plantedStanceHipSag = stanceHipSag;
  state.plantedStanceHipLat = stanceHipLat;
  state.plantedStanceKnee = clamp(0.5 * rearPlant.knee + 0.5 * 0.1, -0.25, 0.16);
  state.plantedStanceAnkle = rearPlant.ankle;
  state.plantedSpineSag = spineSag;
  state.plantedSpineLat = spineLat;
  state.plantedPoseActive = true;
}

/**
 * Pure P1A sagittal capture-point error (m) for the stance support column.
 *
 * LIP capture: captureZ = COM.z + COMVelocity.z / ω, with
 * ω = √(g / height) and height = max(minHeight, COM.y − stanceFoot.y).
 * error = captureZ − stanceFoot.z. Zero velocity reduces to position error.
 * Constants: {@link CAPTURE_GRAVITY_MPS2}, {@link CAPTURE_MIN_COM_HEIGHT_M}.
 */
export function p1aSagittalCaptureErrorM(args: {
  comY: number;
  comZ: number;
  comVelocityZ: number;
  stanceFootY: number;
  stanceFootZ: number;
  gravityMps2?: number;
  minComHeightM?: number;
}): number {
  const height = Math.max(
    args.minComHeightM ?? CAPTURE_MIN_COM_HEIGHT_M,
    args.comY - args.stanceFootY,
  );
  const g = args.gravityMps2 ?? CAPTURE_GRAVITY_MPS2;
  const omega = Math.sqrt(g / height);
  const captureZ = args.comZ + args.comVelocityZ / omega;
  return captureZ - args.stanceFootZ;
}

/**
 * P1A single-support stance column for lift/hold/place.
 *
 * Measured defect (trial-0, swing=right/stance=left): after prepare the dual-
 * support balance law still treats COM-to-the-right-of-support as "push with
 * the right leg / flex the left". Swing motors are overridden by the lift IK,
 * so only the stance-flex half of that law remains active — COM runs from
 * ≈−0.10 m to ≈+0.43 m, the left plant unloads, and the body falls onto the
 * still-planted swing foot (`lift|L0R1`, not genuine stance-only support).
 *
 * Fix: overlay measured cp16 COM→stance shrink *lateral* targets on stance hip,
 * spine, and stance ankle roll, plus prior P1A stance *sagittal* laws on hip /
 * knee / ankle / spine from capture-point errZ (not raw COM−plant). Single-
 * support torque may raise max authority only. No root forces, no
 * friction/material changes. No extra velocity or angular law terms.
 */
export function applyP1AStanceSupportColumn(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  state: MuscleControllerState,
  config: MuscleControllerConfig,
  observation: MuscleObservation,
): void {
  if (!state.swingSide || !state.stancePlantLatched) return;
  const swing = state.swingSide;
  const stance: MuscleSwingSide = swing === 'left' ? 'right' : 'left';
  const hipStance = stance === 'left' ? 'leftHip' : 'rightHip';
  const kneeStance = stance === 'left' ? 'leftKnee' : 'rightKnee';
  const ankleStance = stance === 'left' ? 'leftAnkle' : 'rightAnkle';

  const comX = observation.centerOfMass[0];
  const comY = observation.centerOfMass[1];
  const comZ = observation.centerOfMass[2];
  const errX = comX - state.stancePlantWorldXM;
  // Sagittal: LIP capture-point error vs live stance foot (COM vel preferred).
  const { leftFoot, rightFoot } = observationFeet(observation);
  const stanceFoot = stance === 'left' ? leftFoot : rightFoot;
  const comVelZ =
    observation.centerOfMassVelocity?.[2] ?? observation.pelvisVelocity[2];
  const errZ = p1aSagittalCaptureErrorM({
    comY,
    comZ,
    comVelocityZ: comVelZ,
    stanceFootY: stanceFoot.y,
    stanceFootZ: stanceFoot.z,
  });

  // cp16: stanceHipLat ∝ +(com.x − stanceFoot.x); spine counter-leans opposite.
  // Trial knobs (absence = production) retune column lat gain/max + SS scales.
  const knobs = state.p1aTrialStyleKnobs;
  const colGain = resolveP1AStanceColumnLatGainPerM(knobs);
  const colMax = resolveP1AStanceColumnLatMaxRad(knobs);
  const stanceGainScale = resolveP1ASsStanceGainScale(knobs);
  const spineGainScale = resolveP1ASsSpineGainScale(knobs);
  const stanceHipLat = clamp(colGain * errX, -colMax, colMax);
  const spineLat = clamp(
    -colGain * errX * 0.9 * spineGainScale,
    -colMax,
    colMax,
  );

  // Ankle lateral mirrors dual-support L/R roll: +errX → right +roll / left −roll.
  const ankleSide = stance === 'right' ? 1 : -1;
  const ankleLat = clamp(ankleSide * errX * 1.4, -0.22, 0.22);

  // Prior P1A stance sagittal laws: hip/knee/ankle/spine from capture-point errZ
  // (not raw COM−plant). Lateral laws remain plant-based errX only.
  // Offline policy offsets after feedback, before anatomical clamps.
  const sagPol = state.singleSupportHoldActive
    ? resolveP1ASagittalPolicyOffsets(config.p1aSagittalPolicyOffsets)
    : ZERO_P1A_SAGITTAL_POLICY_OFFSETS;
  const baseHipSag = commands[hipStance]?.targetOffsetRad ?? 0;
  const stanceHipSag = clamp(
    baseHipSag - 0.55 * stanceGainScale * errZ + sagPol.singleSupportStanceHipRad,
    -0.45,
    0.35,
  );
  const baseKnee = commands[kneeStance]?.targetOffsetRad ?? 0;
  const stanceKnee = clamp(
    Math.max(baseKnee, 0.12) + sagPol.singleSupportStanceKneeRad,
    -0.1,
    0.2,
  );
  const ankleSag = clamp(
    0.7 * stanceGainScale * errZ + sagPol.singleSupportStanceAnkleRad,
    -0.16,
    0.12,
  );
  const baseSpineSag = commands.spine?.targetOffsetRad ?? 0;
  const spineSag = clamp(
    baseSpineSag + 1.1 * spineGainScale * errZ + sagPol.singleSupportSpineRad,
    -0.35,
    0.25,
  );

  const hipBase = commands[hipStance];
  const kneeBase = commands[kneeStance];
  const ankleBase = commands[ankleStance];
  const spineBase = commands.spine;

  const stanceCap = hipBase?.capacity ?? clamp01(config.strength);
  // Torque multipliers are max-authority only; setJointCmd keeps max(base, here).
  const hipTorque = (config.baseMaxTorqueNm[hipStance] ?? 1000) * stanceCap * 1.6;
  const kneeTorque = (config.baseMaxTorqueNm[kneeStance] ?? 250) * stanceCap * 1.55;
  const ankleTorque = (config.baseMaxTorqueNm[ankleStance] ?? 70) * stanceCap * 1.5;
  const spineCap = spineBase?.capacity ?? stanceCap;
  const spineTorque = (config.baseMaxTorqueNm.spine ?? 650) * spineCap * 1.25;

  // Activation: pass existing (setJointCmd Math.max with prev → preserved).
  setJointCmd(
    commands,
    hipStance,
    stanceHipSag,
    stanceHipLat,
    hipTorque,
    hipBase?.activation ?? 0,
  );
  setJointCmd(
    commands,
    kneeStance,
    stanceKnee,
    0,
    kneeTorque,
    kneeBase?.activation ?? 0,
  );
  setJointCmd(
    commands,
    ankleStance,
    ankleSag,
    ankleLat,
    ankleTorque,
    ankleBase?.activation ?? 0,
  );

  if (spineBase) {
    setJointCmd(
      commands,
      'spine',
      spineSag,
      spineLat,
      spineTorque,
      spineBase.activation,
    );
  }
}

/**
 * P1A lift / hold / place joint motors only:
 * - stance: single-support column (cp16 lateral + capture-point errZ sagittal laws)
 * - swing: canonical two-link vertical-shortening deltas on latched prepare pose
 *   (sagittal/vertical) + bounded world-X lateral feedback
 * No root forces, no forward step lean, no absolute IK motor targets, no gain overlays.
 */
function applyP1ASingleSupportJointOverrides(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  state: MuscleControllerState,
  config: MuscleControllerConfig,
  observation: MuscleObservation,
): void {
  if (!state.swingSide || !state.stancePlantLatched) return;
  const swing = state.swingSide;
  const hipSwing = swing === 'left' ? 'leftHip' : 'rightHip';
  const kneeSwing = swing === 'left' ? 'leftKnee' : 'rightKnee';
  const ankleSwing = swing === 'left' ? 'leftAnkle' : 'rightAnkle';

  const { leftFoot, rightFoot } = observationFeet(observation);
  const swingFoot = swing === 'left' ? leftFoot : rightFoot;

  const swingCap = commands[hipSwing]?.capacity ?? clamp01(config.strength);

  // Lift apex: 0.04 m above the original plant. Use a C2 target ramp with no
  // first-frame jump; contact events remain the truth for phase transitions.
  let liftBlend = 1;
  if (state.stepPhase === 'lift') {
    liftBlend = minJerkBlend01(
      state.stepPhaseElapsedS / Math.max(P1A_LIFT_RAMP_S, 1e-3),
    );
  } else if (state.stepPhase === 'place') {
    liftBlend =
      1 -
      minJerkBlend01(
        state.stepPhaseElapsedS / Math.max(P1A_REPLACE_S, 1e-3),
      );
  }
  // C2-scaled lift height — solved before applying measured X/Z corrections.
  // Optional trial knob overrides production P1A_LIFT_CLEARANCE_M when set.
  // Forward steps need a higher apex so oriented sole clears 0.04 m while the
  // hip also tracks sagittal advance (in-place keeps certified 0.037).
  // Oriented clearance ≈ footCenterY − halfHeight(0.03). Need center ≥0.07 for
  // 0.04 clear; knee clamp −0.55 was saturating vertical shortening (~0.034 clear).
  const baseLiftClearM = resolveP1ALiftClearanceM(state.p1aTrialStyleKnobs);
  const forwardSwing = state.commandedStepForwardM > 1e-4;
  // Forward lift/hold apex. Push-14 floored at 0.065 but knee clamp −0.55
  // capped dynamics at ~0.036 m center / ~0.029 sole (clearance miss).
  // Push-15: command a trackable apex (~0.05) once the knee floor opens to
  // match liftHM — enough for sole clearance ≥0.032 with margin, without the
  // 0.065 hold-depth collapse under full two-link flex.
  const liftClearM = forwardSwing
    ? Math.max(baseLiftClearM, P1A_FORWARD_LIFT_CLEARANCE_FLOOR_M)
    : baseLiftClearM;
  const liftHM = liftClearM * liftBlend;
  // Gate A push 18: closed-loop touchdown-velocity nulling — blend plant
  // target toward foot − gain×V (signed) so world-frame horizontal foot
  // velocity → 0 at contact. Push 15's max(0,Vz) open-loop rear bias
  // overshot to −1..−1.6 m/s at TD; full signed feedback + symmetric clamp
  // kills both forward and rear residual. Engage on hold + place: dual-contact
  // place candidates switch to load motors and skip this function, so
  // pre-touchdown hold/early-place is the only window that owns swing motors.
  // Production default scale = 0 (off).
  let goalZ = state.stepTargetWorldZM;
  let goalX = state.stepTargetWorldXM;
  /** Direct hip-sag velocity-null term (rad); set inside pre-TD band. */
  let hipVelNullRad = 0;
  let hipLatVelNullRad = 0;
  if (
    forwardSwing &&
    (state.stepPhase === 'place' || state.stepPhase === 'hold')
  ) {
    const swingVel =
      swing === 'left'
        ? observation.leftFootVelocity
        : observation.rightFootVelocity;
    const footVz = swingVel?.[2] ?? 0;
    const footVx = swingVel?.[0] ?? 0;
    const heightAbovePlantM = swingFoot.y - state.stepTargetWorldYM;
    // Widen the band vs capture freeze so hold can start decelerating the
    // swing foot before place dual-contact steals the motor path.
    const freezeH =
      resolveP1APlaceCaptureFreezeM(state.p1aTrialStyleKnobs) *
      (state.stepPhase === 'hold' ? 3 : 1);
    const retractGainS = resolveP1APlaceSwingRetractGainS(
      state.p1aTrialStyleKnobs,
    );
    const retractScale = resolveP1APlaceSwingRetractScale(
      state.p1aTrialStyleKnobs,
    );
    // Position-path closed-loop (signed foot − gain×V blend) — full scale.
    goalZ = p1aPlaceSwingGroundSpeedMatchZM({
      commandedForwardM: state.commandedStepForwardM,
      plantWorldZM: state.stepTargetWorldZM,
      swingFootZM: swingFoot.z,
      footHeightAbovePlantM: heightAbovePlantM,
      freezeAbovePlantM: freezeH,
      swingFootVzMps: footVz,
      retractGainS,
      scale: retractScale,
    });
    // Lateral nulling (same law on X).
    goalX = p1aPlaceSwingGroundSpeedMatchZM({
      commandedForwardM: state.commandedStepForwardM,
      plantWorldZM: state.stepTargetWorldXM,
      swingFootZM: swingFoot.x,
      footHeightAbovePlantM: heightAbovePlantM,
      freezeAbovePlantM: freezeH,
      swingFootVzMps: footVx,
      retractGainS,
      scale: retractScale,
    });
    // Direct target-velocity term (rad): ∝ measured V error (target 0),
    // height-indexed, no timers. Bypasses position maxBias when |V| is large.
    // Sign: hipSag += kV·Vz (Vz>0 → +hip → −Z force; FK sag− → +Z).
    if (retractScale > 0 && heightAbovePlantM <= freezeH) {
      const bandW =
        heightAbovePlantM <= 0 ? 1 : 1 - heightAbovePlantM / freezeH;
      const w = clamp(retractScale, 0, 1) * bandW;
      // rad/(m/s); kV=2·gainS measured sweet with s=0.4/g=0.2 (tdVz≈0.07).
      const kV = 2.0 * retractGainS;
      hipVelNullRad = clamp(kV * w * footVz, -0.22, 0.22);
      hipLatVelNullRad = clamp(kV * w * footVx, -0.15, 0.15);
    }
  }
  const goal = {
    x: goalX,
    y: state.stepTargetWorldYM + liftHM,
    z: goalZ,
  };

  const errX = goal.x - swingFoot.x;
  const errZ = goal.z - swingFoot.z;
  // Latch the accepted prepare command pose once. P1A then applies relative
  // vertical-shortening deltas on that motor frame (not absolute solver angles).
  if (!state.plantedPoseActive) {
    const hipBase = commands[hipSwing];
    const kneeBase = commands[kneeSwing];
    const ankleBase = commands[ankleSwing];
    if (hipBase && kneeBase && ankleBase) {
      state.plantedSwingHipSag = hipBase.targetOffsetRad;
      state.plantedSwingHipLat =
        hipBase.targetRotationVectorRad[2] ?? 0;
      state.plantedSwingKnee = kneeBase.targetOffsetRad;
      state.plantedSwingAnkle = ankleBase.targetOffsetRad;
      state.p1aSwingAnkleLat =
        ankleBase.targetRotationVectorRad[2] ?? 0;
      state.plantedPoseActive = true;
    }
  }
  // Sagittal Z: hold/place base 2.0 (boost when commanded forward); lateral X
  // retains prior schedule (place 2.0, lift/hold 1.6). Optional trial scale.
  const swingGainScale = resolveP1ASsSwingGainScale(state.p1aTrialStyleKnobs);
  const kZ =
    p1aSwingSagittalFeedbackGainPerM(
      state.stepPhase,
      state.commandedStepForwardM,
    ) * swingGainScale;
  const kX = (state.stepPhase === 'place' ? 2.0 : 1.6) * swingGainScale;
  // Sagittal/vertical: pure two-link vertical-shortening from C2-scaled h
  // plus measured Z foot feedback (FK sag− → +Z so −kZ*errZ) plus direct
  // velocity-null term (push 18 closed loop).
  const sagDiff = p1aSwingVerticalShorteningDeltas(liftHM);
  const hipSag = clamp(
    state.plantedSwingHipSag +
      sagDiff.hipSagDeltaRad -
      kZ * errZ +
      hipVelNullRad,
    -0.85,
    0.55,
  );
  // Bounded world-X lateral feedback + direct Vx null (push 18).
  const hipLateralDelta = clamp(
    kX * errX + hipLatVelNullRad,
    -0.35,
    0.35,
  );
  const hipZ = clamp(
    state.plantedSwingHipLat + hipLateralDelta,
    -0.55,
    0.55,
  );
  // Forward SS knee floor (production = −0.55; search may open slightly).
  const kneeSagMin = p1aSwingKneeSagMinRad({
    commandedForwardM: state.commandedStepForwardM,
    liftClearanceHM: liftHM,
    forwardMinRad: resolveP1AForwardSwingKneeSagMinRad(state.p1aTrialStyleKnobs),
  });
  const kneeSag = clamp(
    state.plantedSwingKnee + sagDiff.kneeSagDeltaRad,
    kneeSagMin,
    0.12,
  );
  // Applied (post-clamp) deltas drive sole-preserving ankle counterrotation.
  const hipSagDelta = hipSag - state.plantedSwingHipSag;
  const kneeSagDelta = kneeSag - state.plantedSwingKnee;
  let ankleSag = p1aSwingAnkleSagFromChain({
    plantedAnkleRad: state.plantedSwingAnkle,
    hipSagDeltaRad: hipSagDelta,
    kneeSagDeltaRad: kneeSagDelta,
  });
  // Gate A push 19: pre-TD sole-pitch flatten. Chain counter-rotation preserves
  // latched sole relative to the shank, not world; pelvis/hip tilt leaves the
  // sole pitched at contact → edge slap + rear sweep. Closed-loop ankle servo
  // drives measured world sole pitch → 0 in the same height band as velocity
  // nulling. Production scale 0 (off).
  if (
    forwardSwing &&
    (state.stepPhase === 'place' || state.stepPhase === 'hold')
  ) {
    const measuredPitch =
      swing === 'left'
        ? observation.leftFootPitchRad
        : observation.rightFootPitchRad;
    if (typeof measuredPitch === 'number' && Number.isFinite(measuredPitch)) {
      const heightAbovePlantM = swingFoot.y - state.stepTargetWorldYM;
      const freezeH =
        resolveP1APlaceCaptureFreezeM(state.p1aTrialStyleKnobs) *
        (state.stepPhase === 'hold' ? 3 : 1);
      const flattenDelta = p1aPlaceSwingSoleFlattenAnkleDeltaRad({
        commandedForwardM: state.commandedStepForwardM,
        footHeightAbovePlantM: heightAbovePlantM,
        freezeAbovePlantM: freezeH,
        footPitchRad: measuredPitch,
        gain: resolveP1APlaceSwingSoleFlattenGain(state.p1aTrialStyleKnobs),
        scale: resolveP1APlaceSwingSoleFlattenScale(state.p1aTrialStyleKnobs),
      });
      ankleSag = clamp(
        ankleSag + flattenDelta,
        -P1A_SWING_ANKLE_SAG_MAX_RAD,
        P1A_SWING_ANKLE_SAG_MAX_RAD,
      );
    }
  }
  const ankleLat = clamp(
    state.p1aSwingAnkleLat - hipLateralDelta,
    -0.55,
    0.55,
  );

  const hipTorque = (config.baseMaxTorqueNm[hipSwing] ?? 900) * swingCap * 1.35;
  const kneeTorque = (config.baseMaxTorqueNm[kneeSwing] ?? 230) * swingCap * 1.35;
  const ankleTorque = (config.baseMaxTorqueNm[ankleSwing] ?? 55) * swingCap * 1.25;
  setJointCmd(commands, hipSwing, hipSag, hipZ, hipTorque, 1);
  setJointCmd(commands, kneeSwing, kneeSag, 0, kneeTorque, 1);
  setJointCmd(
    commands,
    ankleSwing,
    ankleSag,
    ankleLat,
    ankleTorque,
    1,
  );
  // Lift/hold/place swing speed cap (canonical reachability: 2.25 rad/s).
  applyP1ASwingMotorSpeedCap(commands, [
    hipSwing,
    kneeSwing,
    ankleSwing,
  ]);

  // Stance column: single-support law (not dual-support flex-the-stance map).
  applyP1AStanceSupportColumn(commands, state, config, observation);
}

/**
 * Override swing/stance joint targets for the active capture step.
 * Prepare: both feet planted, COM shift to stance. Lift/place: IK + strong stance brace.
 * Explicit Gate A: stance/load use live hip→fixed-world-foot plant motors.
 * P1A: in-place transfer + low lift/hold/replace (no forward lean).
 */
function applyCaptureStepJointOverrides(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  state: MuscleControllerState,
  config: MuscleControllerConfig,
  observation: MuscleObservation,
): void {
  if (state.stepPhase === 'none' || !state.swingSide) return;

  const swing = state.swingSide;
  const stance: MuscleSwingSide = swing === 'left' ? 'right' : 'left';
  const hipSwing = swing === 'left' ? 'leftHip' : 'rightHip';
  const kneeSwing = swing === 'left' ? 'leftKnee' : 'rightKnee';
  const ankleSwing = swing === 'left' ? 'leftAnkle' : 'rightAnkle';
  const hipStance = stance === 'left' ? 'leftHip' : 'rightHip';
  const kneeStance = stance === 'left' ? 'leftKnee' : 'rightKnee';
  const ankleStance = stance === 'left' ? 'leftAnkle' : 'rightAnkle';

  const { leftHip, rightHip, leftFoot, rightFoot } = observationFeet(observation);
  const hip = swing === 'left' ? leftHip : rightHip;
  const swingFoot = swing === 'left' ? leftFoot : rightFoot;
  const liftU = config.stepLiftS > 1e-9 ? clamp(state.stepPhaseElapsedS / config.stepLiftS, 0, 1) : 1;
  const placeU = config.stepPlaceS > 1e-9 ? clamp(state.stepPhaseElapsedS / config.stepPlaceS, 0, 1) : 1;
  const explicitPlant = state.explicitStepActive && state.stancePlantLatched;
  const p1a = state.singleSupportHoldActive;

  // --- prepare: load transfer, no swing lift ---
  if (state.stepPhase === 'prepare') {
    const stanceFootLive = stance === 'left' ? leftFoot : rightFoot;

    // P1A: support-reference balance already owns the COM transfer objective.
    // Stage-1 prepare ankle-sat attribution: stance ankle roll monopolizes
    // lateral demand (hip share ~9%, hips unsaturated) and saturates 0.317 s
    // above the CoP-useful ceiling. Rebalance only: cap ankle roll + route
    // residual shift through calibrated hip laterals. No stance brace / plant IK.
    if (p1a) {
      applyP1APrepareLateralLoadShareCommands(commands, state, observation);
      return;
    }

    // Gate A / capture: cp16 lateral COM→stance brace + mild forward midfoot bias.
    // Explicit: still applies stance plant IK so the latched sole stays put.
    const explicitForward =
      state.explicitStepActive && state.commandedStepForwardM > 0;
    applyStanceBraceCommands(commands, swing, config, {
      comShift: true,
      comX: observation.centerOfMass[0],
      stanceFootX: stanceFootLive.x,
      stanceHipSag: explicitForward ? 0.14 : undefined,
      spineSag: explicitForward ? 0.08 : undefined,
    });
    if (explicitForward) {
      // Mild stance ankle plantarflex for midfoot load (joint motors only).
      const stanceCap = commands[ankleStance]?.capacity ?? clamp01(config.strength);
      setJointCmd(
        commands,
        ankleStance,
        0.1,
        0,
        (config.baseMaxTorqueNm[ankleStance] ?? 70) * stanceCap * 1.5,
        1,
      );
    }
    // Gate A only: fixed-world stance plant IK during double-support prepare.
    if (explicitPlant) {
      const stanceHipPos = stance === 'left' ? leftHip : rightHip;
      const plant = plantLegJointTargets({
        hip: stanceHipPos,
        foot: stanceFootLive,
        footTarget: {
          x: state.stancePlantWorldXM,
          y: state.stancePlantWorldYM,
          z: state.stancePlantWorldZM,
        },
        side: stance,
        plantGain: 2.2,
      });
      const stanceCap = commands[kneeStance]?.capacity ?? clamp01(config.strength);
      // Knee plant hold; ankle: blend plantarflex midfoot load with plant IK.
      setJointCmd(
        commands,
        kneeStance,
        clamp(0.5 * plant.knee + 0.5 * 0.12, -0.3, 0.14),
        0,
        (config.baseMaxTorqueNm[kneeStance] ?? 250) * stanceCap * 1.55,
        1,
      );
      const ankleCmd = commands[ankleStance];
      const ankleSag = ankleCmd
        ? clamp(0.45 * ankleCmd.targetOffsetRad + 0.55 * plant.ankle, -0.14, 0.14)
        : plant.ankle;
      setJointCmd(
        commands,
        ankleStance,
        ankleSag,
        0,
        (config.baseMaxTorqueNm[ankleStance] ?? 70) * stanceCap * 1.55,
        1,
      );
      // Sagittal plant correction only — keep brace hip laterals for COM transfer.
      const hipCmd = commands[hipStance];
      if (hipCmd) {
        setJointCmd(
          commands,
          hipStance,
          clamp(0.7 * hipCmd.targetOffsetRad + 0.3 * plant.hipSag, -0.9, 0.3),
          hipCmd.targetRotationVectorRad[2] ?? 0,
          Math.max(hipCmd.maxTorqueNm, (config.baseMaxTorqueNm[hipStance] ?? 1000) * stanceCap * 1.55),
          1,
        );
      }
    }
    return;
  }

  // P1A lift/hold/place: low apex (0.03 m) over original plant; coherent base stance.
  // Place contact-candidate: both contact flags → existing P1A symmetric
  // load-acceptance motors while remaining in place (state-machine
  // impulse+geometry gate still owns place→load).
  if (p1a && (state.stepPhase === 'lift' || state.stepPhase === 'hold' || state.stepPhase === 'place')) {
    if (state.stepPhase === 'place') {
      const stanceFootLive = stance === 'left' ? leftFoot : rightFoot;
      const stanceContact = footGroundedFromObservation(
        observation,
        stance,
        stanceFootLive.y,
      );
      const swingContact = footGroundedFromObservation(
        observation,
        swing,
        swingFoot.y,
      );
      if (
        shouldApplyP1APlaceContactCandidateLoadMotor({
          stepPhase: state.stepPhase,
          singleSupportHoldActive: state.singleSupportHoldActive,
          stanceContact,
          swingContact,
        })
      ) {
        // Same plant-owned ankle lat clear as load; P1A symmetric path only.
        state.p1aSwingAnkleLat = 0;
        applyP1AInPlaceLoadAcceptanceCommands(
          commands,
          state,
          config,
          observation,
        );
        return;
      }
    }
    applyP1ASingleSupportJointOverrides(commands, state, config, observation);
    return;
  }

  // Explicit Gate A steps use a lower swing apex so peak clearance stays in
  // the 0.04–0.10 m band; capture recovery keeps the taller 0.10 m lift.
  const liftClearanceM = state.explicitStepActive ? 0.05 : 0.1;
  const liftTarget = {
    x: state.stepTargetWorldXM,
    y: state.stepTargetWorldYM + liftClearanceM,
    z: state.stepTargetWorldZM,
  };
  const placeTarget = {
    x: state.stepTargetWorldXM,
    y: state.stepTargetWorldYM,
    z: state.stepTargetWorldZM,
  };

  // Load: both legs track fixed world plants via joint motors only.
  // Gate A / capture: front/rear forward-step plant (applyExplicitDoubleSupport…).
  // P1A: symmetric in-place plants (applyP1AInPlaceLoadAcceptanceCommands).
  if (
    state.stepPhase === 'load' &&
    shouldApplyExplicitDoubleSupportPlant({
      stepPhase: state.stepPhase,
      explicitStepActive: state.explicitStepActive,
      stancePlantLatched: state.stancePlantLatched,
      singleSupportHoldActive: state.singleSupportHoldActive,
    })
  ) {
    // Release P1A swing lateral latch; plant IK owns both ankles during load.
    state.p1aSwingAnkleLat = 0;
    if (state.singleSupportHoldActive) {
      applyP1AInPlaceLoadAcceptanceCommands(
        commands,
        state,
        config,
        observation,
      );
    } else {
      applyExplicitDoubleSupportPlantCommands(commands, state, config, observation);
    }
    return;
  }

  let hipSag = 0;
  let hipZ = 0;
  let kneeSag = 0;
  let ankleSag = 0;
  let act = 0.85;

  if (state.stepPhase === 'lift' || state.stepPhase === 'place') {
    const goal = state.stepPhase === 'lift' ? liftTarget : placeTarget;
    const ik = solveTwoLinkLegIk({ hip, footTarget: goal, swingSide: swing });
    const errX = goal.x - swingFoot.x;
    const errY = goal.y - swingFoot.y;
    const errZ = goal.z - swingFoot.z;
    // FK: sag− → +Z, lat+ → +X. Track per-axis error with configurable gains.
    const kXZ =
      state.stepPhase === 'lift' ? config.stepLiftGainXZ : config.stepPlaceGainXZ;
    const kY = state.stepPhase === 'lift' ? 0.9 : config.stepPlaceGainY;
    hipSag = clamp(ik.hipSagittalRad - kXZ * errZ, -1.05, 0.4);
    hipZ = clamp(ik.hipLateralRad + kXZ * errX, -0.6, 0.6);
    kneeSag = clamp(ik.kneeRad - kY * errY, -1.15, 0.08);
    if (state.stepPhase === 'lift') {
      const u = Math.max(liftU, 0.4);
      hipSag *= u;
      hipZ *= 0.85 * u;
      kneeSag = clamp(Math.min(kneeSag, -0.2) * u, -1.0, -0.15);
      // Explicit: lower lift ankle kick so peak sole clearance stays ≤0.10 m.
      ankleSag = (state.explicitStepActive ? 0.04 : 0.1) * u;
    } else {
      // Place: keep full horizontal servo; gradually plant knee only when near target.
      const horiz = Math.hypot(errX, errZ);
      if (placeU > 0.35 && horiz < 0.14) {
        const blend = clamp((placeU - 0.35) / 0.65, 0, 1);
        kneeSag = clamp(kneeSag * (1 - blend) + -0.04 * blend, -0.75, 0.05);
      }
      // Explicit place: soft flat-foot approach (reduces bounce/end-begin thrash).
      if (state.explicitStepActive) {
        const approach = clamp(1 - horiz / 0.18, 0, 1);
        hipSag = clamp(hipSag * (1 - 0.25 * approach), -1.0, 0.25);
        kneeSag = clamp(kneeSag * (1 - 0.35 * approach) + -0.05 * approach, -0.85, 0.05);
        ankleSag = clamp(ik.ankleRad * 0.25 * (1 - approach), -0.1, 0.04);
      } else {
        ankleSag = clamp(ik.ankleRad - 0.05 * placeU, -0.2, 0.15);
      }
    }
    act = 1;
  } else if (state.plantedPoseActive) {
    hipSag = state.plantedSwingHipSag;
    hipZ = state.plantedSwingHipLat;
    kneeSag = state.plantedSwingKnee;
    ankleSag = state.plantedSwingAnkle;
    act = 0.95;
  } else {
    const ik = solveTwoLinkLegIk({ hip, footTarget: placeTarget, swingSide: swing });
    hipSag = ik.hipSagittalRad * 0.5;
    hipZ = ik.hipLateralRad;
    kneeSag = -0.06;
    ankleSag = 0.04;
    act = 0.95;
  }

  const swingCap = commands[hipSwing]?.capacity ?? clamp01(config.strength);
  const stanceCap = commands[hipStance]?.capacity ?? clamp01(config.strength);
  // Explicit place near target: lower swing torque to reduce sole bounce thrash.
  let stepBoost = config.stepSwingTorqueScale;
  if (state.explicitStepActive && state.stepPhase === 'place') {
    const near =
      Math.hypot(state.stepTargetWorldXM - swingFoot.x, state.stepTargetWorldZM - swingFoot.z) < 0.1;
    if (near) stepBoost = Math.min(stepBoost, 1.25);
  }
  const hipTorque = (config.baseMaxTorqueNm[hipSwing] ?? 900) * swingCap * stepBoost;
  const kneeTorque = (config.baseMaxTorqueNm[kneeSwing] ?? 230) * swingCap * stepBoost;
  const ankleTorque = (config.baseMaxTorqueNm[ankleSwing] ?? 55) * swingCap * stepBoost;

  setJointCmd(commands, hipSwing, hipSag, hipZ, hipTorque, act);
  setJointCmd(commands, kneeSwing, kneeSag, 0, kneeTorque, act);
  setJointCmd(commands, ankleSwing, ankleSag, 0, ankleTorque, act);

  // Strong stance/trunk throughout flight and plant (never symmetric neutral).
  const stanceDirX = stance === 'right' ? 1 : -1;
  const stanceHipTorque = (config.baseMaxTorqueNm[hipStance] ?? 900) * stanceCap * 1.55;
  const stanceKneeTorque = (config.baseMaxTorqueNm[kneeStance] ?? 230) * stanceCap * 1.55;
  const stanceAnkleTorque = (config.baseMaxTorqueNm[ankleStance] ?? 55) * stanceCap * 1.45;
  if (state.stepPhase === 'load' && state.plantedPoseActive) {
    // Capture recovery load (non-explicit): keep planted asymmetric pose.
    const midX = (leftFoot.x + rightFoot.x) * 0.5;
    const loadLat = prepareLateralTargets({
      comX: observation.centerOfMass[0],
      stanceFootX: midX,
      maxLat: 0.18,
      gainPerM: 1.6,
    });
    setJointCmd(
      commands,
      hipSwing,
      state.plantedSwingHipSag,
      state.plantedSwingHipLat + loadLat.swingHipLat * 0.5,
      hipTorque,
      1,
    );
    setJointCmd(commands, kneeSwing, state.plantedSwingKnee, 0, kneeTorque, 1);
    setJointCmd(commands, ankleSwing, state.plantedSwingAnkle, 0, ankleTorque, 1);
    setJointCmd(
      commands,
      hipStance,
      state.plantedStanceHipSag,
      state.plantedStanceHipLat + loadLat.stanceHipLat,
      stanceHipTorque,
      1,
    );
    setJointCmd(commands, kneeStance, state.plantedStanceKnee, 0, stanceKneeTorque, 1);
    setJointCmd(commands, ankleStance, state.plantedStanceAnkle, 0, stanceAnkleTorque, 1);
    const spineCap = commands.spine?.capacity ?? clamp01(config.strength);
    setJointCmd(
      commands,
      'spine',
      state.plantedSpineSag,
      state.plantedSpineLat + loadLat.spineLat,
      (config.baseMaxTorqueNm.spine ?? 650) * spineCap * 1.35,
      0.95,
    );
  } else {
    // Lift/place stance brace (baseline open-loop). Explicit steps add mild
    // ankle/knee plant feedback toward the latched world plant without
    // replacing hip brace (full plant IK during swing degraded foot advance).
    setJointCmd(commands, hipStance, 0.12, stanceDirX * 0.1, stanceHipTorque, 1);
    setJointCmd(commands, kneeStance, 0.14, 0, stanceKneeTorque, 1);
    setJointCmd(commands, ankleStance, 0.08, 0, stanceAnkleTorque, 1);
    if (explicitPlant) {
      const stanceHipPos = stance === 'left' ? leftHip : rightHip;
      const stanceFootLive = stance === 'left' ? leftFoot : rightFoot;
      const plant = plantLegJointTargets({
        hip: stanceHipPos,
        foot: stanceFootLive,
        footTarget: {
          x: state.stancePlantWorldXM,
          y: state.stancePlantWorldYM,
          z: state.stancePlantWorldZM,
        },
        side: stance,
        plantGain: 1.6,
      });
      setJointCmd(
        commands,
        kneeStance,
        clamp(0.35 * plant.knee + 0.65 * 0.14, -0.25, 0.16),
        0,
        stanceKneeTorque,
        1,
      );
      setJointCmd(
        commands,
        ankleStance,
        clamp(0.55 * plant.ankle + 0.45 * 0.08, -0.14, 0.12),
        0,
        stanceAnkleTorque,
        1,
      );
    }
    const counterLat = clamp(-hipZ * 0.35 + stanceDirX * 0.08, -0.28, 0.28);
    const counterSag = clamp(-state.stepTargetZM * 0.25, -0.2, 0.2);
    const spineCap = commands.spine?.capacity ?? clamp01(config.strength);
    setJointCmd(
      commands,
      'spine',
      counterSag,
      counterLat,
      (config.baseMaxTorqueNm.spine ?? 650) * spineCap * 1.3,
      0.95,
    );
  }
}

/**
 * Persist the planted-support motor pose after the capture step ends.
 * Explicit Gate A: live double-support plant IK (not a frozen pose that unplants).
 * Capture recovery: frozen planted joint targets. Cleared after recovered dwell/reset.
 */
function applyPlantedSupportPose(
  commands: Partial<Record<HumanoidJointName, MuscleJointCommand>>,
  state: MuscleControllerState,
  config: MuscleControllerConfig,
  observation?: MuscleObservation,
): void {
  if (!state.plantedPoseActive || !state.swingSide) return;

  // Explicit Gate A one-step (phase none): keep both feet on fixed world plants.
  // P1A: shouldApply is load-only, so phase none does not take this path.
  if (
    observation &&
    shouldApplyExplicitDoubleSupportPlant({
      stepPhase: state.stepPhase,
      explicitStepActive: state.explicitStepActive,
      stancePlantLatched: state.stancePlantLatched,
      singleSupportHoldActive: state.singleSupportHoldActive,
    })
  ) {
    applyExplicitDoubleSupportPlantCommands(commands, state, config, observation);
    return;
  }

  const swing = state.swingSide;
  const stance: MuscleSwingSide = swing === 'left' ? 'right' : 'left';
  const hipSwing = swing === 'left' ? 'leftHip' : 'rightHip';
  const kneeSwing = swing === 'left' ? 'leftKnee' : 'rightKnee';
  const ankleSwing = swing === 'left' ? 'leftAnkle' : 'rightAnkle';
  const hipStance = stance === 'left' ? 'leftHip' : 'rightHip';
  const kneeStance = stance === 'left' ? 'leftKnee' : 'rightKnee';
  const ankleStance = stance === 'left' ? 'leftAnkle' : 'rightAnkle';

  const swingCap = commands[hipSwing]?.capacity ?? clamp01(config.strength);
  const stanceCap = commands[hipStance]?.capacity ?? clamp01(config.strength);
  const spineCap = commands.spine?.capacity ?? clamp01(config.strength);

  setJointCmd(
    commands,
    hipSwing,
    state.plantedSwingHipSag,
    state.plantedSwingHipLat,
    (config.baseMaxTorqueNm[hipSwing] ?? 900) * swingCap,
    0.95,
  );
  setJointCmd(
    commands,
    kneeSwing,
    state.plantedSwingKnee,
    0,
    (config.baseMaxTorqueNm[kneeSwing] ?? 230) * swingCap,
    0.95,
  );
  setJointCmd(
    commands,
    ankleSwing,
    state.plantedSwingAnkle,
    0,
    (config.baseMaxTorqueNm[ankleSwing] ?? 55) * swingCap,
    0.9,
  );
  setJointCmd(
    commands,
    hipStance,
    state.plantedStanceHipSag,
    state.plantedStanceHipLat,
    (config.baseMaxTorqueNm[hipStance] ?? 900) * stanceCap,
    1,
  );
  setJointCmd(
    commands,
    kneeStance,
    state.plantedStanceKnee,
    0,
    (config.baseMaxTorqueNm[kneeStance] ?? 230) * stanceCap,
    1,
  );
  setJointCmd(
    commands,
    ankleStance,
    state.plantedStanceAnkle,
    0,
    (config.baseMaxTorqueNm[ankleStance] ?? 55) * stanceCap,
    0.95,
  );
  setJointCmd(
    commands,
    'spine',
    state.plantedSpineSag,
    state.plantedSpineLat,
    (config.baseMaxTorqueNm.spine ?? 650) * spineCap,
    0.9,
  );
}

function capacityForGroup(group: MuscleGroupName, config: MuscleControllerConfig, fatigue: number): number {
  const fatigueFactor = 1 - fatigue * config.fatigueTorqueScale;
  let capacity = clamp01(config.strength) * clamp01(fatigueFactor);
  if (config.weakLeftLeg && isLeftLegGroup(group)) {
    capacity *= config.weakLeftLegCapacity;
  }
  return clamp01(capacity);
}

/**
 * Bounded signed component from a horizontal COM/demand error axis.
 * Magnitude depends only on |error| and leanScale (not per-joint activation),
 * so left/right and forward/back equal-magnitude errors produce equal |component|
 * and only the sign flips. Always ≤ maxOffset.
 */
function boundedErrorComponent(
  error: number,
  maxOffset: number,
  leanScale: number,
  jointSign: number,
  errorGainPerM: number,
): number {
  if (jointSign === 0 || error === 0) return 0;
  const magnitude =
    Math.min(Math.abs(error) * errorGainPerM, 1) * maxOffset * Math.max(leanScale, 0.35);
  if (magnitude <= 1e-12) return 0;
  const sign = error > 0 ? 1 : -1;
  return clamp(jointSign * sign * magnitude, -maxOffset, maxOffset);
}

/**
 * Lateral (local Z) target for spherical joints from desired.errorX.
 * +errorX (COM right) → +Z on rightHip, −Z on leftHip, counter-lean on spine.
 */
function lateralTargetRad(
  joint: HumanoidJointName,
  errorX: number,
  maxOffset: number,
  leanScale: number,
  errorGainPerM: number,
): number {
  if (!isMuscleSphericalJoint(joint)) return 0;
  let jointSign = 0;
  if (joint === 'rightHip') jointSign = 1;
  else if (joint === 'leftHip') jointSign = -1;
  else if (joint === 'spine') jointSign = -1;
  return boundedErrorComponent(errorX, maxOffset, leanScale, jointSign, errorGainPerM);
}

/**
 * Sagittal (local X) target for spherical joints from desired.errorZ.
 *
 * Rig: parent-local +X = hip/spine extension, −X = flexion (see TUCK_POSE).
 * +errorZ (COM forward) → +X (extend hips/spine to push COM back).
 * −errorZ (COM aft) → −X (flex to bring trunk forward over the feet).
 */
function sphericalSagittalFromErrorZ(
  errorZ: number,
  maxOffset: number,
  leanScale: number,
  errorGainPerM: number,
): number {
  return boundedErrorComponent(errorZ, maxOffset, leanScale, +1, errorGainPerM);
}

/**
 * Signed hinge / sagittal target from antagonistic flex/ext effort.
 *
 * Motor convention (matches spherical): +targetOffsetRad = extend / plantarflex;
 * −targetOffsetRad = flex / dorsiflex. Extensor effort therefore produces a
 * positive offset. Used for revolute knees/ankles.
 */
function hingeSagittalFromActivation(
  act: MuscleGroupActivation,
  maxOffset: number,
  leanScale: number,
): number {
  const net = act.extensor - act.flexor; // +extensor → +offset (extend)
  const activation = Math.max(act.flexor, act.extensor);
  return clamp(
    net * maxOffset * Math.max(leanScale, activation),
    -maxOffset,
    maxOffset,
  );
}

function buildJointCommands(
  state: MuscleControllerState,
  desired: DesiredActivations,
  config: MuscleControllerConfig,
): Partial<Record<HumanoidJointName, MuscleJointCommand>> {
  const commands: Partial<Record<HumanoidJointName, MuscleJointCommand>> = {};
  const leanScale = clamp01(desired.supportError * 4 + desired.tiltDeg / 40 + 0.25);
  const maxOffset = config.maxJointOffsetRad;
  const errorGain = config.errorToOffsetGainPerM;

  for (const group of MUSCLE_GROUP_NAMES) {
    const joint = groupToJoint(group);
    const act = state.activations[group];
    const activation = Math.max(act.flexor, act.extensor);
    const capacity = capacityForGroup(group, config, state.fatigue);
    const baseTorque = config.baseMaxTorqueNm[joint] ?? 0;
    // Hold nearly full torque budget at stance; do not starve pose-hold authority.
    const effort = Math.max(activation, config.stanceTone, 0.2);
    const maxTorqueNm = baseTorque * capacity * clamp01(0.75 + 0.25 * effort);

    // Spherical hips/spine: X from demandZ (sagittal), Z from demandX (lateral).
    // Ankles: pure pitch on X + pure roll on Z (cp20); no lateral mixed into pitch.
    // Revolute knees: hinge scalar only.
    let sagittalRad: number;
    let lateralRad = 0;
    if (joint === 'leftHip' || joint === 'rightHip' || joint === 'spine') {
      // Control error (geometry + integral) for targets; gate on geometric lean
      // with a quiet-stance floor so lateral hip motors stay online at small SE.
      const hipGate = Math.max(
        config.hipGateFloor,
        clamp01((desired.supportError - 0.02) / 0.12),
      );
      sagittalRad =
        sphericalSagittalFromErrorZ(desired.controlErrorZ, maxOffset, leanScale, errorGain) * hipGate;
      sagittalRad += config.stanceHipSagittalOffsetRad;
      // Lateral uses full errorGain (was 0.75×) — narrow feet need equal lateral authority.
      lateralRad =
        lateralTargetRad(joint, desired.controlErrorX, maxOffset * 0.85, leanScale, errorGain) *
        hipGate;
      sagittalRad = clamp(sagittalRad, -maxOffset, maxOffset);
    } else if (joint === 'leftAnkle' || joint === 'rightAnkle') {
      // Ankle PD: pitch (X) from sagittal error; roll (Z) from lateral error.
      // ankleErrorSign: +1 maps +errorZ → +offset (plantarflex); −1 flips polarity.
      const sign = config.ankleErrorSign;
      const pdPitch = sign * desired.controlErrorZ * errorGain * 0.55;
      const fromAct = hingeSagittalFromActivation(act, maxOffset, leanScale) * sign;
      sagittalRad = clamp(
        pdPitch * 0.75 + fromAct * 0.25 + config.stanceAnkleOffsetRad,
        -maxOffset,
        maxOffset,
      );
      // L/R mirror: +errorX (COM right of support) → right ankle +roll, left −roll.
      const side = joint === 'rightAnkle' ? 1 : -1;
      lateralRad = clamp(
        sign * desired.controlErrorX * errorGain * 0.55 * side,
        -maxOffset * 0.85,
        maxOffset * 0.85,
      );
    } else {
      // Knees: activation + strong extension bias against buckle + neutral flex.
      sagittalRad = hingeSagittalFromActivation(act, maxOffset, leanScale);
      sagittalRad = clamp(
        Math.max(sagittalRad, 0.03 * effort) + config.stanceKneeOffsetRad,
        -maxOffset,
        maxOffset,
      );
    }

    const targetRotationVectorRad: readonly [number, number, number] = isMuscleSphericalJoint(joint)
      ? [sagittalRad, 0, lateralRad]
      : [sagittalRad, 0, 0];

    commands[joint] = {
      targetOffsetRad: sagittalRad,
      targetRotationVectorRad,
      maxTorqueNm,
      capacity,
      activation,
    };
  }

  // Neutral hold for upper body (in MUSCLE_DRIVEN_JOINTS, not balance groups):
  // zero offset → STAND_POSE via the motor bridge so free-falling T-pose limbs
  // do not yank COM.
  //
  // Gate A push 8: commanded forward steps pre-settle arms to natural hang
  // (−1.1) under ordinary balance via soft-slewed targets, then keep hang +
  // low torque through the whole step. Zero-forward P1A identity: no production
  // hang. Search knobs still apply on any singleSupportHoldActive window.
  const productionHangActive =
    state.armPreSettleActive ||
    state.armHoldLatched ||
    (state.singleSupportHoldActive && state.commandedStepForwardM > 1e-4);
  const knobHoldActive = state.singleSupportHoldActive === true;
  const armHoldActive = productionHangActive || knobHoldActive;
  // Soft-slewed hang targets (updated in pre-settle; snapped to full hang
  // when prepare begins). Production uses armShoulderTargetRad / armElbowTargetRad.
  const armShoulderSag = productionHangActive ? state.armShoulderTargetRad : 0;
  const armElbowFlex = productionHangActive ? state.armElbowTargetRad : 0;
  // Search knobs can override when set (any single-support hold, incl. P1A 0).
  const knobHold = resolveP1AArmHoldTargets({
    armHoldActive: knobHoldActive,
    singleSupportHoldActive: state.singleSupportHoldActive,
    commandedStepForwardM: Math.max(state.commandedStepForwardM, 1e-3),
    knobs: state.p1aTrialStyleKnobs,
    ramp01: 1,
    suppressProductionDefaults: true, // knobs only; production via slew state
  });
  const finalShoulder =
    knobHold.shoulderSagRad !== 0 ? knobHold.shoulderSagRad : armShoulderSag;
  const finalElbow =
    knobHold.elbowFlexRad !== 0 ? knobHold.elbowFlexRad : armElbowFlex;
  // Per-joint hang: only joints with non-zero hang targets get hang torque /
  // speed caps. Zero-target shoulders must keep default budgets — lowering
  // torque on target-0 shoulders frees the T-pose residual and collapses plant.
  const shoulderHang = Math.abs(finalShoulder) > 1e-4;
  const elbowHang = Math.abs(finalElbow) > 1e-4;
  const hangPoseActive = armHoldActive && (shoulderHang || elbowHang);
  const armSpeedCap = state.armPreSettleActive
    ? P1A_FORWARD_ARM_PREPARE_MAX_SPEED_RAD_S
    : P1A_FORWARD_ARM_MAX_SPEED_RAD_S;
  for (const joint of MUSCLE_DRIVEN_JOINTS) {
    if (commands[joint]) continue;
    const cap = clamp01(config.strength) * (1 - state.fatigue * config.fatigueTorqueScale);
    let baseTorque = config.baseMaxTorqueNm[joint] ?? 20;
    const isShoulder = joint === 'leftShoulder' || joint === 'rightShoulder';
    const isElbow = joint === 'leftElbow' || joint === 'rightElbow';
    const jointHang =
      armHoldActive && ((isShoulder && shoulderHang) || (isElbow && elbowHang));
    // Hang path: approach / hold budgets at residual≈0.
    // Non-hang explicit step (P1A zero-forward identity): keep 480/220 boost so
    // T-pose residual does not free-flail (certified plant depends on this).
    if (jointHang) {
      if (state.armPreSettleActive) {
        baseTorque = isShoulder
          ? P1A_FORWARD_ARM_PRESETTLE_SHOULDER_TORQUE_NM
          : P1A_FORWARD_ARM_PRESETTLE_ELBOW_TORQUE_NM;
      } else {
        baseTorque = isShoulder
          ? resolveP1AArmShoulderHangTorqueNm(state.p1aTrialStyleKnobs)
          : resolveP1AArmElbowHangTorqueNm(state.p1aTrialStyleKnobs);
      }
    } else if (state.explicitStepActive && (isShoulder || isElbow)) {
      baseTorque = Math.max(baseTorque, isShoulder ? 480 : 220);
    }
    let targetOffsetRad = 0;
    let targetRotationVectorRad: readonly [number, number, number] = [0, 0, 0];
    if (isShoulder && finalShoulder !== 0) {
      targetOffsetRad = finalShoulder;
      targetRotationVectorRad = [finalShoulder, 0, 0];
    } else if (isElbow && finalElbow !== 0) {
      targetOffsetRad = finalElbow;
      targetRotationVectorRad = [finalElbow, 0, 0];
    }
    const cmd: MuscleJointCommand = {
      targetOffsetRad,
      targetRotationVectorRad,
      maxTorqueNm: baseTorque * clamp01(cap),
      capacity: clamp01(cap),
      activation: config.stanceTone,
    };
    if (jointHang) {
      cmd.maxSpeedRadS = armSpeedCap;
    }
    commands[joint] = cmd;
  }

  // Spine counter only during pre-settle (not through prepare/step) — measured
  // to interfere with prepare load-transfer if left on during the step.
  if (
    hangPoseActive &&
    state.armPreSettleActive &&
    finalShoulder !== 0 &&
    commands.spine
  ) {
    const scale = Math.min(1, Math.abs(finalShoulder) / 1.1);
    const counter = P1A_FORWARD_ARM_SPINE_COUNTER_SAG_RAD * scale;
    const sp = commands.spine;
    const sag = clamp((sp.targetOffsetRad ?? 0) + counter, -0.35, 0.35);
    const lat = sp.targetRotationVectorRad[2] ?? 0;
    commands.spine = {
      ...sp,
      targetOffsetRad: sag,
      targetRotationVectorRad: [sag, 0, lat],
    };
  }

  return commands;
}

function groupActivationSnapshot(
  activations: Record<MuscleGroupName, MuscleGroupActivation>,
): Record<MuscleGroupName, number> {
  const out = {} as Record<MuscleGroupName, number>;
  for (const name of MUSCLE_GROUP_NAMES) {
    const a = activations[name];
    out[name] = Math.max(a.flexor, a.extensor);
  }
  return out;
}

function peakAndMean(groupActivation: Record<MuscleGroupName, number>): { peak: number; mean: number } {
  let peak = 0;
  let sum = 0;
  for (const name of MUSCLE_GROUP_NAMES) {
    const v = groupActivation[name];
    if (v > peak) peak = v;
    sum += v;
  }
  return { peak, mean: sum / MUSCLE_GROUP_NAMES.length };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create a fresh controller state. `seed` is stored for deterministic
 * evaluation; the pure balance law itself is seed-free (no RNG). Resetting
 * with the same seed always clears activation/fatigue identically.
 */
function emptyStepFields(): Pick<
  MuscleControllerState,
  | 'stepPhase'
  | 'stepPhaseElapsedS'
  | 'stepArmed'
  | 'recoveredDwellS'
  | 'swingSide'
  | 'stepTargetXM'
  | 'stepTargetZM'
  | 'stepTargetWorldXM'
  | 'stepTargetWorldYM'
  | 'stepTargetWorldZM'
  | 'stepLandingErrorM'
  | 'supportSpanBeforeM'
  | 'supportSpanAfterM'
  | 'prepareDurationS'
  | 'comToStanceM'
  | 'minComToStanceM'
  | 'comLateralToStanceM'
  | 'comSagittalToStanceM'
  | 'minComLateralToStanceM'
  | 'minComSagittalToStanceM'
  | 'swingLiftM'
  | 'prepareAborted'
  | 'prepareStancePlantErrorM'
  | 'minPrepareStancePlantErrorM'
  | 'maxPrepareStancePlantErrorM'
  | 'prepareReadyDwellS'
  | 'maxPrepareReadyDwellS'
  | 'prevLandingErrorM'
  | 'stepCount'
  | 'plantedPoseActive'
  | 'plantedSwingHipSag'
  | 'plantedSwingHipLat'
  | 'plantedSwingKnee'
  | 'plantedSwingAnkle'
  | 'plantedStanceHipSag'
  | 'plantedStanceHipLat'
  | 'plantedStanceKnee'
  | 'plantedStanceAnkle'
  | 'plantedSpineSag'
  | 'plantedSpineLat'
  | 'p1aSwingAnkleLat'
  | 'earlyLoadHipSagSlewRad'
  | 'earlyLoadHipSagSlewActive'
  | 'momentumAbsorbTouchdownComVzMps'
  | 'momentumAbsorbTouchdownLatched'
  | 'p1aPlaceOriginWorldXM'
  | 'p1aPlaceCaptureTargetFrozen'
  | 'pendingOneStep'
  | 'pendingSingleSupportHold'
  | 'pendingArmPreSettleStep'
  | 'armPreSettleActive'
  | 'armPreSettleElapsedS'
  | 'armPreSettleReadyDwellS'
  | 'armShoulderTargetRad'
  | 'armElbowTargetRad'
  | 'armHoldLatched'
  | 'explicitStepActive'
  | 'singleSupportHoldActive'
  | 'commandedStepForwardM'
  | 'commandedStepLateralM'
  | 'stepTargetClamped'
  | 'stancePlantWorldXM'
  | 'stancePlantWorldYM'
  | 'stancePlantWorldZM'
  | 'stancePlantLatched'
> {
  return {
    stepPhase: 'none',
    stepPhaseElapsedS: 0,
    stepArmed: true,
    recoveredDwellS: 0,
    swingSide: null,
    stepTargetXM: 0,
    stepTargetZM: 0,
    stepTargetWorldXM: 0,
    stepTargetWorldYM: 0,
    stepTargetWorldZM: 0,
    stepLandingErrorM: 0,
    supportSpanBeforeM: 0,
    supportSpanAfterM: 0,
    prepareDurationS: 0,
    comToStanceM: 0,
    minComToStanceM: 0,
    comLateralToStanceM: 0,
    comSagittalToStanceM: 0,
    minComLateralToStanceM: 0,
    minComSagittalToStanceM: 0,
    swingLiftM: 0,
    prepareAborted: false,
    prepareStancePlantErrorM: 0,
    minPrepareStancePlantErrorM: Number.POSITIVE_INFINITY,
    maxPrepareStancePlantErrorM: 0,
    prepareReadyDwellS: 0,
    maxPrepareReadyDwellS: 0,
    prevLandingErrorM: 0,
    stepCount: 0,
    plantedPoseActive: false,
    plantedSwingHipSag: 0,
    plantedSwingHipLat: 0,
    plantedSwingKnee: 0,
    plantedSwingAnkle: 0,
    plantedStanceHipSag: 0,
    plantedStanceHipLat: 0,
    plantedStanceKnee: 0,
    plantedStanceAnkle: 0,
    plantedSpineSag: 0,
    plantedSpineLat: 0,
    p1aSwingAnkleLat: 0,
    earlyLoadHipSagSlewRad: 0,
    earlyLoadHipSagSlewActive: false,
    momentumAbsorbTouchdownComVzMps: 0,
    momentumAbsorbTouchdownLatched: false,
    p1aPlaceOriginWorldXM: 0,
    p1aPlaceCaptureTargetFrozen: false,
    pendingOneStep: null,
    pendingSingleSupportHold: null,
    pendingArmPreSettleStep: null,
    armPreSettleActive: false,
    armPreSettleElapsedS: 0,
    armPreSettleReadyDwellS: 0,
    armShoulderTargetRad: 0,
    armElbowTargetRad: 0,
    armHoldLatched: false,
    explicitStepActive: false,
    singleSupportHoldActive: false,
    commandedStepForwardM: 0,
    commandedStepLateralM: 0,
    stepTargetClamped: false,
    stancePlantWorldXM: 0,
    stancePlantWorldYM: 0,
    stancePlantWorldZM: 0,
    stancePlantLatched: false,
  };
}

export function createMuscleControllerState(seed = 0): MuscleControllerState {
  return {
    seed: seed | 0,
    activations: emptyActivations(),
    fatigue: 0,
    phase: 'idle',
    lastPushDirection: null,
    elapsedS: 0,
    forbiddenAssistCount: 0,
    integralErrorXM: 0,
    integralErrorZM: 0,
    ...emptyStepFields(),
    p1aTrialStyleKnobs: null,
  };
}

/**
 * Deterministic reset: clears transient activation/fatigue/phase while
 * recording the supplied seed. Safe to call between evaluation trials.
 */
export function resetMuscleController(state: MuscleControllerState, seed = 0): void {
  state.seed = seed | 0;
  state.activations = emptyActivations();
  state.fatigue = 0;
  state.phase = 'idle';
  state.lastPushDirection = null;
  state.elapsedS = 0;
  state.forbiddenAssistCount = 0;
  state.integralErrorXM = 0;
  state.integralErrorZM = 0;
  Object.assign(state, emptyStepFields());
  state.p1aTrialStyleKnobs = null;
}

/**
 * Advance the muscle-inspired controller by `dt` seconds.
 *
 * Pure: mutates only `state`, never applies forces/torques/teleports.
 * Returns a snapshot suitable for telemetry + motor integration.
 *
 * When `jointMotorsAvailable` is false the output still produces joint
 * commands; the integration layer must use equal-and-opposite torque
 * impulses rather than forbidden root assists (capability fallback).
 */
export function stepMuscleController(
  state: MuscleControllerState,
  observation: MuscleObservation,
  dt: number,
  configPartial?: Partial<MuscleControllerConfig>,
  jointMotorsAvailable = true,
): MuscleControllerOutput {
  // Clear diagnostic load-policy channel for this control step (no control use).
  diagP1aLoadPolicyLateral = null;

  const config = resolveConfig(configPartial);
  const safeDt = Math.max(0, dt);
  state.elapsedS += safeDt;

  if (observation.lastPushDirection !== undefined) {
    state.lastPushDirection = observation.lastPushDirection;
  }

  const balanceObservation = p1aBalanceObservation(state, observation);

  // Geometric error + stance setpoint bias for integral (joint demand only).
  const rawErrorX =
    balanceObservation.centerOfMass[0] -
    balanceObservation.supportCenter[0] +
    config.stanceErrorBiasXM;
  const rawErrorZ =
    balanceObservation.centerOfMass[2] -
    balanceObservation.supportCenter[2] +
    config.stanceErrorBiasZM;
  const rawTilt = pelvisTiltDegFromUp(balanceObservation.pelvisUp);
  updateSupportErrorIntegral(
    state,
    rawErrorX,
    rawErrorZ,
    rawTilt,
    balanceObservation.pelvisPosition[1],
    config,
    safeDt,
  );

  const desired = computeDesiredActivations(balanceObservation, config, state);
  smoothActivations(state, desired.targets, safeDt, config);

  const groupActivation = groupActivationSnapshot(state.activations);
  const { mean } = peakAndMean(groupActivation);

  updateFatigue(state, mean, safeDt, config);

  // Capture step before phase resolution so phase can report 'stepping'.
  updateCaptureStep(state, desired, observation, config, safeDt);

  state.phase = resolvePhase(
    state,
    desired.supportError,
    desired.tiltDeg,
    observation.pelvisPosition[1],
    mean,
    config,
  );

  // Fallen: drop activation targets toward limp (still rate-limited) and abort step.
  if (state.phase === 'fallen') {
    clearStepMotion(state);
    clearPlantedPose(state);
    // Leak integral while fallen (update already leaches when not upright).
    const limp = emptyActivations();
    smoothActivations(state, limp, safeDt, config);
  }

  const jointCommands = buildJointCommands(state, desired, config);
  applyCaptureStepJointOverrides(jointCommands, state, config, observation);
  // Persist planted support after motion ends (not a timed fade to neutral).
  if (state.stepPhase === 'none' && state.plantedPoseActive) {
    applyPlantedSupportPose(jointCommands, state, config, observation);
  }
  // Reference-step feedforward blend AFTER certified feedback laws.
  // All-zero blend weights → early return (identity / byte-identical).
  applyReferenceStepFeedforwardBlend(
    jointCommands,
    state,
    config,
    diagReferenceTracking,
  );

  // During stepping, force high activation on swing-side groups for visualization.
  if (state.stepPhase !== 'none' && state.swingSide) {
    const swingAnkle = state.swingSide === 'left' ? 'leftAnkle' : 'rightAnkle';
    const swingKnee = state.swingSide === 'left' ? 'leftKnee' : 'rightKnee';
    const swingHip = state.swingSide === 'left' ? 'leftHip' : 'rightHip';
    for (const g of [swingAnkle, swingKnee, swingHip] as const) {
      state.activations[g].flexor = Math.max(state.activations[g].flexor, 0.75);
      state.activations[g].extensor = Math.max(state.activations[g].extensor, 0.55);
    }
  }

  // Diagnostic only: publish issued lateral targets/torques for load attribution.
  // Must run after every joint override; does not mutate commands.
  publishIssuedLateralCommands(jointCommands);

  const finalGroup = groupActivationSnapshot(state.activations);
  const finalStats = peakAndMean(finalGroup);

  return {
    controlMode: 'muscle-inspired',
    phase: state.phase,
    peakActivation: finalStats.peak,
    meanActivation: finalStats.mean,
    fatigue: state.fatigue,
    weakLeftLeg: config.weakLeftLeg,
    strength: config.strength,
    centerOfMass: [observation.centerOfMass[0], observation.centerOfMass[1], observation.centerOfMass[2]],
    supportCenter: [observation.supportCenter[0], observation.supportCenter[1], observation.supportCenter[2]],
    supportErrorM: desired.supportError,
    pelvisTiltDeg: desired.tiltDeg,
    lastPushDirection: state.lastPushDirection,
    forbiddenAssistCount: state.forbiddenAssistCount,
    groupActivation: finalGroup,
    activations: cloneActivations(state.activations),
    jointCommands,
    preferJointMotors: jointMotorsAvailable,
    stepPhase: state.stepPhase,
    swingSide: state.swingSide,
    stepCount: state.stepCount,
    stepTargetXM: state.stepTargetXM,
    stepTargetZM: state.stepTargetZM,
    stepTargetWorldXM: state.stepTargetWorldXM,
    stepTargetWorldYM: state.stepTargetWorldYM,
    stepTargetWorldZM: state.stepTargetWorldZM,
    stepLandingErrorM: state.stepLandingErrorM,
    supportSpanBeforeM: state.supportSpanBeforeM,
    supportSpanAfterM: state.supportSpanAfterM,
    prepareDurationS: state.prepareDurationS,
    comToStanceM: state.comToStanceM,
    minComToStanceM:
      Number.isFinite(state.minComToStanceM) && state.minComToStanceM < 1e6
        ? state.minComToStanceM
        : 0,
    comLateralToStanceM: state.comLateralToStanceM,
    comSagittalToStanceM: state.comSagittalToStanceM,
    minComLateralToStanceM:
      Number.isFinite(state.minComLateralToStanceM) && state.minComLateralToStanceM < 1e6
        ? state.minComLateralToStanceM
        : 0,
    minComSagittalToStanceM:
      Number.isFinite(state.minComSagittalToStanceM) && state.minComSagittalToStanceM < 1e6
        ? state.minComSagittalToStanceM
        : 0,
    swingLiftM: state.swingLiftM,
    prepareAborted: state.prepareAborted,
    prepareStancePlantErrorM: state.prepareStancePlantErrorM,
    minPrepareStancePlantErrorM:
      Number.isFinite(state.minPrepareStancePlantErrorM) &&
      state.minPrepareStancePlantErrorM < 1e6
        ? state.minPrepareStancePlantErrorM
        : 0,
    maxPrepareStancePlantErrorM: state.maxPrepareStancePlantErrorM,
    prepareReadyDwellS: state.prepareReadyDwellS,
    maxPrepareReadyDwellS: state.maxPrepareReadyDwellS,
    stepArmed: state.stepArmed,
    plantedPoseActive: state.plantedPoseActive,
    recoveredDwellS: state.recoveredDwellS,
    integralErrorXM: state.integralErrorXM,
    integralErrorZM: state.integralErrorZM,
    integralCorrectionXM: desired.integralCorrectionXM,
    integralCorrectionZM: desired.integralCorrectionZM,
    explicitStepActive: state.explicitStepActive,
    singleSupportHoldActive: state.singleSupportHoldActive,
    commandedStepForwardM: state.commandedStepForwardM,
    commandedStepLateralM: state.commandedStepLateralM,
    stepTargetClamped: state.stepTargetClamped,
    pendingOneStep: state.pendingOneStep !== null,
    pendingSingleSupportHold: state.pendingSingleSupportHold !== null,
  };
}

/**
 * Left-leg torque capacity relative to right-leg for the same config/fatigue.
 * Used by unit tests and the weakness evaluation gate.
 */
export function leftLegCapacityRatio(configPartial?: Partial<MuscleControllerConfig>, fatigue = 0): number {
  const config = resolveConfig(configPartial);
  const left = capacityForGroup('leftHip', config, fatigue);
  const right = capacityForGroup('rightHip', config, fatigue);
  if (right <= 1e-12) return 0;
  return left / right;
}

/**
 * Deterministic fingerprint of controller state for determinism gates.
 * Does not use Math.random or wall-clock time.
 */
export function hashMuscleControllerState(state: MuscleControllerState): string {
  const stepPhaseCode =
    state.stepPhase === 'prepare'
      ? 4
      : state.stepPhase === 'lift'
        ? 1
        : state.stepPhase === 'place'
          ? 2
          : state.stepPhase === 'load'
            ? 3
            : state.stepPhase === 'hold'
              ? 5
              : 0;
  const swingCode = state.swingSide === 'left' ? 1 : state.swingSide === 'right' ? 2 : 0;
  const parts: number[] = [
    state.seed,
    state.fatigue,
    state.elapsedS,
    state.forbiddenAssistCount,
    state.integralErrorXM,
    state.integralErrorZM,
    stepPhaseCode,
    state.stepPhaseElapsedS,
    state.stepArmed ? 1 : 0,
    state.recoveredDwellS,
    swingCode,
    state.stepTargetXM,
    state.stepTargetZM,
    state.stepTargetWorldXM,
    state.stepTargetWorldZM,
    state.stepLandingErrorM,
    state.supportSpanBeforeM,
    state.supportSpanAfterM,
    state.prepareDurationS,
    state.comToStanceM,
    state.swingLiftM,
    state.prepareAborted ? 1 : 0,
    state.prepareStancePlantErrorM,
    Number.isFinite(state.minPrepareStancePlantErrorM)
      ? state.minPrepareStancePlantErrorM
      : -1,
    state.maxPrepareStancePlantErrorM,
    state.prepareReadyDwellS,
    state.maxPrepareReadyDwellS,
    state.stepCount,
    state.plantedPoseActive ? 1 : 0,
    state.plantedSwingHipSag,
    state.plantedSwingHipLat,
    state.plantedSwingKnee,
    state.plantedSwingAnkle,
    state.plantedStanceHipSag,
    state.plantedStanceHipLat,
    state.plantedStanceKnee,
    state.plantedStanceAnkle,
    state.plantedSpineSag,
    state.plantedSpineLat,
    state.p1aSwingAnkleLat,
    state.earlyLoadHipSagSlewActive ? 1 : 0,
    state.earlyLoadHipSagSlewRad,
    state.momentumAbsorbTouchdownLatched ? 1 : 0,
    state.momentumAbsorbTouchdownComVzMps,
    state.p1aPlaceOriginWorldXM,
    state.p1aPlaceCaptureTargetFrozen ? 1 : 0,
    state.stancePlantLatched ? 1 : 0,
    state.stancePlantWorldXM,
    state.stancePlantWorldZM,
    state.explicitStepActive ? 1 : 0,
    state.singleSupportHoldActive ? 1 : 0,
    state.commandedStepForwardM,
    state.armPreSettleActive ? 1 : 0,
    state.armPreSettleElapsedS,
    state.armHoldLatched ? 1 : 0,
  ];
  for (const name of MUSCLE_GROUP_NAMES) {
    const a = state.activations[name];
    parts.push(a.flexor, a.extensor);
  }
  // FNV-1a 32-bit over quantized floats
  let hash = 0x811c9dc5;
  for (const value of parts) {
    const q = Math.round(value * 1e6);
    hash ^= q >>> 0;
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}
